import {
  Box,
  Button,
  Radio,
  Group,
  Drawer,
  Select,
  TextInput,
  Textarea,
  Tooltip,
  Text,
  Loader,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import React, { useEffect, useRef, useCallback, useMemo } from "react";

import swal from "sweetalert";

import { DateInput, DatePickerInput } from "@mantine/dates";

import moment from "moment";
import { useState } from "react";
import { IoIosCloseCircle } from "react-icons/io";
import { IconChevronDown, IconFile } from "@tabler/icons-react";
import { useSelector } from "react-redux";
import { AddMedication, EditMedication } from "../../services/patient";
import {
  scanMedicationLabelMulti,
  resumeMedicationScanMulti,
} from "../../services/medication";
import { requestSmsUpload } from "../../services/smsUpload";
import { decrypt, defaultImage, fileNameFromUrl, showError } from "../../utils";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";
import useStyles from "./style";
import { Dropzone } from "@mantine/dropzone";
import ScheduleMenu from "./ScheduleMenu";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import ConfirmationModal from "../ConfirmationModal";
import { uploadFileToS3Api } from "../../utils/PresignedUrlFunction";
import { updateDiscontinue } from "../../services";
import VersionUpdateModal from "../ConfirmationModal/VersionUpdateModal";
import EyeIconSvg from "../Svgs/EyeIconSvg";
import { FiCalendar } from "react-icons/fi";
import {
  LuX,
  LuCheck,
  LuUpload,
  LuFileText,
  LuSave,
  LuSparkles,
  LuAlertTriangle,
  LuPencil,
  LuTrash2,
  LuChevronLeft,
  LuArrowRight,
  LuImage,
} from "react-icons/lu";
import { BiEdit } from "react-icons/bi";

const bgColor = "var(--app-surface)";

// OCR returns unpadded hours (e.g. "7:00 AM"); the Administering Time(s)
// MultiSelect's option list only contains zero-padded values ("07:00 AM"),
// so an unpadded value silently renders as no selection at all.
const normalizeTimeSlots = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .map((t) => {
      const match = String(t).match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
      if (!match) return null;
      return `${match[1].padStart(2, "0")}:${match[2]} ${match[3].toUpperCase()}`;
    })
    .filter(Boolean);
};

// Two OCR rows are only the same medication order if the name AND the
// schedule/route/rx also match — the same drug name legitimately appears
// twice on a MAR as separate orders (e.g. a Scheduled and a PRN order for
// the same medication with different Rx #, route, and instructions).
// Matching on medicationName alone falsely groups those as duplicates.
const getMedOrderKey = (ocr) => {
  const norm = (v) => (v ?? "").toString().toLowerCase().trim();
  const name = norm(ocr?.medicationName?.value);
  if (!name) return "";
  return [
    name,
    norm(ocr?.scheduleType?.value),
    norm(ocr?.route?.value),
    norm(ocr?.rxNumber?.value),
    norm(ocr?.instructions?.value),
  ].join("|");
};

const ROUTE_OPTIONS = [
  "Buccal",
  "Orally",
  "Intramuscular",
  "Subcutaneous",
  "Cutaneous",
  "Irrigation",
  "Laryngeal",
  "Nasal",
  "Not Applicable",
  "Parenteral",
  "Rectal",
  "Vaginal",
  "Sublingual",
  "Suppository",
  "Injection",
  "Inhalation",
  "Topical",
  "Other",
];

const UNIT_OPTIONS = [
  "mg",
  "mg/ml",
  "cc",
  "iu",
  "ml",
  "puffs",
  "mcg",
  "g",
  "oz",
  "gtts",
  "spray",
  "drops",
  "unit",
  "tablet",
];

const normalizeOcrRoute = (raw) => {
  if (!raw) return raw;
  const exact = ROUTE_OPTIONS.find(
    (o) => o.toLowerCase() === String(raw).trim().toLowerCase(),
  );
  if (exact) return exact;
  const t = String(raw).trim().toLowerCase();
  if (["oral", "po", "by mouth"].includes(t)) return "Orally";
  if (["im"].includes(t)) return "Intramuscular";
  if (["subq", "sub-q", "sc", "subcut"].includes(t)) return "Subcutaneous";
  if (["sl"].includes(t)) return "Sublingual";
  if (["inhaled", "inh"].includes(t)) return "Inhalation";
  if (["inject", "injectable"].includes(t)) return "Injection";
  if (["n/a", "na", "not applicable"].includes(t)) return "Not Applicable";
  return raw;
};

const AddNewMedicationModal = ({
  opened,
  onclose,
  userId,
  selectedClient,
  data,
  isPreview,
  currentPage,
  tabName,
  getAllPatients,
  setShowClientForm,
  getClientData,
  closeAskMedicalModal,
  form2,
  pendingSubmission,
}) => {
  const { classes, cx } = useStyles();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const client = useSelector((state) => state.mar.selectedClient);
  const existingMedications = useSelector((state) => {
    const form3 = (selectedClient || state.mar.selectedClient)?.form3;
    return Array.isArray(form3) ? form3 : [];
  });
  const activeExistingMedNames = useMemo(
    () =>
      new Set(
        existingMedications
          .filter((m) => m?.discontinued !== true)
          .map((m) => (m?.medicationName || "").toLowerCase().trim())
          .filter(Boolean),
      ),
    [existingMedications],
  );
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState("scheduled");
  const [files, setFiles] = useState([]);
  const [scanState, setScanState] = useState("idle"); // 'idle' | 'scanning' | 'done'
  const [scannedFields, setScannedFields] = useState(new Map());
  const [scanImage, setScanImage] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [facingMode, setFacingMode] = useState("environment");
  const [smsPhone, setSmsPhone] = useState("+1");
  const [smsNote, setSmsNote] = useState("");
  const [smsSending, setSmsSending] = useState(false);
  const [smsSent, setSmsSent] = useState(false);
  const [smsPopoverOpen, setSmsPopoverOpen] = useState(false);
  const [smsSecondsLeft, setSmsSecondsLeft] = useState(0);
  const [smsImageReceived, setSmsImageReceived] = useState(false);
  const scanInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  // Bulk upload flow
  const [panelMode, setPanelMode] = useState(null); // null | "upload" | "bulk-review" | "bulk-save-preview" | "bulk-saved"
  const [bulkSavePreviewRows, setBulkSavePreviewRows] = useState([]);
  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [bulkProcessingLabel, setBulkProcessingLabel] = useState("Uploading files");
  const [bulkFakeProgress, setBulkFakeProgress] = useState(0);
  const bulkProgressRafRef = useRef(null);
  const bulkScanAbortControllerRef = useRef(null);
  const [bulkReviewRows, setBulkReviewRows] = useState([]);
  const [bulkEditingRowId, setBulkEditingRowId] = useState(null);
  const [bulkDupeSelectedIds, setBulkDupeSelectedIds] = useState(new Set());
  const [bulkReviewFilter, setBulkReviewFilter] = useState("all");
  const [bulkSelectedIds, setBulkSelectedIds] = useState(new Set());
  const [bulkCombineConfirm, setBulkCombineConfirm] = useState(null);
  const bulkFileInputRef = useRef(null);
  const [isDropzoneDragging, setIsDropzoneDragging] = useState(false);
  // Set when a single-file scan-ocr-multi job hits OCR_MULTI_POLL_TIMEOUT —
  // the backend job keeps running, so this lets "Check status" resume
  // polling the same jobId instead of forcing a full re-upload.
  const [pendingScanJob, setPendingScanJob] = useState(null);
  const afterBulkRowSaveRef = useRef(null);
  const formBoxRef = useRef(null);
  // Set when a single-image scan (no wizard steps, straight to form) matches
  // a medication already on this client's active list — drives the locked
  // "Already in client list" card shown above the form.
  const [singleScanExistingActive, setSingleScanExistingActive] =
    useState(false);
  // getUserMedia + <video> is unreliable in iOS Safari; use native file capture instead.
  const isIOS =
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const { profile } = useSelector((state) => state.profile);
  const businessTimezone = profile?.business?.timezone || "UTC";
  const [frequencyData, setFrequencyData] = useState([
    "QAM",
    "QPM",
    "Q4HR",
    "Q2HR",
    "Q6HR",
    "Q8HR",
    "Q12HR",
    "AC",
    "PC",
    "BID",
    "TID",
    "QID",
    "STAT",
    "QOD",
    "Noon",
    "QD",
    "5x/day",
    "6x/day",
    "AC",
    "AC HS",
    "AC breakfast",
    "AC breakfast/dinner",
    "AC breakfast/lunch",
    "AC dinner",
    "AC lunch",
    "AC lunch/dinner",
    "BID",
    "BID AC",
    "BID HS",
    "BID PC",
    "BID w/meals",
    "Daily",
    "Daily AC",
    "Every other day",
    "Every other evening",
    "On call",
    "Once",
    "PC",
    "PC HS",
    "q2h",
    "q2min",
    "q30min",
    "q36hr",
    "q3day",
    "q3h",
    "q3min",
    "q48h",
    "q4h",
    "q5min",
    "q6h",
    "q6min",
    "q72h",
    "q4day",
    "q5day",
    "q7day",
    "q10day",
    "q14day",
    "q21day",
    "q28day",
    "q7min",
    "q8h",
    "q8min",
    "q96hrs",
    "q2week",
    "PC breakfast",
    "PC breakfast/dinner",
    "PC breakfast/lunch",
    "PC dinner",
    "PC lunch",
    "PC lunch/dinner",
    "QID",
    "TID",
    "TID AC",
    "TID HS",
    "TID PC",
    "TID w/meals",
    "Today",
    "UD",
    "Before discharge",
    "p each loose BM",
    "q10min",
    "q12h",
    "q15min",
    "q1h",
    "q1min",
    "q20min",
    "q24h",
    "q3week",
    "q4week",
    "q6week",
    "q2month",
    "q3month",
    "q4month",
    "q6month",
    "qAM",
    "qPM",
    "qAM HS",
    "qAM PM",
    "qHS",
    "qmonth",
    "qweek",
    "qyear",
    "qShift",
    "w/breakfast",
    "w/breakfast/dinner",
    "w/breakfast/lunch",
    "w/dinner",
    "w/lunch",
    "w/lunch/dinner",
    "w/meals",
    "w/meals HS",
    "x1",
    "Daily",
    "Once Weekly",
    "Once Monthly",
  ]);
  const [selectedDays, setSelectedDays] = useState(
    data?.repeatOn || [moment().format("dddd")],
  );
  const [isMedicationCountEnabled, setIsMedicationCountEnabled] =
    useState("yes");

  // RxTerms API state
  const [rxSuggestions, setRxSuggestions] = useState([]);
  const [rxLoading, setRxLoading] = useState(false);
  const [rxShowDropdown, setRxShowDropdown] = useState(false);
  const [rxStrengthOptions, setRxStrengthOptions] = useState([]);
  const rxDebounceRef = useRef(null);
  const rxDropdownRef = useRef(null);

  const parseRouteFromDisplayName = (displayName) => {
    const match = displayName.match(/\(([^)]+)\)/);
    if (!match) return null;
    const inside = match[1].toLowerCase();
    if (inside.includes("oral")) return "Orally";
    if (inside.includes("inject") || inside.includes("injectable"))
      return "Injection";
    if (inside.includes("inhalation") || inside.includes("inhalant"))
      return "Inhalation";
    if (inside.includes("nasal")) return "Nasal";
    if (inside.includes("sublingual")) return "Sublingual";
    if (inside.includes("topical")) return "Topical";
    if (inside.includes("buccal")) return "Buccal";
    if (inside.includes("rectal")) return "Rectal";
    if (inside.includes("vaginal")) return "Vaginal";
    if (inside.includes("subcutaneous")) return "Subcutaneous";
    if (inside.includes("intramuscular")) return "Intramuscular";
    if (inside.includes("cutaneous")) return "Cutaneous";
    if (inside.includes("parenteral")) return "Parenteral";
    return null;
  };

  const parseDoseForm = (text) => {
    const t = text.toLowerCase();
    if (t.includes("tablet") || t.includes(" tab")) return "Tablet";
    if (t.includes("capsule") || t.includes(" cap")) return "Capsule";
    if (t.includes("syrup")) return "Syrup";
    if (t.includes("pill")) return "Pill";
    if (t.includes("liquid")) return "Liquid";
    if (t.includes("solution")) return "Solution";
    if (t.includes("cream")) return "Cream";
    if (t.includes("ointment")) return "Ointment";
    if (t.includes("lotion")) return "Lotion";
    if (t.includes("gel")) return "Gel";
    if (t.includes("gas")) return "Gas";
    if (t.includes("inhalant") || t.includes("inhalation")) return "Inhalant";
    if (t.includes("inject")) return "Injection";
    if (t.includes("suppository")) return "Suppository";
    if (t.includes("lozenge")) return "Lozenge";
    if (t.includes("paste")) return "Paste";
    if (t.includes("patch")) return "Patch";
    if (t.includes("rinse")) return "Rinse";
    if (t.includes("shampoo")) return "Shampoo";
    if (t.includes("drop")) return "Drops";
    if (t.includes("spray")) return "Spray";
    if (t.includes("powder")) return "Powder";
    if (t.includes("cartridge")) return "Injection";
    return null;
  };

  const searchRxTerms = useCallback(async (term) => {
    if (!term || term.length < 2) {
      setRxSuggestions([]);
      setRxShowDropdown(false);
      return;
    }
    setRxLoading(true);
    try {
      const url = `https://clinicaltables.nlm.nih.gov/api/rxterms/v3/search?terms=${encodeURIComponent(term)}&ef=STRENGTHS_AND_FORMS,RXCUIS,SXDG_RXCUI,DISPLAY_NAME_SYNONYM&maxList=10`;
      const response = await fetch(url);
      const result = await response.json();
      const [total, codes, extraData, displayItems] = result;
      const mapped = (codes || []).map((code, i) => ({
        displayName: code,
        strengthsAndForms: extraData?.STRENGTHS_AND_FORMS?.[i] || [],
        rxcuis: extraData?.RXCUIS?.[i] || [],
        sxdgRxcui: extraData?.SXDG_RXCUI?.[i] || null,
        displayNameSynonym: extraData?.DISPLAY_NAME_SYNONYM?.[i] || [],
      }));
      console.log("RxTerms API — raw response:", result);
      console.log("RxTerms API — total results:", total);
      console.log("RxTerms API — mapped drugs:", mapped);
      setRxSuggestions(mapped);
      setRxShowDropdown(mapped.length > 0);
    } catch (err) {
      console.error("RxTerms API error:", err);
    } finally {
      setRxLoading(false);
    }
  }, []);

  const handleRxInputChange = (value) => {
    form.setFieldValue("medicationName", value);
    setRxStrengthOptions([]);
    form.setFieldValue("strengthAndForm", "");
    if (rxDebounceRef.current) clearTimeout(rxDebounceRef.current);
    rxDebounceRef.current = setTimeout(() => searchRxTerms(value), 400);
  };

  const handleRxSelect = (drug) => {
    console.log("RxTerms — selected drug full data:", drug);
    console.log("  Display Name:", drug.displayName);
    console.log("  Strengths & Forms:", drug.strengthsAndForms);
    console.log("  RxCUIs:", drug.rxcuis);
    console.log("  SXDG RxCUI:", drug.sxdgRxcui);
    console.log("  Synonyms:", drug.displayNameSynonym);
    form.setFieldValue("medicationName", drug.displayName);
    form.setFieldValue("strengthAndForm", "");
    const route = parseRouteFromDisplayName(drug.displayName);
    if (route) form.setFieldValue("ingestionMode", route);
    const doseForm = parseDoseForm(drug.displayName);
    if (doseForm) form.setFieldValue("dosageForm", doseForm);
    setRxStrengthOptions(drug.strengthsAndForms || []);
    setRxShowDropdown(false);
    setRxSuggestions([]);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (rxDropdownRef.current && !rxDropdownRef.current.contains(e.target)) {
        setRxShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const [confirmModal, { open: confirmOpen, close: confirmClose }] =
    useDisclosure(false);
  const [
    versionUpdateModal,
    { open: versionUpdateOpen, close: versionUpdateClose },
  ] = useDisclosure(false);

  const handleRadioChange = (event) => {
    const checked = event;
    setIsMedicationCountEnabled(checked);
    if (checked?.toLowerCase() === "no") {
      form.setFieldValue("totalMedicine", "unlimited");
      form.setFieldValue("medicineRemaining", "unlimited");
    } else if (checked?.toLowerCase() === "yes") {
      // Switching back to limited: restore last saved numeric values from DB.
      // If DB had numeric values use them; if DB was also unlimited (first time
      // switching to limited) clear the fields so the user can type new values.
      const lastTotal =
        data?.totalMedicine !== "unlimited" ? data?.totalMedicine : null;
      const lastRemaining =
        data?.medicineRemaining !== "unlimited"
          ? data?.medicineRemaining
          : null;
      form.setFieldValue("totalMedicine", lastTotal ?? null);
      form.setFieldValue("medicineRemaining", lastRemaining ?? null);
    }
  };

  const validation = () => {
    const _validation = {
      isPrescribed: (value) => (value ? null : "Required"),
      isNarcotic: (value) => (value ? null : "Required"),
      medicationName: (value) => (value ? null : "Required"),
      medicationType: (value) => (value ? null : "Required"),
      // dosage: (value) => (value ? null : "Required"),
      // unit: (value) => (value ? null : "Required"),
      // frequency: (value) => (value ? null : "Required"),
      ingestionMode: (value) => (value ? null : "Required"),
      totalMedicine: (value) => (value ? null : "Required"),
      medicationadministered: (value) => (value ? null : "Required"),
      medicationInstruction: (value) => (value ? null : "Required"),
      medicationDate: (value) => (value ? null : "Required"),
      medicineRemaining: (value) => (value ? null : "Required"),
      timeSlots: (value) => (value ? null : "Required"),
      newMedicationEndDate: (value, values) => {
        if (!value) return null;
        const end = moment(value).startOf("day");
        if (
          values?.medicationDate &&
          end.isBefore(moment(values.medicationDate).startOf("day"))
        ) {
          return "End Date cannot be before Medication Start Date";
        }
        if (
          values?.expirationDate &&
          end.isAfter(moment(values.expirationDate).startOf("day"))
        ) {
          return "End Date cannot be after Expiration Date";
        }
        return null;
      },
    };
    if (data) {
      delete _validation?.medicineRemaining;
    }
    // Time Slots only applies to Scheduled medications (ScheduleMenu is
    // hidden for PRN) — this must drop regardless of add vs. edit, not
    // only when adding, or editing an existing PRN medication gets stuck
    // requiring a field the form never renders a way to fill in.
    if (type === "PRN") {
      delete _validation?.timeSlots;
    }
    return _validation;
  };
  // const _initialValues = {
  //   isPrescribed: "Yes",
  //   isNarcotic: "Yes",
  //   medicationName: "Aleoya",
  //   medicationType: "scheduled",
  //   dosage: 200,
  //   unit: "mg",
  //   frequency: "Q4HR",
  //   ingestionMode: "Buccal",
  //   dosageForm: "Tablet",
  //   totalMedicine: 200,
  //   medicationUse: "qwertyu",
  //   medicationInstruction: "qwerty",
  //   responsibleParty: "Family",
  //   administering: "Family",
  //   medicationDate: new Date("2024/07/28"),
  //   expirationDate: new Date("2024/09/30"),
  //   timeSlots: ["02:00 AM", "08:00 PM"],
  //   medicineRemaining: 180,
  //   medicationadministered: 3,
  //   orderDate: null,
  //   repeatEvery: 1,
  //   repeatType: "week",
  //   repeatOn: [
  //     moment("2024/07/29").format("dddd"),
  //     moment("2024/07/30").format("dddd"),
  //     moment("2024/07/31").format("dddd"),
  //   ],
  //   scheduleType: "custom",
  // };
  const _initialValues = {
    isPrescribed: "",
    isNarcotic: "",
    medicationName: "",
    medicationType: "",
    dosage: "",
    unit: "",
    frequency: "",
    ingestionMode: "",
    dosageForm: "",
    strengthAndForm: "",
    totalMedicine: "",
    // medicationUse: "",
    medicationInstruction: "",
    // responsibleParty: "",
    // administering: "",
    medicationDate: new Date(),
    expirationDate: null,
    newMedicationEndDate: null,
    timeSlots: [],
    medicineRemaining: "",
    medicationadministered: "",
    orderDate: null,
    repeatEvery: 1,
    repeatType: "day",
    repeatOn: [moment().format("dddd")],
    scheduleType: "daily",
    files: [],
    refillsLeft: "",
    rxNumber: "",
  };
  const form = useForm({
    initialValues: _initialValues,
    validate: validation(),
  });

  // Tracks the last value of `opened` so we can detect the drawer just opening.
  const prevOpenedRef = useRef(opened);
  // Tracks the last pendingSubmission id so we can detect new OCR data arriving while the drawer is open.
  const prevPendingIdRef = useRef(null);
  // Stores the last OCR data received via SMS so Remove doesn't lose it.
  const smsOcrDataRef = useRef(null);

  const applyOcrPrefill = useCallback(
    (ocrData) => {
      if (!ocrData) return;
      const updates = {};

      const fieldMap = {
        medicationName: "medicationName",
        dosage: "dosage",
        unit: "unit",
        frequency: "frequency",
        route: "ingestionMode",
        doseForm: "dosageForm",
        rxNumber: "rxNumber",
        quantity: "totalMedicine",
        instructions: "medicationInstruction",
      };

      Object.entries(fieldMap).forEach(([ocrKey, formKey]) => {
        const entry = ocrData?.[ocrKey];
        if (
          entry?.value !== null &&
          entry?.value !== undefined &&
          entry?.confidence > 0
        ) {
          if (ocrKey === "route") {
            updates[formKey] = normalizeOcrRoute(entry.value);
          } else if (ocrKey === "unit") {
            updates[formKey] = String(entry.value).trim().toLowerCase();
          } else {
            updates[formKey] = entry.value;
          }
        }
      });

      if (updates.dosageForm) {
        const raw = String(updates.dosageForm);
        updates.dosageForm =
          raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
      }

      const scheduleEntry = ocrData?.scheduleType;
      if (
        scheduleEntry?.value !== null &&
        scheduleEntry?.value !== undefined &&
        scheduleEntry?.confidence > 0
      ) {
        const raw = String(scheduleEntry.value).toLowerCase();
        updates.medicationType = raw === "prn" ? "PRN" : raw;
      }

      const narcoticEntry = ocrData?.narcoticStatus;
      if (
        narcoticEntry?.value !== null &&
        narcoticEntry?.value !== undefined &&
        narcoticEntry?.confidence > 0
      ) {
        updates.isNarcotic = narcoticEntry.value ? "Yes" : "No";
      }

      const isPrescribedEntry = ocrData?.isPrescribed;
      if (isPrescribedEntry != null) {
        updates.isPrescribed = isPrescribedEntry.value === false ? "No" : "Yes";
      }

      const timeSlotsEntry = ocrData?.timeSlots;
      if (
        Array.isArray(timeSlotsEntry?.value) &&
        timeSlotsEntry.value.length > 0 &&
        timeSlotsEntry.confidence > 0
      ) {
        updates.timeSlots = normalizeTimeSlots(timeSlotsEntry.value);
      }

      const startDateEntry = ocrData?.startDate;
      if (
        startDateEntry?.value !== null &&
        startDateEntry?.value !== undefined &&
        startDateEntry?.confidence > 0
      ) {
        const parsedStartDate = moment(
          String(startDateEntry.value).trim(),
          ["MM/DD/YYYY", "M/D/YYYY", "MM-DD-YYYY", "YYYY-MM-DD"],
          true,
        );
        if (parsedStartDate.isValid()) {
          updates.medicationDate = parsedStartDate.toDate();
        }
      }

      const adminCountEntry = ocrData?.administrationCount;
      if (
        adminCountEntry?.value !== null &&
        adminCountEntry?.value !== undefined &&
        adminCountEntry?.confidence > 0
      ) {
        updates.medicationadministered = String(adminCountEntry.value);
      }

      const refillsEntry = ocrData?.refillsCount;
      if (
        refillsEntry?.value !== null &&
        refillsEntry?.value !== undefined &&
        refillsEntry?.confidence > 0
      ) {
        updates.refillsLeft = String(refillsEntry.value);
      }

      if (Object.keys(updates).length > 0) {
        if (updates.medicationType === "PRN") updates.timeSlots = [];
        form.setValues((current) => ({ ...current, ...updates }));
        if (updates.medicationType) setType(updates.medicationType);
      }
    },
    [form],
  );

  // Total Quantity + Remaining are only meaningful if OCR actually found a
  // quantity — otherwise default "Use Medication Count?" to No.
  const deriveMedicationCountEnabled = (ocr, savedOverride) => {
    if (savedOverride != null) return savedOverride;
    return ocr?.quantity?.value != null && ocr.quantity.value !== ""
      ? "yes"
      : "no";
  };

  const applyMedicationCountFromOcr = (ocr, savedOverride) => {
    const countEnabled = deriveMedicationCountEnabled(ocr, savedOverride);
    setIsMedicationCountEnabled(countEnabled);
    if (countEnabled === "no") {
      form.setFieldValue("totalMedicine", "unlimited");
      form.setFieldValue("medicineRemaining", "unlimited");
    }
  };

  useEffect(() => {
    const buildConfidenceMap = (ocrData) => {
      const map = new Map();
      if (!ocrData) return map;
      const fieldMap = {
        medicationName: "medicationName",
        dosage: "dosage",
        unit: "unit",
        frequency: "frequency",
        route: "ingestionMode",
        doseForm: "dosageForm",
        rxNumber: "rxNumber",
        quantity: "totalMedicine",
        instructions: "medicationInstruction",
      };
      Object.entries(fieldMap).forEach(([ocrKey, formKey]) => {
        const entry = ocrData[ocrKey];
        if (entry?.value != null && entry.confidence > 0)
          map.set(formKey, entry.confidence);
      });
      if (ocrData.scheduleType?.confidence > 0)
        map.set("medicationType", ocrData.scheduleType.confidence);
      if (ocrData.narcoticStatus?.confidence > 0)
        map.set("isNarcotic", ocrData.narcoticStatus.confidence);
      if (ocrData.isPrescribed != null)
        map.set("isPrescribed", ocrData.isPrescribed.confidence ?? 100);
      if (
        Array.isArray(ocrData.timeSlots?.value) &&
        ocrData.timeSlots.value.some((t) =>
          /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.test(String(t)),
        ) &&
        ocrData.timeSlots.confidence > 0
      )
        map.set("timeSlots", ocrData.timeSlots.confidence);
      if (
        ocrData.startDate?.value != null &&
        ocrData.startDate.confidence > 0 &&
        moment(
          String(ocrData.startDate.value).trim(),
          ["MM/DD/YYYY", "M/D/YYYY", "MM-DD-YYYY", "YYYY-MM-DD"],
          true,
        ).isValid()
      )
        map.set("medicationDate", ocrData.startDate.confidence);
      if (ocrData.administrationCount?.confidence > 0)
        map.set(
          "medicationadministered",
          ocrData.administrationCount.confidence,
        );
      if (ocrData.refillsCount?.confidence > 0)
        map.set("refillsLeft", ocrData.refillsCount.confidence);
      return map;
    };

    // Base64 image → File conversion so submitted photos slot into the same
    // bulkFiles shape as a manual desktop upload (id/name/size/file/ocrResult).
    const base64ToFile = (base64Data, mimetype, filename) => {
      const byteChars = atob(base64Data);
      const byteNumbers = new Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
      const blob = new Blob([new Uint8Array(byteNumbers)], { type: mimetype });
      return new File([blob], filename, { type: mimetype });
    };

    const stageSmsSubmission = (payload) => {
      const images = payload?.images;
      if (!Array.isArray(images) || images.length === 0) return;
      const staged = images.map((img, i) => {
        const file = base64ToFile(img.data, img.mimetype, img.originalname || `sms-upload-${i}.jpg`);
        return {
          id: `sms-${payload.submissionId || Date.now()}-${i}`,
          name: file.name,
          size: file.size,
          sizeLabel:
            file.size >= 1024 * 1024
              ? (file.size / (1024 * 1024)).toFixed(1) + " MB"
              : (file.size / 1024).toFixed(0) + " KB",
          file,
          status: "queued",
          ocrResult: null,
        };
      });
      setBulkFiles(staged);
      setPanelMode("upload");
      setSmsImageReceived(true);
    };

    const pending = sessionStorage.getItem("pendingOcrResult");
    if (pending) {
      const parsed = JSON.parse(pending);
      sessionStorage.removeItem("pendingOcrResult");
      stageSmsSubmission(parsed);
    }

    const handler = (e) => {
      sessionStorage.removeItem("pendingOcrResult");
      stageSmsSubmission(e.detail);
    };
    window.addEventListener("medication-ocr-ready", handler);
    return () => window.removeEventListener("medication-ocr-ready", handler);
  }, []);

  useEffect(() => {
    const justOpened = opened && !prevOpenedRef.current;
    prevOpenedRef.current = opened;

    if (data) {
      const resolvedMedicationType = (() => {
        if (!data?.medicationType) return "";
        if (Array.isArray(data.medicationType)) {
          return (
            data.medicationType[data.medicationType.length - 1]?.status || ""
          );
        }
        try {
          const parsed = JSON.parse(data.medicationType);
          if (Array.isArray(parsed)) {
            return parsed[parsed.length - 1]?.status || "";
          }
        } catch {}
        return data.medicationType;
      })();
      setType(resolvedMedicationType || "scheduled");

      form.setValues({
        ...data,
        medicationType: resolvedMedicationType,
        medicineRemaining: data?.medicineRemaining,
        ingestionMode: data?.ingestionMethod || data?.ingestionMode,
        medicationDate: data?.medicationDate
          ? new Date(data?.medicationDate)
          : null,
        expirationDate:
          data &&
          isPreview &&
          (data?.hasExpiryDate == "false" || data?.hasExpiryDate == false)
            ? ""
            : data &&
                (data?.hasExpiryDate == "false" || data?.hasExpiryDate == false)
              ? ""
              : data?.expirationDate &&
                  data?.expirationDate != "null" &&
                  data?.expirationDate != "undefined"
                ? new Date(data?.expirationDate)
                : data?.medicationEndDate && data?.medicationEndDate != "null"
                  ? new Date(data?.medicationEndDate)
                  : null,
        medicationEndDate:
          data && isPreview && data?.hasExpiryDate == "false"
            ? ""
            : data && data.hasExpiryDate == "false"
              ? ""
              : data?.medicationEndDate && data?.medicationEndDate != "null"
                ? new Date(data?.medicationEndDate)
                : null,
        newMedicationEndDate:
          data?.newMedicationEndDate && data?.newMedicationEndDate != "null"
            ? new Date(data?.newMedicationEndDate)
            : null,
        repeatEvery: Number(data?.repeatEvery || 1),
        orderDate:
          data?.orderDate &&
          data?.orderDate != "null" &&
          data?.orderDate != "undefined"
            ? new Date(data?.orderDate)
            : null,
        files: data?.file || [],
      });
      // setFiles(data?.file || []);

      // FIX 1: Handle file initialization properly from doctorNote URL
      if (data?.doctorNote) {
        // Create a file object from the existing doctor note URL
        const existingFile = {
          path: fileNameFromUrl(data.doctorNote, "uploaded-file.pdf"),
          uploadURL: data.doctorNote,
        };
        setFiles([existingFile]);
      } else {
        setFiles([]);
      }
      if (data?.strengthAndForm) {
        setRxStrengthOptions([data.strengthAndForm]);
      }
      if (
        data?.medicineRemaining == "unlimited" &&
        data?.totalMedicine == "unlimited"
      ) {
        setIsMedicationCountEnabled("no");
      } else {
        setIsMedicationCountEnabled("yes");
      }
    } else if (justOpened) {
      // Drawer just opened in add-mode: reset stale values from a previous edit.
      form.reset();
      setFiles([]);
      setRxStrengthOptions([]);
      setIsMedicationCountEnabled("yes");
      setPanelMode(null);
      setBulkFiles([]);
      resetScan();
      setBulkProcessing(false);
      setBulkReviewRows([]);
      setBulkEditingRowId(null);
      afterBulkRowSaveRef.current = null;
      // Pre-fill from OCR data when opened from a pending SMS submission
      if (pendingSubmission?.ocrData) {
        applyOcrPrefill(pendingSubmission.ocrData);
        applyMedicationCountFromOcr(pendingSubmission.ocrData);
      }
      prevPendingIdRef.current = pendingSubmission?.id ?? null;
    } else if (
      opened &&
      !data &&
      pendingSubmission?.ocrData &&
      pendingSubmission.id !== prevPendingIdRef.current
    ) {
      console.log("[Modal] OCR fill | pendingSubmission:", pendingSubmission);
      prevPendingIdRef.current = pendingSubmission.id;
      applyOcrPrefill(pendingSubmission.ocrData);
      applyMedicationCountFromOcr(pendingSubmission.ocrData);
    } else {
      console.log(
        "[Modal] OCR fill skipped | opened:",
        opened,
        "data:",
        !!data,
        "ocrData:",
        !!pendingSubmission?.ocrData,
        "isNew:",
        pendingSubmission?.id !== prevPendingIdRef.current,
      );
    }
    // Do NOT reset when `data` changes while the drawer is already open —
    // that would wipe OCR-filled values when the user clicks Remove.
  }, [data, opened, pendingSubmission]);

  const reset = () => {
    if (afterBulkRowSaveRef.current) {
      const cb = afterBulkRowSaveRef.current;
      afterBulkRowSaveRef.current = null;
      form.reset();
      setScanState("idle");
      setScannedFields(new Map());
      setScanImage(null);
      setFiles([]);
      setRxStrengthOptions([]);
      setIsMedicationCountEnabled("yes");
      resetSmsForm();
      setLoading(false);
      cb();
      return;
    }
    getClientData?.();
    getAllPatients?.(currentPage, true, tabName);
    setLoading(false);
    resetScan();
    setFiles([]);
    setRxStrengthOptions([]);
    setIsMedicationCountEnabled("yes");
    resetSmsForm();
    setPanelMode(null);
    setBulkFiles([]);
    setBulkProcessing(false);
    setBulkReviewRows([]);
    setBulkEditingRowId(null);
    setBulkSelectedIds(new Set());
    afterBulkRowSaveRef.current = null;
    onclose();
  };

  const handleDateConverter = (date, timeslot = null) => {
    const currentDate = new Date(date);
    const timeString = timeslot;

    if (timeslot) {
      const [time, modifier] = timeString.split(" ");
      let [hours, minutes] = time.split(":").map(Number);

      if (modifier === "PM" && hours !== 12) {
        hours += 12;
      }
      if (modifier === "AM" && hours === 12) {
        hours = 0;
      }

      let newdate = currentDate.setHours(hours, minutes, 0, 0);
      return moment(newdate);
    } else {
      return moment(date);
    }
  };

  const setScheduleDates = (values) => {
    const _format = "YYYY-MM-DD";
    // let _startDate = moment.tz(moment(values?.medicationDate), moment.tz.guess()).tz("UTC");
    let _startDate = moment(values?.medicationDate).startOf("day");

    let _endDate = values?.newMedicationEndDate
      ? moment(values?.newMedicationEndDate)
      : values?.expirationDate
        ? moment(values?.expirationDate)
        : // : moment(values?.medicationDate).startOf("day");
          moment().endOf("year");
    let _dateRange = [];
    let _currentDate = _startDate.clone();
    let _finalDateWithTimeSlots = [];

    if (values?.scheduleType === "custom") {
      if (values?.repeatType === "day") {
        // For Day
        while (_currentDate.isSameOrBefore(_endDate, "day")) {
          _dateRange.push(_currentDate.format(_format));
          _currentDate.add(values?.repeatEvery, "day");
        }

        _dateRange?.forEach((date) => {
          values?.timeSlots.forEach((time) => {
            _finalDateWithTimeSlots.push({
              date: moment(date).toISOString(),
              timeslot: time,
            });
            // _finalDateWithTimeSlots.push(
            //     moment.tz(moment(date + " " + time), user?.timeZone)?.toISOString()
            //     // moment(date + " " + time, "YYYY-MM-DD hh:mm A")?.toISOString()
            // );
          });
        });
      } else if (values?.repeatType === "week") {
        // For Week
        while (_currentDate.isSameOrBefore(_endDate, "day")) {
          let days = [
            "Sunday",
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
          ];
          let days_dates = Array.from({ length: 7 }, (_, i) =>
            _currentDate
              .clone()
              .startOf("week")
              .add(i, "days")
              .format("MM-DD-YYYY"),
          );

          values?.repeatOn?.map((day) => {
            let index = days?.indexOf(day);
            if (
              index !== -1 &&
              moment(days_dates[index]).isSameOrAfter(_startDate) &&
              moment(days_dates[index]).isSameOrBefore(_endDate)
            ) {
              // _currentDate.format(_format)
              // _dateRange.push(moment(days_dates[index]).toISOString());
              _dateRange.push(moment(days_dates[index]).format(_format));
              // _dateRange.push(moment(days_dates[index]).tz(user?.timeZone).format(_format));
            }
          });
          _currentDate.add(values?.repeatEvery, "week");
        }

        _dateRange?.forEach((date) => {
          values?.timeSlots?.forEach((time) => {
            // _finalDateWithTimeSlots.push(
            //     moment(date + " " + time, "YYYY-MM-DD hh:mm A")?.toISOString()
            // );
            // if (serverFeTimeDiff > 20000) {
            //     console.log("🚀 ~ values?.timeSlots?.split ~ diff if:", diff)
            // } else {
            //     console.log("🚀 ~ values?.timeSlots?.split ~ diff else:", diff)

            // _finalDateWithTimeSlots.push(
            //     moment(date + " " + "00:00 AM" , "YYYY-MM-DD hh:mm A").tz("UTC").subtract(Math.abs(diff) , "hours").add(2 , "hours")?.toISOString()
            //   );

            // theory how it will work
            //  we need to cater three timeZones
            //  BE = KHI, FE = Brazil, UTC TimeZone
            //  first of all calculate the difference between BE and FE timezone here = 8 hours
            //  after
            //

            // let currentDate = moment(date, "YYYY-MM-DD").tz(user?.timeZone) // first convert this value to FE Timezone then you have to add/subtract 8 hours BE FE Timezone difference
            // let timeslot;

            // if (serverFeTimeDiff >= 0) {
            //     timeslot = currentDate.add(serverFeTimeDiff , 'hours').format("hh:mm A")
            //     currentDate = currentDate.add(serverFeTimeDiff , 'hours').tz("UTC")
            // } else {
            //     timeslot = currentDate.subtract(Math.abs(serverFeTimeDiff) , 'hours').format("hh:mm A")
            //     currentDate = currentDate.subtract(Math.abs(serverFeTimeDiff) , 'hours').tz("UTC")
            // }
            // then get start of day
            // then add the fixed hours like 02:00 AM == 2 hours
            // then convert it to UTC and save in server

            // currentDate = currentDate.startOf('day')

            // console.log('frontend time start day', currentDate)
            // currentDate = currentDate.add(2, "hours")
            // console.log(' added', currentDate)
            // console.log(' added', moment(currentDate).tz(user.timeZone))

            // currentDate = currentDate.tz("UTC")
            // console.log('timeslot ',timeslot)

            // let final = moment
            //   .tz(handleDateConverter(date, time), moment.tz.guess())
            //   .tz("UTC")
            //   .toISOString();
            let final = moment(date).toISOString();

            _finalDateWithTimeSlots.push({
              // date :  handleDateConverter(date,time)?.toISOString(),
              date: final,
              timeslot: time,
            });

            // }
          });
        });
      } else {
        // For Month
        while (_currentDate.isSameOrBefore(_endDate, "day")) {
          _dateRange.push(_currentDate.format(_format));
          _currentDate.add(values?.repeatEvery, "month");
        }

        _dateRange?.forEach((date) => {
          // values?.timeSlots?.split(",").forEach((time) => {
          values?.timeSlots?.forEach((time) => {
            _finalDateWithTimeSlots.push({
              date: moment(date).toISOString(),
              timeslot: time,
            });
            // _finalDateWithTimeSlots.push(
            //     moment.tz(moment(date + " " + time), user?.timeZone)?.toISOString()
            //     // moment(date + " " + time, "YYYY-MM-DD hh:mm A")?.toISOString()
            // );
          });
        });
      }
    } else if (values?.scheduleType === "daily") {
      while (_currentDate.isSameOrBefore(_endDate, "day")) {
        _dateRange.push(_currentDate.format(_format));
        _currentDate.add(values?.repeatEvery, "day");
      }

      _dateRange?.forEach((date) => {
        values?.timeSlots.forEach((time) => {
          _finalDateWithTimeSlots.push({
            date: moment(date).toISOString(),
            timeslot: time,
          });
        });
      });
    }
    return {
      scheduleDates: _finalDateWithTimeSlots,
      startDate: _startDate,
      endDate: _endDate,
    };
  };

  const onSave = async (values) => {
    if (
      (Number(values?.medicineRemaining) || 0) > Number(values?.totalMedicine)
    ) {
      return swal(
        "Error",
        "Current Medication Count cannot exceed Medication Total Quantity.",
        "error",
      );
    }

    if (
      Number(values?.medicationadministered) >
        Number(values?.medicineRemaining) &&
      !data
    ) {
      return swal(
        "Error",
        "Dosage Administered count cannot exceed Current Medication Count.",
        "error",
      );
    }

    let clientId = selectedClient ? selectedClient?.id : client?.id;

    if (data) {
      // Open version update modal for editing
      versionUpdateOpen(true);
      // const checkArrays = (arr1, arr2) => {
      //   if (
      //     !Array.isArray(arr1) ||
      //     !Array.isArray(arr2) ||
      //     arr1.length !== arr2.length
      //   ) {
      //     return true;
      //   }

      //   for (let i = 0; i < arr1.length; i++) {
      //     if (arr2.indexOf(arr1[i]) == -1) {
      //       return true;
      //     }
      //     // if (arr1[i] !== arr2[i]) {
      //     //   return true;
      //     // }
      //   }

      //   return false;
      // };
      // const _format = "YYYY-MM-DD";
      // const _medicationType = data?.medicationType !== values?.medicationType;
      // const _startDate =
      //   moment(data?.medicationDate).format(_format) !==
      //   moment(values?.medicationDate).format(_format);
      // const _endDate =
      //   data?.expirationDate &&
      //   data?.expirationDate != "null" &&
      //   moment(data?.expirationDate).format(_format) !==
      //     moment(values?.expirationDate).format(_format);
      // const _timeSlots = checkArrays(data?.timeSlots, values?.timeSlots);
      // const _repeatOn =
      //   data?.scheduleType === "custom"
      //     ? checkArrays(data?.repeatOn, values?.repeatOn)
      //     : false;

      // const _scheduleType =
      //   data?.scheduleType && values?.scheduleType
      //     ? data?.scheduleType !== values?.scheduleType
      //     : false;
      // const _repeatEvery =
      //   data?.scheduleType === "custom"
      //     ? Number(data?.repeatEvery) &&
      //       Number(data?.repeatEvery) != Number(values?.repeatEvery)
      //     : false;
      // const _repeatType =
      //   data?.scheduleType === "custom"
      //     ? data?.repeatType && data?.repeatType !== values?.repeatType
      //     : false;

      //     if (
      //       _medicationType ||
      //       _startDate ||
      //       _endDate ||
      //       _timeSlots ||
      //       _scheduleType ||
      //       _repeatEvery ||
      //       _repeatType ||
      //       _repeatOn
      //     ) {
      //   confirmOpen();
      // } else {
      //   handleEdit(false);
      // }
    } else {
      if (!values?.timeSlots?.length && values?.medicationType != "PRN")
        return showError("Select time slots for medicine.");

      const { scheduleDates } = setScheduleDates(values);

      const _formData = new FormData();
      files?.length > 0 && _formData.append("file", files[0]?.uploadURL);
      // _formData.append("administering", values?.administering);
      _formData.append("dosage", values?.dosage);
      _formData.append("dosageForm", values?.dosageForm);
      _formData.append(
        "expirationDate",
        // values?.expirationDate ? values?.expirationDate?.toISOString() : null
        values?.expirationDate
          ? moment(values?.expirationDate)?.endOf("D")?.toISOString()
          : null,
        // values?.expirationDate ? moment(values?.expirationDate)?.endOf("D") : null
      );
      _formData.append("frequency", values?.frequency);
      _formData.append("ingestionMode", values?.ingestionMode);
      _formData.append("isNarcotic", values?.isNarcotic);
      _formData.append("isPrescribed", values?.isPrescribed);
      _formData.append("rxNumber", values?.rxNumber || "");
      _formData.append(
        "medicationDate",
        values?.medicationDate ? values?.medicationDate?.toISOString() : null,
        // values?.medicationDate ? moment(values?.medicationDate)?.startOf("D")?.toISOString() : null
        // values?.medicationDate ? moment(values?.medicationDate)?.startOf("D") : null
      );

      // Start of Day on Frontend Date with 00:00:00 1 Aug 2024 Karachi Time.

      _formData.append("medicationInstruction", values?.medicationInstruction);
      _formData.append("medicationName", values?.medicationName);
      _formData.append(
        "medicationType",
        JSON.stringify([
          { status: values?.medicationType, date: moment().toISOString() },
        ]),
      );
      // _formData.append("medicationUse", values?.medicationUse);
      _formData.append(
        "medicationadministered",
        values?.medicationadministered,
      );
      _formData.append(
        "medicineRemaining",
        // Number(values?.medicineRemaining) || 0
        values?.medicineRemaining,
      );
      _formData.append("orderDate", values?.orderDate?.toISOString());
      // _formData.append("responsibleParty", values?.responsibleParty);
      _formData.append("timeSlots", values?.timeSlots);
      _formData.append("totalMedicine", values?.totalMedicine);
      _formData.append("unit", values?.unit);
      _formData.append("strengthAndForm", values?.strengthAndForm || "");
      if (values?.refillsLeft !== "" && values?.refillsLeft != null) {
        _formData.append("refillsLeft", Number(values.refillsLeft));
      }
      _formData.append(
        "medicineProvided",
        Number(values?.medicineRemaining) || 0,
      );
      _formData.append("id", Date.now());
      // Always send all schedule fields so backend stores correct metadata in MAR doc
      _formData.append("scheduleType", values?.scheduleType || "daily");
      _formData.append(
        "repeatEvery",
        values?.scheduleType === "custom" ? values?.repeatEvery || 1 : 1,
      );
      _formData.append(
        "repeatType",
        values?.scheduleType === "custom" ? values?.repeatType || "day" : "",
      );
      _formData.append(
        "repeatOn",
        values?.scheduleType === "custom"
          ? (values?.repeatOn || []).join(",")
          : "",
      );
      // medicationEndDate mirrors expirationDate (MAR scheduling cutoff).
      // newMedicationEndDate is the provider's stop-administering date and is
      // sent separately — it must never overwrite medicationEndDate.
      _formData.append(
        "medicationEndDate",
        values?.expirationDate
          ? moment(values?.expirationDate)?.endOf("D")?.toISOString()
          : moment().endOf("year").toISOString(),
      );
      if (values?.newMedicationEndDate) {
        _formData.append(
          "newMedicationEndDate",
          moment(values?.newMedicationEndDate)?.endOf("D")?.toISOString(),
        );
      }
      // _formData.append(
      //   "medicationEndDate",
      //   values?.expirationDate
      //     ? moment(values?.expirationDate)?.endOf("D")?.toISOString()
      //     : moment(values?.medicationDate)?.endOf("D")?.toISOString()
      // );

      _formData.append("scheduleDates", JSON.stringify(scheduleDates));

      _formData.append("hasExpiryDate", values?.expirationDate ? true : false);
      // _formData.append("timezone", businessTimezone ? businessTimezone : moment.tz.guess());

      setLoading(true);
      await AddMedication(_formData, clientId, false, null, reset);
      if (selectedClient) {
        closeAskMedicalModal();
        setShowClientForm(false);
      }
      setLoading(false);
    }
  };

  const handleUpdateThisMedication = () => {
    // Close version update modal
    versionUpdateClose();
    const checkArrays = (arr1, arr2) => {
      if (
        !Array.isArray(arr1) ||
        !Array.isArray(arr2) ||
        arr1.length !== arr2.length
      ) {
        return true;
      }

      for (let i = 0; i < arr1.length; i++) {
        if (arr2.indexOf(arr1[i]) == -1) {
          return true;
        }
        // if (arr1[i] !== arr2[i]) {
        //   return true;
        // }
      }

      return false;
    };
    const values = form.values;
    const _format = "YYYY-MM-DD";
    const _medicationType = data?.medicationType !== values?.medicationType;
    const _startDate =
      moment(data?.medicationDate).format(_format) !==
      moment(values?.medicationDate).format(_format);
    const _endDate =
      data?.expirationDate &&
      data?.expirationDate != "null" &&
      moment(data?.expirationDate).format(_format) !==
        moment(values?.expirationDate).format(_format);
    const _timeSlots = checkArrays(data?.timeSlots, values?.timeSlots);
    const _repeatOn =
      data?.scheduleType === "custom"
        ? checkArrays(data?.repeatOn, values?.repeatOn)
        : false;

    const _scheduleType =
      data?.scheduleType && values?.scheduleType
        ? data?.scheduleType !== values?.scheduleType
        : false;
    const _repeatEvery =
      data?.scheduleType === "custom"
        ? Number(data?.repeatEvery) &&
          Number(data?.repeatEvery) != Number(values?.repeatEvery)
        : false;
    const _repeatType =
      data?.scheduleType === "custom"
        ? data?.repeatType && data?.repeatType !== values?.repeatType
        : false;

    if (
      _medicationType ||
      _startDate ||
      _endDate ||
      _timeSlots ||
      _scheduleType ||
      _repeatEvery ||
      _repeatType ||
      _repeatOn
    ) {
      confirmOpen();
    } else {
      handleEdit(false);
    }
  };

  const handleEdit = async (confirm) => {
    if (confirmModal) {
      confirmClose();
    }
    const values = form.values;

    let current = moment().startOf("day");
    // const { scheduleDates } = setScheduleDates({
    //   ...values,
    //   medicationDate: current,
    // });

    // const { scheduleDates } = setScheduleDates(values)

    const _formData = new FormData();
    // files?.length > 0 && _formData.append("file", files[0]?.uploadURL);
    if (files?.length > 0) {
      _formData.append("file", files[0]?.uploadURL);
    } else if (data?.doctorNote) {
      // If files array is empty but data has a doctor note, preserve it
      _formData.append("file", data.doctorNote);
    }
    // _formData.append("administering", values?.administering);
    _formData.append("dosage", values?.dosage);
    _formData.append("dosageForm", values?.dosageForm);
    _formData.append(
      "expirationDate",
      values?.expirationDate ? values?.expirationDate?.toISOString() : null,
    );
    _formData.append("hasExpiryDate", values?.expirationDate ? true : false);
    _formData.append("frequency", values?.frequency);
    _formData.append("ingestionMode", values?.ingestionMode);
    _formData.append("isNarcotic", values?.isNarcotic);
    _formData.append("isPrescribed", values?.isPrescribed);
    _formData.append("rxNumber", values?.rxNumber || "");
    _formData.append(
      "medicationDate",
      values?.medicationDate ? values?.medicationDate?.toISOString() : null,
    );
    _formData.append("medicationInstruction", values?.medicationInstruction);
    _formData.append("medicationName", values?.medicationName);
    _formData.append(
      "medicationType",
      JSON.stringify(
        Array.isArray(data?.medicationType)
          ? [
              ...data.medicationType,
              { status: values?.medicationType, date: moment().toISOString() },
            ]
          : [{ status: values?.medicationType, date: moment().toISOString() }],
      ),
    );
    // _formData.append("medicationUse", values?.medicationUse);
    _formData.append("medicationadministered", values?.medicationadministered);
    // _formData.append("medicineRemaining", Number(data?.medicineRemaining) || 0);
    // _formData.append("medicineRemaining", data?.medicineRemaining);
    _formData.append("orderDate", values?.orderDate?.toISOString());
    // _formData.append("responsibleParty", values?.responsibleParty);
    _formData.append("timeSlots", values?.timeSlots);
    // Use isMedicationCountEnabled as source of truth for totalMedicine
    _formData.append(
      "totalMedicine",
      isMedicationCountEnabled?.toLowerCase() === "no"
        ? "unlimited"
        : values?.totalMedicine,
    );
    _formData.append("unit", values?.unit);
    if (values?.refillsLeft !== "" && values?.refillsLeft != null) {
      _formData.append("refillsLeft", Number(values.refillsLeft));
    }
    _formData.append("strengthAndForm", values?.strengthAndForm || "");
    _formData.append("medicineProvided", Number(data?.medicineRemaining) || 0);
    _formData.append("id", data?.id);
    // Always send all schedule fields so backend can clear stale custom values when switching to daily
    _formData.append("scheduleType", values?.scheduleType || "daily");
    _formData.append(
      "repeatEvery",
      values?.scheduleType === "custom" ? values?.repeatEvery || 1 : 1,
    );
    _formData.append(
      "repeatType",
      values?.scheduleType === "custom" ? values?.repeatType || "day" : "",
    );
    _formData.append(
      "repeatOn",
      values?.scheduleType === "custom"
        ? (values?.repeatOn || []).join(",")
        : "",
    );
    _formData.append("confirm", confirm);
    // medicationEndDate mirrors expirationDate — never the provider stop date
    // (newMedicationEndDate), which is sent separately below.
    _formData.append(
      "medicationEndDate",
      values?.expirationDate
        ? moment(values?.expirationDate)?.toISOString()
        : moment().endOf("year").toISOString(),
    );
    if (values?.newMedicationEndDate) {
      _formData.append(
        "newMedicationEndDate",
        moment(values?.newMedicationEndDate)?.toISOString(),
      );
    }
    // _formData.append("hasExpiryDate", values?.expirationDate ? true : false);
    // _formData.append("scheduleDates", JSON.stringify(scheduleDates));

    // Always derive medicineRemaining and totalMedicine from the current toggle
    // state, not from the original DB value. This fixes limited→unlimited→limited
    // toggling where the old code sent data?.medicineRemaining (stale DB value).
    if (isMedicationCountEnabled?.toLowerCase() === "no") {
      _formData.append("medicineRemaining", "unlimited");
    } else {
      // Limited: send whatever the user currently has in the form fields.
      // values?.medicineRemaining is kept in sync by handleRadioChange.
      _formData.append("medicineRemaining", values?.medicineRemaining);
    }

    let newStartDate = moment(values?.medicationDate).format("MM-DD-YYYY");
    let oldStartDate = moment(data?.medicationDate).format("MM-DD-YYYY");
    // let oldScheduleType = data?.scheduleType;
    // _formData.append("old_schedule_type", oldScheduleType);
    // _formData.append("old_timeSlots", data?.timeSlots);

    let newEndDate = values?.newMedicationEndDate
      ? moment(values?.newMedicationEndDate)?.toISOString()
      : values?.expirationDate
        ? moment(values?.expirationDate)?.toISOString()
        : moment(values?.medicationDate)?.toISOString();
    // let newEndDate = moment(values?.expirationDate).format("MM-DD-YYYY");
    let oldEndDate = moment(data?.medicationEndDate).format("MM-DD-YYYY");

    if (moment(newStartDate) < moment(oldStartDate)) {
      // if(values?.scheduleType === "custom" && values?.repeatType === "week"){
      //   const { scheduleDates } = setScheduleDates({
      //     medicationDate: values?.medicationDate,
      //     expirationDate: moment(data?.expirationDate),
      //     // expirationDate: moment(data?.medicationDate).subtract(1, "days"),
      //     scheduleType: values?.scheduleType,
      //     repeatType: values?.repeatType,
      //     repeatEvery: values?.repeatEvery,
      //     repeatOn: values?.repeatOn,
      //     timeSlots: values?.timeSlots,
      //   })

      //   let filteredSchedules = scheduleDates?.filter(_s => moment(_s?.date).isBefore(data?.medicationDate))

      //   _formData.append("new_start_schedules", JSON.stringify(filteredSchedules));
      //   _formData.append("old_start_date", moment(oldStartDate));
      //   _formData.append("new_start_date", moment(newStartDate));
      // } else {
      //   const { scheduleDates } = setScheduleDates({
      //     medicationDate: values?.medicationDate,
      //     expirationDate: moment(data?.medicationDate).subtract(1, "days"),
      //     scheduleType: values?.scheduleType,
      //     repeatType: values?.repeatType,
      //     repeatEvery: values?.repeatEvery,
      //     repeatOn: values?.repeatOn,
      //     timeSlots: values?.timeSlots,
      //   })

      //   _formData.append("new_start_schedules", JSON.stringify(scheduleDates));
      //   _formData.append("old_start_date", moment(oldStartDate));
      //   _formData.append("new_start_date", moment(newStartDate));
      // }

      const { scheduleDates } = setScheduleDates({
        medicationDate: values?.medicationDate,
        expirationDate: moment(data?.medicationDate).subtract(1, "days"),
        scheduleType: values?.scheduleType,
        repeatType: values?.repeatType,
        repeatEvery: values?.repeatEvery,
        repeatOn: values?.repeatOn,
        timeSlots: values?.timeSlots,
      });

      _formData.append("new_start_schedules", JSON.stringify(scheduleDates));
      _formData.append("old_start_date", moment(oldStartDate));
      _formData.append("new_start_date", moment(newStartDate));
    } else if (moment(newStartDate) > moment(oldStartDate)) {
      _formData.append("old_start_date", moment(oldStartDate));
      _formData.append("new_start_date", moment(newStartDate));
    }

    if (moment(newEndDate) < moment(oldEndDate)) {
      _formData.append("old_end_date", moment(oldEndDate));
      _formData.append("new_end_date", moment(newEndDate));
    } else if (moment(newEndDate) > moment(oldEndDate)) {
      // if(values?.scheduleType === "custom" && values?.repeatType === "day"){
      if (values?.scheduleType === "custom") {
        const { scheduleDates } = setScheduleDates({
          // medicationDate: moment(data?.expirationDate).add(1, "days"),
          medicationDate: values?.medicationDate,
          expirationDate: values?.expirationDate,
          newMedicationEndDate: values?.newMedicationEndDate,
          scheduleType: values?.scheduleType,
          repeatType: values?.repeatType,
          repeatEvery: values?.repeatEvery,
          repeatOn: values?.repeatOn,
          timeSlots: values?.timeSlots,
        });
        let filteredSchedules = scheduleDates?.filter((_s) =>
          moment(_s?.date).isAfter(data?.expirationDate),
        );

        _formData.append(
          "new_end_schedules",
          JSON.stringify(filteredSchedules),
        );
      } else {
        const { scheduleDates } = setScheduleDates({
          medicationDate: moment(data?.expirationDate).add(1, "days"),
          expirationDate: values?.expirationDate,
          newMedicationEndDate: values?.newMedicationEndDate,
          scheduleType: values?.scheduleType,
          repeatType: values?.repeatType,
          repeatEvery: values?.repeatEvery,
          repeatOn: values?.repeatOn,
          timeSlots: values?.timeSlots,
        });

        _formData.append("new_end_schedules", JSON.stringify(scheduleDates));
      }

      _formData.append("old_end_date", moment(oldEndDate));
      _formData.append("new_end_date", moment(newEndDate));
    }

    const scheduleChanged =
      values?.scheduleType !== data?.scheduleType ||
      JSON.stringify(values?.timeSlots) !== JSON.stringify(data?.timeSlots) ||
      JSON.stringify(values?.repeatOn) !== JSON.stringify(data?.repeatOn) ||
      values?.repeatType !== data?.repeatType ||
      Number(values?.repeatEvery) !== Number(data?.repeatEvery);

    if (scheduleChanged) {
      const { scheduleDates: newScheduleDates } = setScheduleDates({
        medicationDate: values?.medicationDate,
        expirationDate: values?.expirationDate,
        newMedicationEndDate: values?.newMedicationEndDate,
        scheduleType: values?.scheduleType,
        repeatType: values?.repeatType,
        repeatEvery: values?.repeatEvery,
        repeatOn: values?.repeatOn,
        timeSlots: values?.timeSlots,
      });
      _formData.append("updateSchedule", true);
      _formData.append("scheduleDates", JSON.stringify(newScheduleDates));
    }
    setLoading(true);
    await EditMedication(_formData, client?.id, false, reset);
    if (versionUpdateModal) {
      versionUpdateClose();
    }
    setLoading(false);
  };

  const handleNewVersion = async (newStartDate, newExpirationDate) => {
    setLoading(true);
    const values = form.values;

    try {
      const obj = {
        clientId: client?.id,
        medicationId: data?.id,
        discontinuedOn: moment(newStartDate)
          .subtract(1, "days")
          .endOf("day")
          .toISOString(),
      };
      const res = await updateDiscontinue(obj, client?.id);

      if (res?.status !== 200) {
        throw new Error("Failed to discontinue medication");
      }

      const versionValues = {
        ...values,
        medicationDate: newStartDate ? new Date(newStartDate) : new Date(),
        expirationDate: newExpirationDate ? new Date(newExpirationDate) : null,
        // New versions start without an End Date rather than inheriting a
        // stale one from the prior version's dates.
        newMedicationEndDate: null,
      };

      const { scheduleDates } = setScheduleDates(versionValues);

      const _formData = new FormData();
      files?.length > 0 && _formData.append("file", files[0]?.uploadURL);
      // _formData.append("administering", values?.administering);
      _formData.append("dosage", values?.dosage);
      _formData.append("dosageForm", values?.dosageForm);
      _formData.append(
        "expirationDate",
        versionValues?.expirationDate
          ? moment(versionValues?.expirationDate)?.endOf("D")?.toISOString()
          : null,
      );
      _formData.append("frequency", values?.frequency);
      _formData.append("ingestionMode", values?.ingestionMode);
      _formData.append("isNarcotic", values?.isNarcotic);
      _formData.append("isPrescribed", values?.isPrescribed);
      _formData.append("rxNumber", values?.rxNumber || "");
      _formData.append(
        "medicationDate",
        versionValues?.medicationDate
          ? versionValues?.medicationDate?.toISOString()
          : null,
      );

      _formData.append("medicationInstruction", values?.medicationInstruction);
      _formData.append("medicationName", values?.medicationName);
      _formData.append(
        "medicationType",
        JSON.stringify([
          { status: values?.medicationType, date: moment().toISOString() },
        ]),
      );
      // _formData.append("medicationUse", values?.medicationUse);
      _formData.append(
        "medicationadministered",
        values?.medicationadministered,
      );
      _formData.append("medicineRemaining", values?.medicineRemaining);
      _formData.append("orderDate", values?.orderDate?.toISOString());
      // _formData.append("responsibleParty", values?.responsibleParty);
      _formData.append("timeSlots", values?.timeSlots);
      _formData.append("totalMedicine", values?.totalMedicine);
      _formData.append("unit", values?.unit);
      if (values?.refillsLeft !== "" && values?.refillsLeft != null) {
        _formData.append("refillsLeft", Number(values.refillsLeft));
      }
      _formData.append("strengthAndForm", values?.strengthAndForm || "");
      _formData.append(
        "medicineProvided",
        Number(values?.medicineRemaining) || 0,
      );
      _formData.append("id", Date.now());
      // Always send all schedule fields so backend stores correct metadata in MAR doc
      _formData.append("scheduleType", values?.scheduleType || "daily");
      _formData.append(
        "repeatEvery",
        values?.scheduleType === "custom" ? values?.repeatEvery || 1 : 1,
      );
      _formData.append(
        "repeatType",
        values?.scheduleType === "custom" ? values?.repeatType || "day" : "",
      );
      _formData.append(
        "repeatOn",
        values?.scheduleType === "custom"
          ? (values?.repeatOn || []).join(",")
          : "",
      );
      _formData.append(
        "medicationEndDate",
        versionValues?.expirationDate
          ? moment(versionValues?.expirationDate)?.endOf("D")?.toISOString()
          : moment().endOf("year").toISOString(),
      );
      if (versionValues?.newMedicationEndDate) {
        _formData.append(
          "newMedicationEndDate",
          moment(versionValues?.newMedicationEndDate)?.endOf("D")?.toISOString(),
        );
      }
      // _formData.append(
      //   "medicationEndDate",
      //   values?.expirationDate
      //     ? moment(values?.expirationDate)?.endOf("D")?.toISOString()
      //     : moment(values?.medicationDate)?.endOf("D")?.toISOString()
      // );

      _formData.append("scheduleDates", JSON.stringify(scheduleDates));

      _formData.append(
        "hasExpiryDate",
        versionValues?.expirationDate ? true : false,
      );
      _formData.append("previousMedId", data?.id);

      await AddMedication(_formData, client?.id, false, null, reset, false);

      versionUpdateClose();
      setLoading(false);
      swal(
        "Success",
        "New medication version created successfully.",
        "success",
      );
    } catch (error) {
      console.error(error);
      showError(error.message || "Operation failed");
      setLoading(false);
    }
  };

  const formatString = (str) => {
    const words = str?.split("");
    if (words?.length <= 15) {
      return str;
    }
    const startWords = words?.slice(0, 10)?.join("");
    const endWords = words?.slice(-10)?.join("");
    return `${startWords} ... ${endWords}`;
  };

  const daysOfWeek = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  const handleDownloadFile = async (url) => {
    try {
      if (url) {
        const response = await fetch(url);
        const blob = await response.blob();
        const filename = fileNameFromUrl(url, "download");
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = filename;
        link.click();
      } else {
        console.error("No url found");
      }
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  const onChangeMedStartDate = (_sDate) => {
    if (data) {
      // form.values?.repeatType === "week"
      if (form.values?.scheduleType === "custom") {
        if (form.values?.repeatType === "day") {
          const original = moment(data?.medicationDate);
          const newDate = moment(_sDate);

          let diff;

          if (newDate < original) {
            diff = original.diff(newDate, "days");
          } else if (newDate > original) {
            diff = newDate.diff(original, "days");
          }

          let repeatDays = Number(form.values?.repeatEvery);

          // Check if the difference is a multiple of repeatDays
          if (diff % repeatDays !== 0) {
            const remainder = Math.abs(diff % repeatDays);
            let validPrevious, validNext;

            if (newDate.isBefore(original)) {
              // Moving backward
              validPrevious = newDate.clone().subtract(remainder, "days");
              validNext = validPrevious.clone().subtract(repeatDays, "days");
            } else {
              // Moving forward
              validPrevious = newDate.clone().subtract(remainder, "days");
              validNext = newDate.clone().add(repeatDays - remainder, "days");
            }

            return alert("Invalid start date");
          } else {
            form.setFieldValue("medicationDate", _sDate);
          }
        } else if (form.values?.repeatType === "week") {
          const original = moment(data?.medicationDate);
          const newDate = moment(_sDate);
          const repeatEvery = Number(form.values?.repeatEvery); // e.g., 1 or 2
          const repeatOn = form.values?.repeatOn || [];

          const newDayName = newDate.format("dddd"); // Day name of new start date

          // Step 2: Ensure the new start date is also in repeatOn
          if (!repeatOn.includes(newDayName)) {
            return alert(
              `Invalid start date. Please select a date that falls on: ${repeatOn.join(
                ", ",
              )}.`,
            );
          }

          // console.log("firstValidDay and newDate", moment(firstValidDay).format("MM-DD-YYYY"), moment(newDate).format("MM-DD-YYYY"))

          // Step 3: Ensure the new date is a multiple of repeatEvery weeks from first valid occurrence
          let weekDiff;
          if (newDate < original) {
            weekDiff = original.diff(newDate.startOf("week"), "weeks");
          } else {
            weekDiff = newDate.diff(original.startOf("week"), "weeks");
          }

          if (Math.abs(weekDiff) % repeatEvery !== 0) {
            return alert(
              `Invalid start date. Please select a date that repeats every ${repeatEvery} weeks on ${repeatOn.join(
                ", ",
              )}.`,
            );
          } else {
            form.setFieldValue("medicationDate", _sDate);
          }
        } else if (form.values?.repeatType === "month") {
          const original = moment(data?.medicationDate);
          const newDate = moment(_sDate);
          const originalDay = original.date(); // e.g., 10th of the month
          const newDay = newDate.date();
          const repeatMonths = Number(form.values?.repeatEvery); // e.g., 1 or 2

          if (repeatMonths === 1) {
            // User can only select the same day of the month (e.g., 10th)
            if (newDay !== originalDay) {
              return alert(
                `Invalid start date. Please select the ${originalDay}th of the month.`,
              );
            }
          } else if (repeatMonths > 1) {
            // Ensure the new date is every `repeatMonths` interval
            const monthDiff = newDate.diff(original, "months");
            if (monthDiff % repeatMonths !== 0 || newDay !== originalDay) {
              return alert(
                `Invalid start date. Please select the ${originalDay}th every ${repeatMonths} months.`,
              );
            }
          }

          form.setFieldValue("medicationDate", _sDate);
        }
      } else {
        form.setFieldValue("medicationDate", _sDate);
      }
    } else {
      form.setFieldValue("medicationDate", _sDate);
    }
  };

  const onChangeMedEndDate = (_sDate) => {
    if (data) {
      if (form.values?.scheduleType === "custom") {
        if (form.values?.repeatType === "day") {
          const original = moment(data?.expirationDate);
          const newDate = moment(_sDate);

          let diff;

          if (newDate < original) {
            diff = original.diff(newDate, "days");
          } else if (newDate > original) {
            diff = newDate.diff(original, "days");
          }

          let repeatDays = Number(form.values?.repeatEvery);

          // Check if the difference is a multiple of repeatDays
          if (diff % repeatDays !== 0) {
            const remainder = Math.abs(diff % repeatDays);
            let validPrevious, validNext;

            if (newDate.isBefore(original)) {
              // Moving backward
              validPrevious = newDate.clone().subtract(remainder, "days");
              validNext = validPrevious.clone().subtract(repeatDays, "days");
            } else {
              // Moving forward
              validPrevious = newDate.clone().subtract(remainder, "days");
              validNext = newDate.clone().add(repeatDays - remainder, "days");
            }

            return alert("Invalid end date");
          } else {
            form.setFieldValue("expirationDate", _sDate);
          }
          // } else if(form.values?.repeatType === "month"){
        } else {
          form.setFieldValue("expirationDate", _sDate);
        }
      } else {
        form.setFieldValue("expirationDate", _sDate);
      }
    } else {
      form.setFieldValue("expirationDate", _sDate);
    }
  };

  const buildConfidenceMapFromOcr = useCallback((ocrData) => {
    const map = new Map();
    if (!ocrData) return map;
    const fieldMap = {
      medicationName: "medicationName",
      dosage: "dosage",
      unit: "unit",
      frequency: "frequency",
      route: "ingestionMode",
      doseForm: "dosageForm",
      rxNumber: "rxNumber",
      quantity: "totalMedicine",
      instructions: "medicationInstruction",
    };
    Object.entries(fieldMap).forEach(([ocrKey, formKey]) => {
      const entry = ocrData[ocrKey];
      if (entry?.value != null && entry.confidence > 0)
        map.set(formKey, entry.confidence);
    });
    if (ocrData.scheduleType?.confidence > 0)
      map.set("medicationType", ocrData.scheduleType.confidence);
    if (ocrData.narcoticStatus?.confidence > 0)
      map.set("isNarcotic", ocrData.narcoticStatus.confidence);
    if (ocrData.isPrescribed != null)
      map.set("isPrescribed", ocrData.isPrescribed.confidence ?? 100);
    if (
      Array.isArray(ocrData.timeSlots?.value) &&
      ocrData.timeSlots.value.some((t) =>
        /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.test(String(t)),
      ) &&
      ocrData.timeSlots.confidence > 0
    )
      map.set("timeSlots", ocrData.timeSlots.confidence);
    if (
      ocrData.startDate?.value != null &&
      ocrData.startDate.confidence > 0 &&
      moment(
        String(ocrData.startDate.value).trim(),
        ["MM/DD/YYYY", "M/D/YYYY", "MM-DD-YYYY", "YYYY-MM-DD"],
        true,
      ).isValid()
    )
      map.set("medicationDate", ocrData.startDate.confidence);
    if (ocrData.administrationCount?.confidence > 0)
      map.set("medicationadministered", ocrData.administrationCount.confidence);
    if (ocrData.refillsCount?.confidence > 0)
      map.set("refillsLeft", ocrData.refillsCount.confidence);
    return map;
  }, []);

  // Compress images larger than 2 MB so the OCR API doesn't time out on mobile photos.
  const compressImage = (file) =>
    new Promise((resolve) => {
      const MAX_WIDTH = 1920;
      const QUALITY = 0.82;
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        const canvas = document.createElement("canvas");
        let { width, height } = img;
        if (width > MAX_WIDTH) {
          height = Math.round((height * MAX_WIDTH) / width);
          width = MAX_WIDTH;
        }
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) =>
            resolve(
              new File([blob], file.name.replace(/\.[^.]+$/, ".jpg"), {
                type: "image/jpeg",
              }),
            ),
          "image/jpeg",
          QUALITY,
        );
      };
      img.src = objectUrl;
    });

  const handleScanFileChange = async (e) => {
    let file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) file = await compressImage(file);
    stageFilesToBulkList([file]);
    e.target.value = "";
  };

  // ── Webcam helpers ──────────────────────────────────────────────────────────
  // Attach the stream once the video element is in the DOM (cameraOpen → true).
  // iOS Safari needs both srcObject assignment AND an explicit play() after mount;
  // a setTimeout(0) fires before React commits the element, causing a black screen
  // on first open. useEffect runs after the commit, so the element is always ready.
  useEffect(() => {
    if (cameraOpen && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => {});
    }
  }, [cameraOpen]);

  const openCamera = async (facing = "environment") => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: facing } },
      });
      streamRef.current = stream;
      setFacingMode(facing);
      setCameraOpen(true); // useEffect above will assign srcObject + call play()
    } catch {
      showError(
        "Camera access denied or not available. Please allow camera permissions and try again.",
      );
    }
  };

  const switchCamera = async () => {
    const newFacing = facingMode === "environment" ? "user" : "environment";
    streamRef.current?.getTracks().forEach((t) => t.stop());
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: newFacing } },
      });
      streamRef.current = stream;
      setFacingMode(newFacing);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch(() => {});
      }
    } catch {
      showError("Could not switch camera.");
    }
  };

  const closeCamera = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setCameraOpen(false);
  };

  const captureFromCamera = async () => {
    const video = videoRef.current;
    if (!video) return;
    const width = video.videoWidth;
    const height = video.videoHeight;
    if (!width || !height) {
      showError("Camera is still loading. Please wait a moment and try again.");
      return;
    }
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    canvas.getContext("2d").drawImage(video, 0, 0, width, height);
    canvas.toBlob(
      async (blob) => {
        if (!blob || blob.size < 5000) {
          showError("Failed to capture photo. Please try again.");
          return;
        }
        closeCamera();
        let file = new File([blob], `camera-capture-${Date.now()}.jpg`, {
          type: "image/jpeg",
        });
        if (file.size > 2 * 1024 * 1024) file = await compressImage(file);
        stageFilesToBulkList([file]);
      },
      "image/jpeg",
      0.92,
    );
  };

  const scannedBadge = (formKey) => {
    const confidence = scannedFields.get(formKey);
    if (confidence === undefined) return null;
    const val = form.values[formKey];
    const isEmpty =
      val === undefined ||
      val === null ||
      val === "" ||
      (Array.isArray(val) && val.length === 0);
    if (isEmpty) return null;
    const color =
      confidence >= 80 ? "#22c55e" : confidence >= 50 ? "#f59e0b" : "#ef4444";
    return (
      <span
        style={{
          marginLeft: 6,
          fontSize: 10,
          fontWeight: 700,
          color,
          background: color + "1A",
          borderRadius: 4,
          padding: "1px 5px",
          verticalAlign: "middle",
        }}
      >
        {confidence}%
      </span>
    );
  };

  const resetScan = () => {
    setScanState("idle");
    setScannedFields(new Map());
    setScanImage(null);
    setSingleScanExistingActive(false);
    smsOcrDataRef.current = null;
    if (scanInputRef.current) scanInputRef.current.value = "";
    form.reset();
    resetSmsForm();
  };

  const savedContacts = useMemo(() => {
    const list = [];
    if (form2?.emergencyContactName && form2?.emergencyContactPhoneNumber)
      list.push({
        name: form2.emergencyContactName,
        phone: form2.emergencyContactPhoneNumber,
        role: "Emergency Contact",
      });
    if (form2?.caseManagerName && form2?.caseManagerPhoneNumber)
      list.push({
        name: form2.caseManagerName,
        phone: form2.caseManagerPhoneNumber,
        role: "Case Manager",
      });
    if (form2?.pcpName && form2?.pcpPhoneNumber)
      list.push({
        name: form2.pcpName,
        phone: form2.pcpPhoneNumber,
        role: "Primary Care Provider",
      });
    (form2?.additionalProvider || []).forEach((p) => {
      if (p.providerName && p.providerPhone)
        list.push({
          name: p.providerName,
          phone: p.providerPhone,
          role: p.providerType || "Provider",
        });
    });
    return list;
  }, [form2]);

  const isValidSmsPhone = (p) => (p?.replace(/\D/g, "")?.length || 0) >= 10;

  const smsFormatCountdown = (s) => {
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${String(r).padStart(2, "0")}`;
  };

  useEffect(() => {
    if (!smsSent || smsImageReceived) return;
    if (smsSecondsLeft <= 0) {
      setSmsSent(false);
      setSmsPhone("+1");
      return;
    }
    const t = setTimeout(() => setSmsSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [smsSent, smsSecondsLeft, smsImageReceived]);

  const handleSmsSend = async () => {
    if (!isValidSmsPhone(smsPhone)) return;
    const clientId = selectedClient ? selectedClient?.id : client?.id;
    setSmsSending(true);
    try {
      await requestSmsUpload({
        clientId,
        phoneNumber: smsPhone,
        note: smsNote.trim() || undefined,
      });
      setSmsSent(true);
      setSmsSecondsLeft(600);
    } catch {
      // error shown by service
    } finally {
      setSmsSending(false);
    }
  };

  const resetSmsForm = () => {
    setSmsPhone("+1");
    setSmsNote("");
    setSmsSent(false);
    setSmsSecondsLeft(0);
    setSmsImageReceived(false);
  };

  // Multi-med OCR now runs as a polled background job — stop polling if the
  // component unmounts mid-scan (e.g. route change) rather than leaking requests.
  useEffect(() => {
    return () => {
      bulkScanAbortControllerRef.current?.abort();
      cancelAnimationFrame(bulkProgressRafRef.current);
    };
  }, []);

  // Shared staging for every entry point (desktop file-picker, desktop camera,
  // desktop webcam capture) — all land in bulkFiles; OCR only runs on Process click.
  const stageFilesToBulkList = (filesToStage) => {
    if (!filesToStage.length) return;
    const staged = filesToStage.map((file, i) => ({
      id: `bulk-${Date.now()}-${i}`,
      name: file.name,
      size: file.size,
      sizeLabel:
        file.size >= 1024 * 1024
          ? (file.size / (1024 * 1024)).toFixed(1) + " MB"
          : (file.size / 1024).toFixed(0) + " KB",
      file,
      status: "queued",
      ocrResult: null,
    }));
    setBulkFiles((prev) => [...prev, ...staged]);
    setPanelMode("upload");
  };

  const handleBulkFilesSelect = (e) => {
    const selected = Array.from(e.target.files || []);
    stageFilesToBulkList(selected);
    if (bulkFileInputRef.current) bulkFileInputRef.current.value = "";
  };

  const handleBulkFilesDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDropzoneDragging(false);
    const dropped = Array.from(e.dataTransfer?.files || []);
    stageFilesToBulkList(dropped);
  };

  const handleBulkRemoveFile = (id) => {
    setBulkFiles((prev) => {
      const next = prev.filter((f) => f.id !== id);
      if (next.length === 0) {
        setPanelMode(null);
        setPendingScanJob(null);
      }
      return next;
    });
  };

  // Builds bulk-review rows from a scan-ocr-multi result for a single
  // uploaded file — shared by the initial process call and by the "Check
  // status" resume flow so a poll timeout doesn't need its own copy.
  const finishMultiMedsForSingleFile = (multiMeds, sourceFileName, sizeLabel) => {
    const KEY_FIELDS = ["medicationName", "dosage", "frequency", "route","scheduleType","timeSlots","administrationCount"];
    const batchOrderCount = {};
    multiMeds.forEach((ocr) => {
      const k = getMedOrderKey(ocr);
      if (k) batchOrderCount[k] = (batchOrderCount[k] || 0) + 1;
    });
    const seenOrders = new Set();
    const rows = multiMeds.map((ocr, idx) => {
      const ocrName = (ocr?.medicationName?.value || "").toLowerCase().trim();
      const orderKey = getMedOrderKey(ocr);
      const isInBatch =
        orderKey.length > 0 && (batchOrderCount[orderKey] || 0) > 1;
      const isExistingActive =
        ocrName.length > 0 && activeExistingMedNames.has(ocrName);
      const isDuplicate =
        (isInBatch && seenOrders.has(orderKey)) || isExistingActive;
      if (orderKey) seenOrders.add(orderKey);
      const keyConfs = KEY_FIELDS.map((f) => ocr[f]?.confidence ?? 0);
      const avgConf = keyConfs.reduce((a, b) => a + b, 0) / keyConfs.length;
      const minConf = Math.min(...keyConfs);
      const needsReview = !isDuplicate && (minConf < 40 || avgConf < 60);
      return {
        id: `multi-${Date.now()}-${idx}`,
        name: ocr?.medicationName?.value || `Medication ${idx + 1}`,
        sourceFile: sourceFileName || "",
        sizeLabel: sizeLabel || "",
        status: "done",
        ocrResult: ocr,
        isDuplicate,
        isExistingActiveDuplicate: isExistingActive,
        needsReview,
        saved: false,
        skipped: false,
      };
    });

    cancelAnimationFrame(bulkProgressRafRef.current);
    setBulkFakeProgress(100);
    setBulkFiles([]);
    setBulkProcessing(false);

    if (rows.length === 1) {
      applyOcrPrefill(rows[0].ocrResult);
      applyMedicationCountFromOcr(rows[0].ocrResult);
      setScannedFields(buildConfidenceMapFromOcr(rows[0].ocrResult));
      setScanState("done");
      setSingleScanExistingActive(!!rows[0].isExistingActiveDuplicate);
      setPanelMode(null);
    } else {
      setBulkReviewRows(rows);
      setBulkReviewFilter("all");
      setPanelMode("bulk-review");
    }
  };

  // Resumes polling a scan-ocr-multi job that previously timed out client-side,
  // instead of re-uploading the file and starting a brand-new backend job.
  const handleCheckScanStatus = async () => {
    if (!pendingScanJob) return;
    const { jobId, fileName, sizeLabel } = pendingScanJob;

    setBulkProcessing(true);
    setBulkProcessingLabel(
      "Processing your document(s)… this can take a few minutes for large files",
    );
    setBulkFakeProgress(0);
    const startTime = Date.now();
    const tau = 20000;
    const animateProgress = () => {
      const elapsed = Date.now() - startTime;
      const pct = 85 * (1 - Math.exp(-elapsed / tau));
      setBulkFakeProgress(pct);
      bulkProgressRafRef.current = requestAnimationFrame(animateProgress);
    };
    bulkProgressRafRef.current = requestAnimationFrame(animateProgress);

    bulkScanAbortControllerRef.current?.abort();
    const abortController = new AbortController();
    bulkScanAbortControllerRef.current = abortController;

    try {
      const multiResult = await resumeMedicationScanMulti(jobId, {
        signal: abortController.signal,
      });
      if (abortController.signal.aborted) return;
      if (Array.isArray(multiResult) && multiResult.length >= 1) {
        setPendingScanJob(null);
        finishMultiMedsForSingleFile(multiResult, fileName, sizeLabel);
        return;
      }
      cancelAnimationFrame(bulkProgressRafRef.current);
      setBulkFakeProgress(100);
      setBulkProcessing(false);
      setPendingScanJob(null);
      setBulkFiles([]);
      showError("We couldn't process this document. Please try again.");
    } catch (err) {
      if (abortController.signal.aborted) return;
      cancelAnimationFrame(bulkProgressRafRef.current);
      setBulkFakeProgress(100);
      setBulkProcessing(false);
      if (err?.code === "OCR_MULTI_POLL_TIMEOUT") {
        setPendingScanJob({ jobId: err.jobId || jobId, fileName, sizeLabel });
        showError("Still processing your document — this can take a few minutes for large files. Please check back shortly.");
        return;
      }
      const errData = err?.response?.data;
      setPendingScanJob(null);
      setBulkFiles([]);
      if (errData?.multiplePatients) {
        showError("This document contains medications for more than one patient. Please upload files for one patient at a time.");
      } else {
        showError(errData?.message || "We couldn't process this document. Please try again.");
      }
    }
  };

  const handleBulkProcess = async () => {
    setPendingScanJob(null);
    setBulkProcessing(true);
    setBulkProcessingLabel("Uploading files");
    setBulkFakeProgress(0);
    // Multi-med OCR runs as a background job now (can take minutes on large
    // multi-image docs), so ease toward 85% indefinitely instead of assuming
    // a fixed duration — it keeps creeping instead of stalling on long jobs.
    const startTime = Date.now();
    const tau = 20000;
    const animateProgress = () => {
      const elapsed = Date.now() - startTime;
      const pct = 85 * (1 - Math.exp(-elapsed / tau));
      setBulkFakeProgress(pct);
      bulkProgressRafRef.current = requestAnimationFrame(animateProgress);
    };
    bulkProgressRafRef.current = requestAnimationFrame(animateProgress);

    bulkScanAbortControllerRef.current?.abort();
    const abortController = new AbortController();
    bulkScanAbortControllerRef.current = abortController;
    const markProcessing = () =>
      setBulkProcessingLabel(
        "Processing your document(s)… this can take a few minutes for large files",
      );

    // Mark all files as processing immediately
    const results = bulkFiles.map((f) => ({ ...f, status: "processing" }));
    setBulkFiles([...results]);

    try {
      // Compress images > 2 MB before sending; skip compression for documents
      const filesToSend = await Promise.all(
        results.map(async (r) => {
          try {
            const isImage = r.file.type.startsWith("image/");
            return isImage && r.file.size > 2 * 1024 * 1024
              ? await compressImage(r.file)
              : r.file;
          } catch {
            return r.file;
          }
        }),
      );

      // For a single file, try the multi-medication endpoint first.
      // If it finds multiple meds the document is a medication order sheet — go straight to bulk review.
      // If it finds exactly one, use that result and skip the second API call.
      if (filesToSend.length === 1) {
        let multiMeds = null;
        try {
          const multiResult = await scanMedicationLabelMulti(filesToSend[0], {
            signal: abortController.signal,
            onProcessing: markProcessing,
          });
          if (Array.isArray(multiResult) && multiResult.length >= 1) {
            multiMeds = multiResult;
          }
        } catch (multiErr) {
          if (abortController.signal.aborted) return;
          const errData = multiErr?.response?.data;
          if (errData?.multiplePatients) {
            cancelAnimationFrame(bulkProgressRafRef.current);
            setBulkFakeProgress(100);
            setBulkFiles([]);
            setBulkProcessing(false);
            showError("This document contains medications for more than one patient. Please upload files for one patient at a time.");
            return;
          }
          if (multiErr?.code === "OCR_MULTI_POLL_TIMEOUT") {
            cancelAnimationFrame(bulkProgressRafRef.current);
            setBulkFakeProgress(100);
            setBulkProcessing(false);
            setPendingScanJob({
              jobId: multiErr.jobId,
              fileName: filesToSend[0]?.name || "",
              sizeLabel: results[0]?.sizeLabel || "",
            });
            showError("Still processing your document — this can take a few minutes for large files. Click \"Check status\" when ready.");
            return;
          }
          if (errData?.status === "failed") {
            cancelAnimationFrame(bulkProgressRafRef.current);
            setBulkFakeProgress(100);
            setBulkFiles([]);
            setBulkProcessing(false);
            showError(errData?.message || "We couldn't process this document. Please try again.");
            return;
          }
          // Any other error — mark as failed below
        }

        if (multiMeds) {
          finishMultiMedsForSingleFile(
            multiMeds,
            filesToSend[0]?.name,
            results[0]?.sizeLabel,
          );
          return;
        }
      }

      // Multiple files — run scan-ocr-multi on each file in parallel so every
      // document yields all its medications, not just one per file.
      if (filesToSend.length > 1) {
        const KEY_FIELDS = ["medicationName", "dosage", "frequency", "route"];
        const perFileResults = await Promise.all(
          filesToSend.map(async (file, i) => {
            try {
              const multiResult = await scanMedicationLabelMulti(file, {
                signal: abortController.signal,
                onProcessing: markProcessing,
              });
              if (Array.isArray(multiResult) && multiResult.length >= 1) {
                return { index: i, success: true, meds: multiResult };
              }
              return { index: i, success: false, meds: [] };
            } catch (multiErr) {
              if (abortController.signal.aborted) {
                return { index: i, success: false, aborted: true };
              }
              const errData = multiErr?.response?.data;
              if (errData?.multiplePatients) {
                return { index: i, success: false, multiplePatients: true };
              }
              if (multiErr?.code === "OCR_MULTI_POLL_TIMEOUT") {
                return { index: i, success: false, timedOut: true };
              }
              if (errData?.status === "failed") {
                return { index: i, success: false, failedMessage: errData?.message };
              }
              return { index: i, success: false, meds: [] };
            }
          })
        );

        if (abortController.signal.aborted) return;

        const hasMultiPatientError = perFileResults.some((r) => r.multiplePatients);
        if (hasMultiPatientError) {
          showError("One or more files contain medications for more than one patient. Please upload files for one patient at a time.");
        }
        const hasTimedOutError = perFileResults.some((r) => r.timedOut);
        if (hasTimedOutError) {
          showError("One or more files are still processing — this can take a few minutes for large documents. Please check back shortly.");
        }
        const failedMessages = [
          ...new Set(perFileResults.map((r) => r.failedMessage).filter(Boolean)),
        ];
        if (failedMessages.length > 0) {
          showError(failedMessages.join(" "));
        }

        const allRows = [];
        perFileResults.forEach((res) => {
          if (res.success && res.meds?.length) {
            res.meds.forEach((ocr, idx) => {
              allRows.push({
                id: `multi-${res.index}-${idx}`,
                name: ocr?.medicationName?.value || `Medication ${allRows.length + 1}`,
                sourceFile: filesToSend[res.index]?.name || "",
                sizeLabel: results[res.index]?.sizeLabel || "",
                status: "done",
                ocrResult: ocr,
                saved: false,
                skipped: false,
              });
            });
          }
        });

        if (allRows.length > 0) {
          const batchOrderCount = {};
          allRows.forEach((r) => {
            const k = getMedOrderKey(r.ocrResult);
            if (k) batchOrderCount[k] = (batchOrderCount[k] || 0) + 1;
          });
          const seenOrders = new Set();
          const rows = allRows.map((r) => {
            const ocr = r.ocrResult;
            const ocrName = (ocr?.medicationName?.value || "").toLowerCase().trim();
            const orderKey = getMedOrderKey(ocr);
            const isInBatch = orderKey.length > 0 && (batchOrderCount[orderKey] || 0) > 1;
            const isExistingActive =
              ocrName.length > 0 && activeExistingMedNames.has(ocrName);
            const isDuplicate =
              (isInBatch && seenOrders.has(orderKey)) || isExistingActive;
            if (orderKey) seenOrders.add(orderKey);
            const keyConfs = KEY_FIELDS.map((f) => ocr[f]?.confidence ?? 0);
            const avgConf = keyConfs.reduce((a, b) => a + b, 0) / keyConfs.length;
            const minConf = Math.min(...keyConfs);
            const needsReview = !isDuplicate && (minConf < 40 || avgConf < 60);
            return {
              ...r,
              isDuplicate,
              isExistingActiveDuplicate: isExistingActive,
              needsReview,
            };
          });

          cancelAnimationFrame(bulkProgressRafRef.current);
          setBulkFakeProgress(100);
          setBulkFiles([]);
          setBulkProcessing(false);
          setBulkReviewRows(rows);
          setBulkReviewFilter("all");
          setPanelMode("bulk-review");
          return;
        }
      }

      // scan-ocr-multi is the only OCR endpoint for this flow — if it didn't
      // yield usable results above, mark every file failed rather than retrying
      // with a different endpoint.
      results.forEach((_, i) => {
        results[i] = { ...results[i], status: "failed" };
      });
    } catch {
      // Full request failure — mark all as failed
      results.forEach((_, i) => {
        results[i] = { ...results[i], status: "failed" };
      });
    }

    cancelAnimationFrame(bulkProgressRafRef.current);
    setBulkFakeProgress(100);
    setBulkFiles([...results]);
    setBulkProcessing(false);

    const doneFiles = results.filter((r) => r.status === "done");

    if (results.length === 1) {
      if (doneFiles.length === 1) {
        applyOcrPrefill(doneFiles[0].ocrResult);
        applyMedicationCountFromOcr(doneFiles[0].ocrResult);
        setScannedFields(buildConfidenceMapFromOcr(doneFiles[0].ocrResult));
        setScanState("done");
        const scannedName = (
          doneFiles[0].ocrResult?.medicationName?.value || ""
        )
          .toLowerCase()
          .trim();
        setSingleScanExistingActive(
          scannedName.length > 0 && activeExistingMedNames.has(scannedName),
        );
      }
      setBulkFiles([]);
      setPanelMode(null);
    } else {
      const KEY_FIELDS = ["medicationName", "dosage", "frequency", "route"];
      // Count how many times each distinct medication order (name + schedule/
      // route/rx) appears in this batch
      const batchOrderCount = {};
      results.forEach((r) => {
        const k = getMedOrderKey(r.ocrResult);
        if (k) batchOrderCount[k] = (batchOrderCount[k] || 0) + 1;
      });
      const seenOrders = new Set();
      const rows = results.map((r) => {
        const ocr = r.ocrResult;
        const ocrName = (ocr?.medicationName?.value || "").toLowerCase().trim();
        const orderKey = getMedOrderKey(ocr);
        const isInBatch =
          orderKey.length > 0 && (batchOrderCount[orderKey] || 0) > 1;
        const isExistingActive =
          ocrName.length > 0 && activeExistingMedNames.has(ocrName);
        const isDuplicate =
          (isInBatch && seenOrders.has(orderKey)) || isExistingActive;
        if (orderKey) seenOrders.add(orderKey);
        const keyConfs = ocr
          ? KEY_FIELDS.map((f) => ocr[f]?.confidence ?? 0)
          : [];
        const avgConf = keyConfs.length
          ? keyConfs.reduce((a, b) => a + b, 0) / keyConfs.length
          : 0;
        const minConf = keyConfs.length ? Math.min(...keyConfs) : 0;
        const needsReview =
          !isDuplicate && r.status === "done" && (minConf < 40 || avgConf < 60);
        return {
          id: r.id,
          name: r.name,
          sourceFile: r.name,
          sizeLabel: r.sizeLabel || "",
          status: r.status,
          ocrResult: ocr,
          isDuplicate,
          isExistingActiveDuplicate: isExistingActive,
          needsReview,
          saved: false,
          skipped: false,
        };
      });
      setBulkReviewRows(rows);
      setBulkReviewFilter("all");
      setPanelMode("bulk-review");
    }
  };

  const handleBulkRowReview = (row) => {
    setBulkDupeSelectedIds(new Set());
    setBulkCombineConfirm(null);
    setBulkEditingRowId(row.id);
  };

  const handleBulkRowEdit = (row, returnTo = "bulk-review") => {
    if (!row.ocrResult) return;
    const ocr = row.ocrResult;
    form.reset();
    setType("scheduled");
    applyOcrPrefill(ocr);
    if (ocr._formOverrides) {
      form.setValues((current) => ({
        ...current,
        ...ocr._formOverrides,
      }));
    }
    applyMedicationCountFromOcr(ocr, ocr._formOverrides?.isMedicationCountEnabled);
    setScannedFields(buildConfidenceMapFromOcr(ocr));
    setScanState("done");
    setBulkEditingRowId(row.id);
    setPanelMode(null);
    afterBulkRowSaveRef.current = (latestRows) => {
      if (returnTo === "bulk-save-preview") {
        setPanelMode("bulk-save-preview");
        setBulkEditingRowId(null);
      } else {
        const updated = (latestRows || bulkReviewRows).map((r) =>
          r.id === row.id
            ? { ...r, needsReview: false, isDuplicate: false }
            : r,
        );
        let nextReviewRow = updated.find(
          (r) =>
            r.id !== row.id &&
            bulkSelectedIds.has(r.id) &&
            !r.saved &&
            (r.isDuplicate || r.needsReview || r.status === "failed"),
        );
        let finalRows = updated;
        if (nextReviewRow?.isDuplicate) {
          // Editing a duplicate resolves the whole batch-duplicate group
          // (same as Combine): keep this row, drop every sibling that is the
          // same medication order (name + schedule/route/rx) so the same
          // order can't come back around as a second "ready" entry. Rows that
          // only share a name but are separate orders (e.g. a Scheduled and a
          // PRN order for the same drug) must not be removed.
          const nextOrderKey = getMedOrderKey(nextReviewRow.ocrResult);
          const siblingIdsToRemove = new Set(
            updated
              .filter(
                (r) =>
                  r.id !== nextReviewRow.id &&
                  r.status === "done" &&
                  !r.saved &&
                  !r.skipped &&
                  getMedOrderKey(r.ocrResult) === nextOrderKey,
              )
              .map((r) => r.id),
          );
          finalRows = updated.filter((r) => !siblingIdsToRemove.has(r.id));
          setBulkSelectedIds((prev) => {
            const next = new Set(prev);
            siblingIdsToRemove.forEach((id) => next.delete(id));
            return next;
          });
          nextReviewRow = { ...nextReviewRow, isDuplicate: false };
          finalRows = finalRows.map((r) =>
            r.id === nextReviewRow.id ? nextReviewRow : r,
          );
        }
        setBulkReviewRows(finalRows);
        if (nextReviewRow) {
          setBulkDupeSelectedIds(new Set());
          setBulkCombineConfirm(null);
          handleBulkRowEdit(nextReviewRow, returnTo);
          const scrollFormBoxToTop = () => {
            let el = formBoxRef.current;
            while (el) {
              if (el.scrollTop > 0 || el.scrollHeight > el.clientHeight) {
                el.scrollTop = 0;
              }
              el = el.parentElement;
            }
          };
          scrollFormBoxToTop();
          requestAnimationFrame(scrollFormBoxToTop);
          setTimeout(scrollFormBoxToTop, 0);
          setTimeout(scrollFormBoxToTop, 50);
        } else {
          setBulkEditingRowId(null);
          setPanelMode(returnTo);
        }
      }
    };
  };

  // Sync isMedicationCountEnabled whenever a bulk row edit starts
  useEffect(() => {
    if (!bulkEditingRowId) return;
    const row = bulkReviewRows.find((r) => r.id === bulkEditingRowId);
    if (!row?.ocrResult) return;
    const ocr = row.ocrResult;
    const overrides = ocr._formOverrides || {};
    applyMedicationCountFromOcr(ocr, overrides.isMedicationCountEnabled);
  }, [bulkEditingRowId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBulkRowSkip = (id) => {
    setBulkReviewRows((prev) => {
      const updated = prev.filter((r) => r.id !== id);
      const allSaved = updated.length === 0 || updated.every((r) => r.saved);
      if (allSaved) {
        setPanelMode("bulk-saved");
      } else if (bulkEditingRowId === id) {
        const nextReviewRow = updated.find(
          (r) =>
            bulkSelectedIds.has(r.id) &&
            !r.saved &&
            (r.isDuplicate || r.needsReview || r.status === "failed"),
        );
        if (nextReviewRow) {
          setBulkDupeSelectedIds(new Set());
          setBulkCombineConfirm(null);
          setBulkEditingRowId(nextReviewRow.id);
        } else {
          setBulkEditingRowId(null);
        }
      }
      return updated;
    });
    setBulkSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const handleBulkRowAccept = (row) => {
    setBulkReviewRows((prev) => {
      const orderKey = getMedOrderKey(row.ocrResult);
      const dupeIdsToRemove =
        row.isDuplicate && orderKey
          ? new Set(
              prev
                .filter(
                  (r) =>
                    r.id !== row.id &&
                    r.status === "done" &&
                    !r.saved &&
                    !r.skipped &&
                    getMedOrderKey(r.ocrResult) === orderKey,
                )
                .map((r) => r.id),
            )
          : new Set();
      const updated = prev
        .filter((r) => !dupeIdsToRemove.has(r.id))
        .map((r) =>
          r.id === row.id
            ? { ...r, needsReview: false, isDuplicate: false }
            : r,
        );
      const nextReviewRow = updated.find(
        (r) =>
          r.id !== row.id &&
          bulkSelectedIds.has(r.id) &&
          !r.saved &&
          (r.isDuplicate || r.needsReview || r.status === "failed"),
      );
      if (nextReviewRow) {
        setBulkDupeSelectedIds(new Set());
        setBulkCombineConfirm(null);
        setBulkEditingRowId(nextReviewRow.id);
      } else {
        setBulkEditingRowId(null);
      }
      return updated;
    });
    setBulkSelectedIds((prev) => {
      if (!prev.size) return prev;
      const next = new Set(prev);
      let changed = false;
      const orderKey = getMedOrderKey(row.ocrResult);
      bulkReviewRows.forEach((r) => {
        if (
          r.id !== row.id &&
          r.isDuplicate &&
          getMedOrderKey(r.ocrResult) === orderKey
        ) {
          if (next.delete(r.id)) changed = true;
        }
      });
      return changed ? next : prev;
    });
  };

  const handleCancelBulkRowEdit = () => {
    form.reset();
    setScanState("idle");
    setScannedFields(new Map());
    setScanImage(null);
    afterBulkRowSaveRef.current = null;
    setBulkEditingRowId(null);
    setPanelMode("bulk-review");
  };

  const scanStyles = {
    scanCard: {
      background: "var(--app-surface)",
      border: "1px solid var(--app-border)",
      borderRadius: 12,
      padding: 16,
      marginBottom: 0,
    },
    scanHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      marginBottom: 12,
    },
    scanIconWrap: {
      width: 36,
      height: 36,
      borderRadius: 8,
      background: "var(--app-tint-purple)",
      border: "1px solid var(--app-border)",
      display: "grid",
      placeItems: "center",
      flexShrink: 0,
    },
    scanTitle: { margin: 0, fontSize: 14, fontWeight: 700, color: "var(--app-text)", fontFamily: "Inter" },
    scanSubtitle: { margin: "2px 0 0", fontSize: 12, color: "var(--app-text-muted)", fontFamily: "Inter", lineHeight: 1.4 },
    scanResetBtn: {
      height: 28,
      padding: "0 10px",
      borderRadius: 6,
      border: "1px solid var(--app-border)",
      background: "var(--app-surface)",
      color: "var(--app-text)",
      fontSize: 12,
      fontWeight: 600,
      fontFamily: "Inter",
      cursor: "pointer",
      boxShadow: "0 1px 2px 0 rgba(16,24,40,0.05)",
    },
    sectionCard: {
      background: "var(--app-surface)",
      borderRadius: 12,
      border: "1px solid var(--app-border)",
      padding: 16,
      display: "flex",
      flexDirection: "column",
      gap: 12,
    },
    sectionTitle: {
      margin: "0 0 12px 0",
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: 0.6,
      color: "var(--app-text-muted)",
      textTransform: "uppercase",
      fontFamily: "Inter",
    },
  };

  return (
    <Drawer
      // className={classes.addMedicationModal}
      opened={opened}
      onClose={() => {
        bulkScanAbortControllerRef.current?.abort();
        cancelAnimationFrame(bulkProgressRafRef.current);
        onclose();
        getAllPatients?.(currentPage, true, tabName);
        form?.reset();
        setRxStrengthOptions([]);
        setPanelMode(null);
        setBulkFiles([]);
        setBulkProcessing(false);
        setBulkReviewRows([]);
        setBulkEditingRowId(null);
        afterBulkRowSaveRef.current = null;
        resetScan();
      }}
      closeOnClickOutside={false}
      withCloseButton={false}
      offset={isMobile ? 0 : 8}
      size={isMobile ? "100%" : "55%"}
      sx={{
        ".mantine-11shxvp": {
          background: "var(--app-surface-2)",
          borderTopLeftRadius: isMobile ? 0 : 16,
          borderBottomLeftRadius: isMobile ? 0 : 16,
        },
        ".mantine-1q36a81": {
          padding: "0",
        },
      }}
      zIndex={1000}
      position="right"
      overlayProps={{ backgroundOpacity: 2.5, blur: 4 }}
      shadow="md"
      styles={{
        root: { touchAction: isMobile ? "pan-x pan-y pinch-zoom" : undefined },
        overlay: {
          touchAction: isMobile ? "pan-x pan-y pinch-zoom" : undefined,
        },
        inner: { touchAction: isMobile ? "pan-x pan-y pinch-zoom" : undefined },
        body: { touchAction: isMobile ? "pan-x pan-y pinch-zoom" : undefined },
      }}
    >
      <form
        className={classes.modalBody}
        onSubmit={form?.onSubmit((values) => onSave(values))}
      >
        <div className={classes.modalTopBar}>
          <h3 className={classes.modalTitle}>
            {data
              ? isPreview
                ? "Preview Medication"
                : "Change Medication"
              : "Add new medication"}
          </h3>
          <button
            type="button"
            className={classes.modalCloseBtn}
            onClick={() => {
              bulkScanAbortControllerRef.current?.abort();
              cancelAnimationFrame(bulkProgressRafRef.current);
              onclose();
              getAllPatients?.(currentPage, true, tabName);
              form?.reset();
              setRxStrengthOptions([]);
              setPanelMode(null);
              setBulkFiles([]);
              setBulkProcessing(false);
              setBulkReviewRows([]);
              setBulkEditingRowId(null);
              afterBulkRowSaveRef.current = null;
              resetScan();
            }}
          >
            ✕
          </button>
        </div>

        {/* ── Wizard step rail ── */}
        {!isPreview && panelMode !== null && (
          <div className={classes.wizardRail}>
            {(() => {
              const stepIdx =
                panelMode === "bulk-saved"
                  ? 2
                  : panelMode === "bulk-save-preview"
                    ? 2
                    : panelMode === "bulk-review"
                      ? 1
                      : 0;
              return [
                { label: "Upload", key: "upload", Icon: LuUpload },
                { label: "Review", key: "review", Icon: LuFileText },
                { label: "Save", key: "save", Icon: LuSave },
              ].map((step, idx) => {
                const isActive = idx === stepIdx;
                const isComplete = idx < stepIdx;
                const connectorFilled = idx > 0 && idx - 1 < stepIdx;
                return (
                  <React.Fragment key={step.key}>
                    {idx > 0 && (
                      <div
                        className={cx(
                          classes.wizardConnector,
                          connectorFilled ? classes.wizardConnectorFilled : "",
                        )}
                      />
                    )}
                    <div className={classes.wizardStepItem}>
                      <div
                        className={cx(
                          classes.wizardCircle,
                          isActive
                            ? classes.wizardCircleActive
                            : isComplete
                              ? classes.wizardCircleComplete
                              : classes.wizardCircleInactive,
                        )}
                      >
                        {isComplete ? (
                          <LuCheck size={16} />
                        ) : (
                          <step.Icon size={16} />
                        )}
                      </div>
                      <span
                        className={cx(
                          classes.wizardLabel,
                          isActive
                            ? classes.wizardLabelActive
                            : isComplete
                              ? classes.wizardLabelComplete
                              : "",
                        )}
                      >
                        {step.label}
                      </span>
                    </div>
                  </React.Fragment>
                );
              });
            })()}
          </div>
        )}

        <div className={classes.formBox} ref={formBoxRef}>
          <div className={classes.wrapper}>
            <div className={classes.clientCard}>
              <img
                src={
                  selectedClient
                    ? decrypt(selectedClient?.imageurl) || defaultImage
                    : decrypt(client?.imageurl) || defaultImage
                }
                alt="client"
                className={classes.clientAvatar}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = defaultImage;
                }}
              />
              <div className={classes.clientInfo}>
                <p className={classes.clientName}>
                  {selectedClient
                    ? decrypt(selectedClient?.name)
                    : client?.name}
                </p>
                <p className={classes.clientId}>
                  {selectedClient
                    ? selectedClient?.id?.slice(0, 6)?.toUpperCase()
                    : client?.id?.slice(0, 6)?.toUpperCase()}
                </p>
              </div>
            </div>
            {/* ── Discontinue Date ── */}
            {data?.discontinued && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "12px 16px",
                  marginBottom: 16,
                  borderRadius: 8,
                  background: "var(--app-tint-red)",
                  border: "1px solid var(--app-ink-red)",
                }}
              >
                <span style={{ fontWeight: 600, fontFamily: "Inter", color: "var(--app-ink-red)" }}>
                  Discontinue Date
                </span>
                <span style={{ fontFamily: "Inter", color: "var(--app-ink-red)" }}>
                  {data?.discontinuedOn
                    ? moment(data.discontinuedOn).format("MM/DD/YYYY")
                    : "—"}
                </span>
              </div>
            )}
            {/* ── Scan medication ── */}
            {!isPreview && !bulkEditingRowId && !data &&
              panelMode !== "bulk-review" &&
              panelMode !== "bulk-save-preview" &&
              panelMode !== "bulk-saved" && (
              <>
                <div style={scanStyles.scanCard}>
                  {/* header row */}
                  <div style={scanStyles.scanHeader}>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 10 }}
                    >
                      <span style={scanStyles.scanIconWrap}>
                        {/* scan-line icon */}
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="var(--app-ink-purple)"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <line x1="3" y1="12" x2="21" y2="12" />
                          <path d="M3 6h2M19 6h2M3 18h2M19 18h2M7 3v3M17 3v3M7 18v3M17 18v3" />
                        </svg>
                      </span>
                      <div>
                        <p style={scanStyles.scanTitle}>Scan medication</p>
                        <p style={scanStyles.scanSubtitle}>
                          Snap the Rx label or pill bottle. We'll read the name,
                          strength, route, frequency, and Rx number and pre-fill
                          the form.
                        </p>
                      </div>
                    </div>

                    {/* Upload using a mobile phone — inline popover */}
                    <div style={{ position: "relative", flexShrink: 0 }}>
                      <button
                        type="button"
                        onClick={() => setSmsPopoverOpen(!smsPopoverOpen)}
                        style={{
                          background: "transparent",
                          border: 0,
                          padding: 0,
                          cursor: "pointer",
                          fontFamily: "Inter",
                          fontSize: 12,
                          fontWeight: 600,
                          color: "var(--app-ink-purple)",
                          textDecoration: "underline",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Upload using a mobile phone
                      </button>

                      {smsPopoverOpen && (
                        <>
                          <div
                            onClick={() => setSmsPopoverOpen(false)}
                            style={{
                              position: "fixed",
                              inset: 0,
                              zIndex: 1004,
                              background: "transparent",
                            }}
                          />
                          <div
                            style={{
                              position: "absolute",
                              top: "calc(100% + 8px)",
                              right: 0,
                              zIndex: 1005,
                              width: 320,
                              background: "var(--app-surface)",
                              border: "1px solid var(--app-border)",
                              borderRadius: 10,
                              padding: 14,
                              boxShadow: "0 12px 28px rgba(15,23,42,0.18)",
                              fontFamily: "Inter",
                            }}
                          >
                            <button
                              type="button"
                              aria-label="Close"
                              onClick={() => setSmsPopoverOpen(false)}
                              style={{
                                position: "absolute",
                                top: 8,
                                right: 8,
                                width: 24,
                                height: 24,
                                borderRadius: 999,
                                border: 0,
                                background: "transparent",
                                cursor: "pointer",
                                color: "var(--app-text-muted)",
                                display: "grid",
                                placeItems: "center",
                                transition: "background .12s ease",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background =
                                  "var(--app-surface-2)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background =
                                  "transparent";
                              }}
                            >
                              <LuX size={14} color="var(--app-text-muted)" />
                            </button>

                            <p
                              style={{
                                margin: "0 24px 10px 0",
                                fontSize: 12,
                                fontWeight: 500,
                                color: "var(--app-text)",
                                lineHeight: 1.4,
                              }}
                            >
                              Send a secure link to a phone number to upload an
                              image of the medication.
                            </p>

                            {smsSent ? (
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 8,
                                  padding: "10px 12px",
                                  borderRadius: 8,
                                  background: "var(--app-tint-green)",
                                  border: "1px solid var(--app-ink-green)",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    color: "var(--app-ink-green)",
                                    fontSize: 12,
                                    fontWeight: 700,
                                  }}
                                >
                                  <LuCheck size={14} color="var(--app-ink-green)" />
                                  SMS sent to {smsPhone}
                                </div>
                                <p
                                  style={{
                                    margin: 0,
                                    fontSize: 12,
                                    color: "var(--app-ink-green)",
                                    lineHeight: 1.4,
                                  }}
                                >
                                  Link expires in{" "}
                                  <strong
                                    style={{
                                      fontVariantNumeric: "tabular-nums",
                                    }}
                                  >
                                    {smsFormatCountdown(smsSecondsLeft)}
                                  </strong>
                                  . You'll be notified when the image has been
                                  uploaded.
                                </p>
                                <button
                                  type="button"
                                  onClick={resetSmsForm}
                                  style={{
                                    alignSelf: "flex-start",
                                    background: "transparent",
                                    border: 0,
                                    padding: 0,
                                    fontFamily: "Inter",
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: "var(--app-ink-purple)",
                                    textDecoration: "underline",
                                    cursor: "pointer",
                                  }}
                                >
                                  Send another link
                                </button>
                              </div>
                            ) : (
                              <Group spacing={6} noWrap align="flex-end">
                                <TextInput
                                  type="tel"
                                  placeholder="(555) 555-0123"
                                  value={smsPhone}
                                  onChange={(e) => setSmsPhone(e.target.value)}
                                  size="sm"
                                  radius="md"
                                  sx={{ flex: 1 }}
                                />
                                <Button
                                  color="violet"
                                  size="sm"
                                  radius="md"
                                  loading={smsSending}
                                  disabled={!isValidSmsPhone(smsPhone)}
                                  onClick={handleSmsSend}
                                  sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
                                >
                                  Send SMS
                                </Button>
                              </Group>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* dropzone — hidden once files are staged for bulk review/save */}
                  {panelMode !== "bulk-review" &&
                    panelMode !== "bulk-save-preview" &&
                    panelMode !== "bulk-saved" && (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 8,
                          padding: "24px 16px",
                          border: `1px dashed ${isDropzoneDragging ? "var(--app-ink-purple)" : "var(--app-border)"}`,
                          borderRadius: 10,
                          background: isDropzoneDragging
                            ? "var(--app-tint-purple)"
                            : "var(--app-surface-2)",
                          cursor: "pointer",
                          textAlign: "center",
                          transition:
                            "background .12s ease, border-color .12s ease",
                        }}
                        onClick={() => bulkFileInputRef.current?.click()}
                        onMouseEnter={(e) => {
                          if (isDropzoneDragging) return;
                          e.currentTarget.style.background =
                            "var(--app-tint-purple)";
                          e.currentTarget.style.borderColor =
                            "var(--app-ink-purple)";
                        }}
                        onMouseLeave={(e) => {
                          if (isDropzoneDragging) return;
                          e.currentTarget.style.background =
                            "var(--app-surface-2)";
                          e.currentTarget.style.borderColor =
                            "var(--app-border)";
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsDropzoneDragging(true);
                        }}
                        onDragLeave={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsDropzoneDragging(false);
                        }}
                        onDrop={handleBulkFilesDrop}
                      >
                        {/* Single image scan — no capture so gallery/file-picker opens */}
                        <input
                          ref={scanInputRef}
                          type="file"
                          accept="image/*,.heic,.heif"
                          style={{ display: "none" }}
                          onChange={handleScanFileChange}
                        />
                        {/* iOS native camera — used instead of getUserMedia on iPhone/iPad */}
                        <input
                          ref={cameraInputRef}
                          type="file"
                          accept="image/*"
                          capture="environment"
                          style={{ display: "none" }}
                          onChange={handleScanFileChange}
                        />
                        {/* Bulk file upload — multiple files */}
                        <input
                          ref={bulkFileInputRef}
                          type="file"
                          accept="image/*,.heic,.heif,.pdf,.doc,.docx,.xls,.xlsx,.xlsm"
                          multiple
                          style={{ display: "none" }}
                          onChange={handleBulkFilesSelect}
                        />
                        <span
                          style={{
                            width: 44,
                            height: 44,
                            borderRadius: "50%",
                            background: "var(--app-surface)",
                            border: "1px solid var(--app-border)",
                            display: "grid",
                            placeItems: "center",
                            boxShadow: "0 1px 2px 0 rgba(16,24,40,0.05)",
                          }}
                        >
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="var(--app-ink-purple)"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                            <circle cx="12" cy="13" r="4" />
                          </svg>
                        </span>
                        <p
                          style={{
                            margin: 0,
                            fontSize: 13,
                            fontWeight: 600,
                            color: "var(--app-text)",
                            fontFamily: "Inter",
                          }}
                        >
                          {isDropzoneDragging
                            ? "Drop files to upload"
                            : "Take a photo, upload, or drag and drop files to scan medication labels"}
                        </p>
                        <p
                          style={{
                            margin: 0,
                            fontSize: 11,
                            color: "var(--app-text-muted)",
                            fontFamily: "Inter",
                          }}
                        >
                          Image, PDF, Word, or Excel · max 20MB per file
                        </p>
                        <div
                          style={{
                            display: "inline-flex",
                            gap: 8,
                            marginTop: 4,
                          }}
                        >
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              isIOS
                                ? cameraInputRef.current?.click()
                                : openCamera();
                            }}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 6,
                              height: 32,
                              padding: "0 12px",
                              borderRadius: 8,
                              border: "1px solid #6941C6",
                              background: "#6941C6",
                              color: "#FFFFFF",
                              fontSize: 12,
                              fontWeight: 600,
                              fontFamily: "Inter",
                              cursor: "pointer",
                              boxShadow: "0 1px 2px 0 rgba(16,24,40,0.05)",
                            }}
                          >
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                              <circle cx="12" cy="13" r="4" />
                            </svg>
                            Take photo
                          </button>
                          <button
                            type="button"
                            className={classes.uploadFilesBtn}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              bulkFileInputRef.current?.click();
                            }}
                          >
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="16 16 12 12 8 16" />
                              <line x1="12" y1="12" x2="12" y2="21" />
                              <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
                            </svg>
                            Upload files
                          </button>
                        </div>
                      </div>
                    )}

                </div>
                {/* Bulk staged files list */}
                {panelMode === "upload" &&
                  bulkFiles.length > 0 &&
                  (() => {
                    const doneCount = bulkFiles.filter(
                      (f) => f.status === "done" || f.status === "failed",
                    ).length;
                    return (
                      <div className={classes.bulkFileCard}>
                        {/* header */}
                        <div className={classes.bulkFileCardHeader}>
                          <p className={classes.bulkFileCardLabel}>
                            {bulkProcessing
                              ? bulkProcessingLabel
                              : pendingScanJob
                                ? "Still processing on our servers — click \"Check status\" below"
                                : "Uploaded files"}
                          </p>
                          <p className={classes.bulkFileCardCount}>
                            {bulkProcessing ? (
                              <>
                                {doneCount}
                                <span
                                  className={classes.bulkFileCardCountMuted}
                                >
                                  {" "}
                                  / {bulkFiles.length} done
                                </span>
                              </>
                            ) : (
                              <>
                                {bulkFiles.length}
                                <span
                                  className={classes.bulkFileCardCountMuted}
                                >
                                  {" "}
                                  file{bulkFiles.length === 1 ? "" : "s"}
                                </span>
                              </>
                            )}
                          </p>
                        </div>
                        {/* progress bar */}
                        {bulkProcessing && (
                          <div className={classes.bulkFileProgressTrack}>
                            <div
                              className={classes.bulkFileProgressFill}
                              style={{
                                width: `${bulkFakeProgress}%`,
                                transition: "width 0.3s ease",
                              }}
                            />
                          </div>
                        )}
                        {/* file rows */}
                        <div className={classes.bulkFileInnerList}>
                          {bulkFiles.map((f) => {
                            const isImage = f.file?.type?.startsWith("image/");
                            const iconBoxClass =
                              f.status === "failed"
                                ? classes.bulkFileIconBoxFailed
                                : f.status === "done"
                                  ? classes.bulkFileIconBoxDone
                                  : isImage
                                    ? classes.bulkFileIconBoxImage
                                    : classes.bulkFileIconBoxDoc;
                            return (
                              <div
                                key={f.id}
                                className={classes.bulkFileInnerRow}
                              >
                                <span
                                  className={cx(
                                    classes.bulkFileIconBox,
                                    iconBoxClass,
                                  )}
                                >
                                  <LuFileText size={16} />
                                </span>
                                <p className={classes.bulkFileName}>{f.name}</p>
                                <span className={classes.bulkFileSize}>
                                  {f.sizeLabel}
                                </span>
                               
                                {!bulkProcessing && (
                                  <button
                                    type="button"
                                    className={classes.bulkRemoveBtn}
                                    onClick={() => handleBulkRemoveFile(f.id)}
                                    aria-label="Remove file"
                                  >
                                    <LuX size={12} />
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
              </>
            )}

            {/* ── Bulk review table ── */}
            {panelMode === "bulk-review" &&
              (() => {
                const active = bulkReviewRows.filter((r) => !r.skipped);
                // saved rows count as ready; unsaved pending rows with no issues also count as ready
                const readyCount = active.filter(
                  (r) =>
                    r.saved ||
                    (r.status === "done" && !r.isDuplicate && !r.needsReview),
                ).length;
                const needsReviewCount = active.filter(
                  (r) => !r.saved && r.needsReview,
                ).length;
                const failedCount = active.filter(
                  (r) => !r.saved && r.status === "failed",
                ).length;
                const dupCount = active.filter(
                  (r) => !r.saved && r.isDuplicate,
                ).length;
                const outstanding = needsReviewCount + failedCount + dupCount;

                const filtered =
                  bulkReviewFilter === "ready"
                    ? active.filter(
                        (r) =>
                          r.saved ||
                          (r.status === "done" &&
                            !r.isDuplicate &&
                            !r.needsReview),
                      )
                    : bulkReviewFilter === "needs-review"
                      ? active.filter((r) => !r.saved && r.needsReview)
                      : bulkReviewFilter === "failed"
                        ? active.filter(
                            (r) => !r.saved && r.status === "failed",
                          )
                        : bulkReviewFilter === "duplicate"
                          ? active.filter((r) => !r.saved && r.isDuplicate)
                          : bulkReviewRows;

                const chips = [
                  { key: "all", label: "All", count: bulkReviewRows.length },
                  { key: "ready", label: "Ready", count: readyCount },
                  {
                    key: "needs-review",
                    label: "Needs review",
                    count: needsReviewCount,
                  },
                  { key: "failed", label: "OCR failed", count: failedCount },
                  { key: "duplicate", label: "Duplicates", count: dupCount },
                ];

                /* ── Combine confirmation screen ── */
                if (bulkCombineConfirm) {
                  return (
                    <div
                      style={{
                        background: "var(--app-surface)",
                        border: "1px solid var(--app-border)",
                        borderRadius: 12,
                        padding: 24,
                        display: "flex",
                        flexDirection: "column",
                        gap: 16,
                        alignItems: "center",
                        textAlign: "center",
                        fontFamily: "Inter",
                      }}
                    >
                      <span
                        style={{
                          width: 56,
                          height: 56,
                          borderRadius: 999,
                          background: "var(--app-tint-green)",
                          border: "1px solid var(--app-ink-green)",
                          display: "grid",
                          placeItems: "center",
                        }}
                      >
                        <LuCheck size={26} color="var(--app-ink-green)" />
                      </span>
                      {bulkCombineConfirm.fromClientList ? (
                        <>
                          <p
                            style={{
                              margin: 0,
                              fontSize: 18,
                              fontWeight: 700,
                              color: "var(--app-text)",
                            }}
                          >
                            Duplicate removed
                          </p>
                          <p
                            style={{
                              margin: 0,
                              fontSize: 13,
                              color: "var(--app-text-muted)",
                              lineHeight: 1.5,
                              maxWidth: 420,
                            }}
                          >
                            The original record for{" "}
                            <strong>
                              {bulkCombineConfirm.kept?.name}{" "}
                              {bulkCombineConfirm.kept?.strength}
                            </strong>{" "}
                            is already stored in the client's med list.
                            {bulkCombineConfirm.mergedCount > 0 && (
                              <>
                                {" "}
                                {bulkCombineConfirm.mergedCount} duplicate
                                {bulkCombineConfirm.mergedCount === 1
                                  ? ""
                                  : "s"}{" "}
                                from this batch{" "}
                                {bulkCombineConfirm.mergedCount === 1
                                  ? "was"
                                  : "were"}{" "}
                                removed.
                              </>
                            )}
                          </p>
                        </>
                      ) : (
                        <>
                          <p
                            style={{
                              margin: 0,
                              fontSize: 18,
                              fontWeight: 700,
                              color: "var(--app-text)",
                            }}
                          >
                            Combined into one entry
                          </p>
                          <p
                            style={{
                              margin: 0,
                              fontSize: 13,
                              color: "var(--app-text-muted)",
                              lineHeight: 1.5,
                              maxWidth: 420,
                            }}
                          >
                            Kept{" "}
                            <strong>
                              {bulkCombineConfirm.kept?.name}{" "}
                              {bulkCombineConfirm.kept?.strength}
                            </strong>{" "}
                            as the canonical record.
                            {bulkCombineConfirm.mergedCount > 0 && (
                              <>
                                {" "}
                                {bulkCombineConfirm.mergedCount} duplicate
                                {bulkCombineConfirm.mergedCount === 1
                                  ? ""
                                  : "s"}{" "}
                                {bulkCombineConfirm.mergedCount === 1
                                  ? "was"
                                  : "were"}{" "}
                                removed from the batch.
                              </>
                            )}
                          </p>
                          <div
                            style={{
                              width: "100%",
                              maxWidth: 420,
                              border: "1px solid var(--app-border)",
                              borderRadius: 10,
                              padding: 12,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 12,
                              background: "var(--app-surface-2)",
                            }}
                          >
                            <div style={{ minWidth: 0, textAlign: "left" }}>
                              <p
                                style={{
                                  margin: 0,
                                  fontSize: 14,
                                  fontWeight: 700,
                                  color: "var(--app-text)",
                                }}
                              >
                                {bulkCombineConfirm.kept?.name}
                              </p>
                              <p
                                style={{
                                  margin: "2px 0 0",
                                  fontSize: 12,
                                  color: "var(--app-text-muted)",
                                }}
                              >
                                {[
                                  bulkCombineConfirm.kept?.strength,
                                  bulkCombineConfirm.kept?.frequency,
                                  bulkCombineConfirm.kept?.route,
                                ]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </p>
                            </div>
                            <span
                              style={{
                                width: 32,
                                height: 32,
                                borderRadius: 999,
                                background: "var(--app-tint-green)",
                                border: "1px solid var(--app-ink-green)",
                                display: "grid",
                                placeItems: "center",
                                flexShrink: 0,
                              }}
                            >
                              <LuCheck size={16} color="var(--app-ink-green)" />
                            </span>
                          </div>
                        </>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          setBulkCombineConfirm(null);
                          const allSaved =
                            bulkReviewRows.length === 0 ||
                            bulkReviewRows.every((r) => r.saved);
                          if (allSaved) {
                            setBulkEditingRowId(null);
                            setPanelMode("bulk-saved");
                            return;
                          }
                          const nextReviewRow = bulkReviewRows.find(
                            (r) =>
                              bulkSelectedIds.has(r.id) &&
                              !r.saved &&
                              (r.isDuplicate ||
                                r.needsReview ||
                                r.status === "failed"),
                          );
                          if (nextReviewRow) {
                            setBulkDupeSelectedIds(new Set());
                            setBulkEditingRowId(nextReviewRow.id);
                          } else {
                            setBulkEditingRowId(null);
                          }
                        }}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 8,
                          height: 40,
                          padding: "0 22px",
                          borderRadius: 8,
                          border: "1px solid var(--app-primary)",
                          background: "var(--app-primary)",
                          color: "var(--app-on-primary)",
                          fontFamily: "Inter",
                          fontSize: 14,
                          fontWeight: 700,
                          cursor: "pointer",
                          boxShadow: "0 1px 2px rgba(16,24,40,0.05)",
                        }}
                      >
                        Back to review
                      </button>
                    </div>
                  );
                }

                /* ── Inline row review (BulkRowFlow) ── */
                if (bulkEditingRowId) {
                  const activeRow = bulkReviewRows.find(
                    (r) => r.id === bulkEditingRowId,
                  );
                  if (!activeRow) return null;
                  const ocr = activeRow.ocrResult || {};
                  const rowIdx = bulkReviewRows.findIndex(
                    (r) => r.id === bulkEditingRowId,
                  );
                  const isFailedRow = activeRow.status === "failed";
                  const backToList = () => setBulkEditingRowId(null);

                  const orderKey = getMedOrderKey(ocr);
                  // All pending batch rows that are the same medication order
                  // (name + schedule/route/rx) form the duplicate stack — rows
                  // that only share a name but are separate orders (e.g. a
                  // Scheduled and a PRN order for the same drug) must not be
                  // grouped together.
                  const matchingBatchRows =
                    activeRow.isDuplicate && orderKey
                      ? bulkReviewRows.filter(
                          (r) =>
                            r.status === "done" &&
                            !r.saved &&
                            !r.skipped &&
                            getMedOrderKey(r.ocrResult) === orderKey,
                        )
                      : [];
                  const isExistingActiveDupe =
                    activeRow.isDuplicate && !!activeRow.isExistingActiveDuplicate;
                  // A stack renders whenever there are 2+ batch duplicates, OR a single
                  // batch row that is already on the client's active medication list
                  // (in that case the stack pairs the locked "already active" card with
                  // this one selectable OCR row).
                  const dupeStack =
                    matchingBatchRows.length > 1
                      ? matchingBatchRows
                      : isExistingActiveDupe && matchingBatchRows.length === 1
                        ? matchingBatchRows
                        : null;
                  const isExistingActiveOnly =
                    isExistingActiveDupe && matchingBatchRows.length <= 1;
                  // Whether the stack actually has 2+ combinable batch duplicates
                  // (as opposed to just pairing one OCR row with the locked "already
                  // active" card, which has nothing to combine/select).
                  const isCombinableStack = matchingBatchRows.length > 1;
                  const allStackIds = dupeStack
                    ? dupeStack.map((r) => r.id)
                    : [];
                  const selectedCount = allStackIds.filter((id) =>
                    bulkDupeSelectedIds.has(id),
                  ).length;
                  const allSelected =
                    allStackIds.length > 0 &&
                    selectedCount === allStackIds.length;

                  const toggleDupeId = (id) => {
                    setBulkDupeSelectedIds((prev) => {
                      const n = new Set(prev);
                      n.has(id) ? n.delete(id) : n.add(id);
                      return n;
                    });
                  };
                  const toggleAllDupes = () => {
                    setBulkDupeSelectedIds(
                      allSelected ? new Set() : new Set(allStackIds),
                    );
                  };

                  const buildDetailLine = (row) => {
                    const o = row.ocrResult || {};
                    return [
                      o?.administrationCount?.value
                        ? `Take ${o.administrationCount.value}`
                        : null,
                      o?.dosage?.value
                        ? `${o.dosage.value}${o?.unit?.value ? " " + o.unit.value : ""}`
                        : null,
                      o?.doseForm?.value || null,
                      o?.frequency?.value || null,
                      o?.route?.value || null,
                      o?.scheduleType?.value || null,
                      o?.quantity?.value ? `Qty ${o.quantity.value}` : null,
                    ]
                      .filter(Boolean)
                      .join(" · ");
                  };

                  // Icon button group shared by all row cards
                  const iconBtns = (row, isLockedPrimary) =>
                    !isLockedPrimary && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                          flexShrink: 0,
                        }}
                      >
                        <button
                          type="button"
                          title="Edit"
                          onClick={() => handleBulkRowEdit(row)}
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: 8,
                            border: "1px solid var(--app-border)",
                            background: "var(--app-surface)",
                            display: "grid",
                            placeItems: "center",
                            padding: 0,
                            cursor: "pointer",
                          }}
                        >
                          <LuPencil size={14} color="var(--app-text-muted)" />
                        </button>
                        <button
                          type="button"
                          title="Accept"
                          onClick={() => handleBulkRowAccept(row)}
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: 8,
                            border: "1px solid var(--app-ink-green)",
                            background: "var(--app-tint-green)",
                            display: "grid",
                            placeItems: "center",
                            padding: 0,
                            cursor: "pointer",
                          }}
                        >
                          <LuCheck size={15} color="var(--app-ink-green)" />
                        </button>
                        <button
                          type="button"
                          title="Delete"
                          onClick={() => {
                            handleBulkRowSkip(row.id);
                          }}
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: 8,
                            border: "1px solid var(--app-ink-red)",
                            background: "var(--app-surface)",
                            display: "grid",
                            placeItems: "center",
                            padding: 0,
                            cursor: "pointer",
                          }}
                        >
                          <LuTrash2 size={14} color="var(--app-ink-red)" />
                        </button>
                      </div>
                    );

                  // Reason card config
                  const rc = isFailedRow
                    ? {
                        label: "OCR FAILED",
                        bg: "var(--app-tint-red)",
                        border: "var(--app-ink-red)",
                        color: "var(--app-ink-red)",
                        iconBg: "var(--app-tint-red)",
                        body: "Could not read this file. Skip it or go back and re-upload a clearer image.",
                      }
                    : isExistingActiveOnly
                      ? {
                          label: "ALREADY ON ACTIVE LIST",
                          bg: "var(--app-tint-amber)",
                          border: "var(--app-ink-amber)",
                          color: "var(--app-ink-amber)",
                          iconBg: "var(--app-tint-amber)",
                          body: (
                            <>
                              <strong>
                                {ocr?.medicationName?.value ||
                                  "This medication"}
                                {ocr?.dosage?.value
                                  ? ` ${ocr.dosage.value}${ocr?.unit?.value ? " " + ocr.unit.value : ""}`
                                  : ""}
                              </strong>{" "}
                              is already on this client's active medication
                              list. Review before adding it again.
                            </>
                          ),
                        }
                      : activeRow.isDuplicate
                        ? {
                            label: "POSSIBLE DUPLICATE",
                            bg: "var(--app-tint-amber)",
                            border: "var(--app-ink-amber)",
                            color: "var(--app-ink-amber)",
                            iconBg: "var(--app-tint-amber)",
                            body: (
                              <>
                                There are duplicate(s) of{" "}
                                <strong>
                                  {ocr?.medicationName?.value ||
                                    "this medication"}
                                  {ocr?.dosage?.value
                                    ? ` ${ocr.dosage.value}${ocr?.unit?.value ? " " + ocr.unit.value : ""}`
                                    : ""}
                                </strong>{" "}
                                in this batch{isExistingActiveDupe
                                  ? ", and it is already on this client's active medication list"
                                  : ""}. Resolve conflict to continue.
                              </>
                            ),
                          }
                        : activeRow.needsReview
                        ? {
                            label: "NEEDS REVIEW",
                            bg: "var(--app-tint-amber)",
                            border: "var(--app-ink-amber)",
                            color: "var(--app-ink-amber)",
                            iconBg: "var(--app-tint-amber)",
                            body: "Low OCR confidence on some fields. Review and correct before accepting.",
                          }
                        : null;

                  return (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 12,
                      }}
                    >
                      {/* Back nav */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <button
                          type="button"
                          onClick={backToList}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            background: "none",
                            border: "none",
                            padding: "4px 0",
                            fontFamily: "Inter",
                            fontSize: 12,
                            fontWeight: 600,
                            color: "var(--app-text-muted)",
                            cursor: "pointer",
                          }}
                        >
                          <LuChevronLeft size={14} color="var(--app-text-muted)" /> Back to
                          list
                        </button>
                        <span
                          style={{
                            fontSize: 12,
                            color: "var(--app-text-muted)",
                            fontFamily: "Inter",
                          }}
                        >
                          {rowIdx + 1} of {bulkReviewRows.length}
                        </span>
                      </div>

                      {/* Reason card */}
                      {rc && (
                        <div
                          style={{
                            background: rc.bg,
                            border: `1px solid ${rc.border}`,
                            borderRadius: 12,
                            padding: 16,
                            display: "flex",
                            flexDirection: "column",
                            gap: 14,
                          }}
                        >
                          {/* Header row: icon + label + body */}
                          <div
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 10,
                            }}
                          >
                            <span
                              style={{
                                width: 28,
                                height: 28,
                                borderRadius: "50%",
                                flexShrink: 0,
                                background: rc.iconBg,
                                display: "grid",
                                placeItems: "center",
                              }}
                            >
                              <LuAlertTriangle size={14} color={rc.color} />
                            </span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <p
                                style={{
                                  margin: 0,
                                  fontSize: 11,
                                  fontWeight: 700,
                                  letterSpacing: 0.5,
                                  color: rc.color,
                                  textTransform: "uppercase",
                                  fontFamily: "Inter",
                                }}
                              >
                                {rc.label}
                              </p>
                              <p
                                style={{
                                  margin: "4px 0 0",
                                  fontSize: 14,
                                  color: "var(--app-text)",
                                  lineHeight: 1.5,
                                  fontFamily: "Inter",
                                }}
                              >
                                {rc.body}
                              </p>
                            </div>
                          </div>

                          {/* Needs-review white med card (inside reason card) */}
                          {activeRow.needsReview &&
                            !activeRow.isDuplicate &&
                            !isFailedRow && (
                              <div className={classes.brInnerMedCard}>
                                <div className={classes.brInnerMedCardBody}>
                                  <div className={classes.brInnerMedCardHeader}>
                                    <p className={classes.brInnerMedCardName}>
                                      {ocr?.medicationName?.value || "—"}
                                    </p>
                                    <span
                                      className={cx(
                                        classes.brChip,
                                        classes.brChipNeedsReview,
                                      )}
                                    >
                                      Needs review
                                    </span>
                                    {ocr?.narcoticStatus?.value && (
                                      <span
                                        className={cx(
                                          classes.brChip,
                                          classes.brChipNarcotic,
                                        )}
                                      >
                                        Narcotic
                                      </span>
                                    )}
                                  </div>
                                  {buildDetailLine(activeRow) && (
                                    <p className={classes.brInnerMedCardDetail}>
                                      {buildDetailLine(activeRow)}
                                    </p>
                                  )}
                                  <p className={classes.brInnerMedCardSource}>
                                    Source: {activeRow.sourceFile}
                                  </p>
                                </div>
                                {iconBtns(activeRow, false)}
                              </div>
                            )}

                          {/* Duplicate stacked cards (inside reason card) — also covers the
                              case of a single OCR row that is already on the client's
                              active medication list (paired with the locked card below) */}
                          {activeRow.isDuplicate && dupeStack && (
                            <div className={classes.brStackWrap}>
                              {/* Already-active-on-client card, locked, sits above the
                                  selectable OCR-batch duplicates when this medicine is
                                  both a batch duplicate AND already on the client's
                                  active list. It is never selectable/combinable. */}
                              {isExistingActiveDupe && (
                                <div className={classes.brLockedCard}>
                                  <div className={classes.brInnerMedCardBody}>
                                    <div className={classes.brInnerMedCardHeader}>
                                      <p className={classes.brInnerMedCardName}>
                                        {ocr?.medicationName?.value || "—"}
                                      </p>
                                      <span
                                        className={cx(
                                          classes.brChip,
                                          classes.brChipActiveList,
                                        )}
                                      >
                                        Already in client list
                                      </span>
                                      {ocr?.narcoticStatus?.value && (
                                        <span
                                          className={cx(
                                            classes.brChip,
                                            classes.brChipNarcotic,
                                          )}
                                        >
                                          Narcotic
                                        </span>
                                      )}
                                    </div>
                                    {buildDetailLine(activeRow) && (
                                      <p className={classes.brInnerMedCardDetail}>
                                        {buildDetailLine(activeRow)}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Select all row — only relevant when there are 2+
                                  combinable batch duplicates to pick between */}
                              {isCombinableStack && (
                                <div className={classes.brSelectAllRow}>
                                  <label className={classes.brSelectAllLabel}>
                                    <input
                                      type="checkbox"
                                      checked={allSelected}
                                      onChange={toggleAllDupes}
                                      className={classes.brDupeCheckbox}
                                    />
                                    Select all
                                  </label>
                                  <span className={classes.brSelectAllCount}>
                                    {selectedCount} of {allStackIds.length}{" "}
                                    selected
                                  </span>
                                </div>
                              )}

                              {/* Stacked row cards */}
                              {dupeStack.map((row) => {
                                const isSelected = bulkDupeSelectedIds.has(
                                  row.id,
                                );
                                const chipLabel = "Duplicate";
                                const chipClass = classes.brChipDuplicate;
                                const o = row.ocrResult || {};
                                const detail = buildDetailLine(row);
                                return (
                                  <div
                                    key={row.id}
                                    className={cx(
                                      classes.brStackedRowCard,
                                      isSelected &&
                                        classes.brStackedRowCardSelected,
                                    )}
                                  >
                                    {isCombinableStack && (
                                      <div
                                        className={
                                          classes.brStackedRowCheckboxWrap
                                        }
                                      >
                                        <input
                                          type="checkbox"
                                          checked={isSelected}
                                          onChange={() => toggleDupeId(row.id)}
                                          className={classes.brDupeCheckbox}
                                          style={{ cursor: "pointer" }}
                                        />
                                      </div>
                                    )}
                                    <div className={classes.brInnerMedCardBody}>
                                      <div
                                        className={classes.brInnerMedCardHeader}
                                      >
                                        <p className={classes.brInnerMedCardName}>
                                          {o?.medicationName?.value || "—"}
                                        </p>
                                        <span
                                          className={cx(
                                            classes.brChip,
                                            chipClass,
                                          )}
                                        >
                                          {chipLabel}
                                        </span>
                                        {o?.narcoticStatus?.value && (
                                          <span
                                            className={cx(
                                              classes.brChip,
                                              classes.brChipNarcotic,
                                            )}
                                          >
                                            Narcotic
                                          </span>
                                        )}
                                      </div>
                                      {detail && (
                                        <p
                                          className={
                                            classes.brInnerMedCardDetail
                                          }
                                        >
                                          {detail}
                                        </p>
                                      )}
                                      <p className={classes.brInnerMedCardSource}>
                                        Source: {row.sourceFile}
                                      </p>
                                    </div>
                                    {iconBtns(row, false)}
                                  </div>
                                );
                              })}

                              {/* Combine button — only when there are 2+ batch
                                  duplicates to merge */}
                              {isCombinableStack && (
                              <div className={classes.brCombineRow}>
                                <button
                                  type="button"
                                  disabled={selectedCount < 2}
                                  onClick={() => {
                                    const selected = dupeStack.filter((r) =>
                                      bulkDupeSelectedIds.has(r.id),
                                    );
                                    if (selected.length < 2) return;
                                    const keptRow = selected[0];
                                    const removedIds = new Set(
                                      selected.slice(1).map((r) => r.id),
                                    );
                                    const kOcr = keptRow.ocrResult || {};
                                    // Set confirm screen first, then update rows (avoids premature bulk-saved transition)
                                    setBulkCombineConfirm({
                                      kept: {
                                        name: kOcr.medicationName?.value || "—",
                                        strength: kOcr.dosage?.value
                                          ? `${kOcr.dosage.value}${kOcr.unit?.value ? " " + kOcr.unit.value : ""}`
                                          : "",
                                        frequency: kOcr.frequency?.value || "",
                                        route: kOcr.route?.value || "",
                                      },
                                      fromClientList: false,
                                      mergedCount: selected.length - 1,
                                    });
                                    setBulkReviewRows((prev) =>
                                      prev
                                        .filter((r) => !removedIds.has(r.id))
                                        .map((r) =>
                                          r.id === keptRow.id
                                            ? { ...r, saved: true }
                                            : r,
                                        ),
                                    );
                                    setBulkSelectedIds((prev) => {
                                      const next = new Set(prev);
                                      removedIds.forEach((id) =>
                                        next.delete(id),
                                      );
                                      return next;
                                    });
                                  }}
                                  className={cx(
                                    classes.brCombineBtn,
                                    selectedCount >= 2
                                      ? classes.brCombineBtnActive
                                      : classes.brCombineBtnDisabled,
                                  )}
                                >
                                  <LuCheck
                                    size={13}
                                    color={
                                      selectedCount >= 2 ? "var(--app-ink-green)" : "var(--app-text-muted)"
                                    }
                                  />{" "}
                                  Combine
                                </button>
                              </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Failed: skip button */}
                      {isFailedRow && (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "flex-end",
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => {
                              handleBulkRowSkip(activeRow.id);
                            }}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 6,
                              height: 36,
                              padding: "0 16px",
                              borderRadius: 8,
                              border: "1px solid var(--app-ink-red)",
                              background: "var(--app-surface)",
                              color: "var(--app-ink-red)",
                              fontFamily: "Inter",
                              fontSize: 13,
                              fontWeight: 600,
                              cursor: "pointer",
                            }}
                          >
                            <LuTrash2 size={13} color="var(--app-ink-red)" /> Skip this
                            entry
                          </button>
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <>
                    {/* Summary banner */}
                    <div
                      className={cx(
                        classes.bulkReviewBanner,
                        outstanding > 0
                          ? classes.bulkReviewBannerAlert
                          : classes.bulkReviewBannerOk,
                      )}
                    >
                      <span
                        className={cx(
                          classes.bulkReviewBannerDot,
                          outstanding > 0
                            ? classes.bulkReviewBannerDotAlert
                            : classes.bulkReviewBannerDotOk,
                        )}
                      >
                        {outstanding > 0 ? (
                          <LuAlertTriangle size={13} color="var(--app-ink-amber)" />
                        ) : (
                          <LuCheck size={13} color="var(--app-ink-green)" />
                        )}
                      </span>
                      <p className={classes.bulkReviewBannerText}>
                        <strong className={classes.bulkReviewBannerStrong}>
                          {bulkReviewRows.length}
                        </strong>{" "}
                        medication{bulkReviewRows.length !== 1 ? "s" : ""}{" "}
                        extracted.{" "}
                        {outstanding > 0 ? (
                          <>
                            <strong className={classes.bulkReviewBannerStrong}>
                              {outstanding}
                            </strong>{" "}
                            conflict{outstanding !== 1 ? "s" : ""} found. Please
                            resolve to continue.
                          </>
                        ) : (
                          "All ready to save."
                        )}
                      </p>
                    </div>

                    {/* Filter chips */}
                    <div className={classes.bulkFilterChipsRow}>
                      {chips
                        .filter((c) => c.key === "all" || c.count > 0)
                        .map((c) => (
                          <button
                            key={c.key}
                            type="button"
                            className={cx(
                              classes.bulkFilterChip,
                              bulkReviewFilter === c.key
                                ? classes.bulkFilterChipActive
                                : "",
                            )}
                            onClick={() => setBulkReviewFilter(c.key)}
                          >
                            {c.label}
                            <span
                              className={cx(
                                classes.bulkFilterChipBadge,
                                bulkReviewFilter === c.key
                                  ? classes.bulkFilterChipBadgeActive
                                  : "",
                              )}
                            >
                              {c.count}
                            </span>
                          </button>
                        ))}
                    </div>

                    {/* Table */}
                    <div className={classes.bulkReviewTableWrap}>
                      <div className={classes.bulkReviewTableScroll}>
                        <table className={classes.bulkReviewTable}>
                          <thead className={classes.bulkReviewThead}>
                            <tr>
                              <th
                                className={classes.bulkReviewTh}
                                style={{ width: 36, paddingRight: 0 }}
                              >
                                {(() => {
                                  const selectableRows = filtered.filter(
                                    (r) => !r.skipped,
                                  );
                                  const allChecked =
                                    selectableRows.length > 0 &&
                                    selectableRows.every((r) =>
                                      bulkSelectedIds.has(r.id),
                                    );
                                  return (
                                    <input
                                      type="checkbox"
                                      checked={allChecked}
                                      onChange={() => {
                                        if (allChecked) {
                                          setBulkSelectedIds(new Set());
                                        } else {
                                          setBulkSelectedIds(
                                            new Set(
                                              selectableRows.map((r) => r.id),
                                            ),
                                          );
                                        }
                                      }}
                                      style={{
                                        accentColor: "var(--app-primary)",
                                        width: 14,
                                        height: 14,
                                        cursor: "pointer",
                                      }}
                                    />
                                  );
                                })()}
                              </th>
                              <th
                                className={classes.bulkReviewTh}
                                style={{ minWidth: 220 }}
                              >
                                Medication
                              </th>
                              <th className={classes.bulkReviewTh}>Dose</th>
                              <th className={classes.bulkReviewTh}>
                                Frequency
                              </th>
                              <th className={classes.bulkReviewTh}>Route</th>
                              <th className={classes.bulkReviewTh}>Qty</th>
                              <th className={classes.bulkReviewTh}>Source</th>
                              <th className={classes.bulkReviewTh}>Status</th>
                              <th
                                className={classes.bulkReviewTh}
                                style={{ width: 110 }}
                              />
                            </tr>
                          </thead>
                          <tbody>
                            {filtered.map((row) => {
                              const ocr = row.ocrResult;
                              const medName = ocr?.medicationName?.value || "—";
                              const dose = ocr?.dosage?.value
                                ? `${ocr.dosage.value}${ocr.unit?.value ? " " + ocr.unit.value : ""}`
                                : "—";
                              const freq = ocr?.frequency?.value || "—";
                              const route = ocr?.route?.value || "—";
                              const qty = ocr?.quantity?.value || "—";
                              const isNarcoticRow = !!ocr?.narcoticStatus?.value;
                              const isPrnRow =
                                (ocr?.scheduleType?.value || "").toLowerCase() ===
                                "prn";
                              const sourceExt = (row.sourceFile || "")
                                .split(".")
                                .pop()
                                .toLowerCase();
                              const isImageSource = [
                                "png",
                                "jpg",
                                "jpeg",
                                "heic",
                                "heif",
                                "webp",
                              ].includes(sourceExt);

                              const statusLabel = row.saved
                                ? "Ready"
                                : row.status === "failed"
                                  ? "OCR Failed"
                                  : row.isDuplicate
                                    ? "Duplicate"
                                    : row.needsReview
                                      ? "Needs Review"
                                      : "Ready";

                              const statusClass = row.saved
                                ? classes.bulkStatusReady
                                : row.status === "failed"
                                  ? classes.bulkStatusFailed
                                  : row.isDuplicate
                                    ? classes.bulkStatusDuplicate
                                    : row.needsReview
                                      ? classes.bulkStatusNeedsReview
                                      : classes.bulkStatusReady;

                              return (
                                <tr
                                  key={row.id}
                                  className={cx(
                                    classes.bulkReviewTr,
                                    row.saved ? classes.bulkReviewTrSaved : "",
                                    row.skipped && !row.saved
                                      ? classes.bulkReviewTrSkipped
                                      : "",
                                    row.isDuplicate && !row.saved
                                      ? classes.bulkReviewTrDuplicate
                                      : "",
                                  )}
                                >
                                  <td
                                    className={classes.bulkReviewTd}
                                    style={{ paddingRight: 0 }}
                                  >
                                    {!row.skipped && (
                                      <input
                                        type="checkbox"
                                        checked={bulkSelectedIds.has(row.id)}
                                        onChange={() => {
                                          setBulkSelectedIds((prev) => {
                                            const next = new Set(prev);
                                            if (next.has(row.id))
                                              next.delete(row.id);
                                            else next.add(row.id);
                                            return next;
                                          });
                                        }}
                                        style={{
                                          accentColor: "var(--app-primary)",
                                          width: 14,
                                          height: 14,
                                          cursor: "pointer",
                                        }}
                                      />
                                    )}
                                  </td>
                                  <td className={classes.bulkReviewTd}>
                                    <div
                                      className={
                                        classes.bulkReviewMedNameRow
                                      }
                                    >
                                      <p className={classes.bulkReviewMedName}>
                                        {medName}
                                      </p>
                                      {isNarcoticRow && (
                                        <span
                                          className={cx(
                                            classes.bulkReviewTinyBadge,
                                            classes.bulkReviewBadgeNarcotic,
                                          )}
                                        >
                                          NARC
                                        </span>
                                      )}
                                      {isPrnRow && (
                                        <span
                                          className={cx(
                                            classes.bulkReviewTinyBadge,
                                            classes.bulkReviewBadgePrn,
                                          )}
                                        >
                                          PRN
                                        </span>
                                      )}
                                    </div>
                                    {row.isDuplicate && !row.saved && (
                                      <p className={classes.bulkReviewMedSub}>
                                        Possible duplicate in MAR
                                      </p>
                                    )}
                                    {row.needsReview &&
                                      !row.isDuplicate &&
                                      !row.saved && (
                                        <p className={classes.bulkReviewMedSub}>
                                          Low OCR confidence — review fields
                                        </p>
                                      )}
                                  </td>
                                  <td className={classes.bulkReviewTd}>
                                    {dose}
                                  </td>
                                  <td className={classes.bulkReviewTd}>
                                    {freq}
                                  </td>
                                  <td className={classes.bulkReviewTd}>
                                    {route}
                                  </td>
                                  <td className={classes.bulkReviewTd}>
                                    {qty}
                                  </td>
                                  <td className={classes.bulkReviewTd}>
                                    <div
                                      className={classes.bulkReviewSourceBox}
                                    >
                                      <span
                                        className={classes.bulkReviewSourceIcon}
                                      >
                                        {isImageSource ? (
                                          <LuImage size={13} />
                                        ) : (
                                          <LuFileText size={13} />
                                        )}
                                      </span>
                                      <span
                                        className={classes.bulkReviewSourceName}
                                      >
                                        {row.sourceFile}
                                      </span>
                                    </div>
                                  </td>
                                  <td className={classes.bulkReviewTd}>
                                    <span
                                      className={cx(
                                        classes.bulkStatusChip,
                                        statusClass,
                                      )}
                                    >
                                      {statusLabel}
                                    </span>
                                  </td>
                                  <td className={classes.bulkReviewTd}>
                                    <div
                                      className={classes.bulkReviewActionsCell}
                                    >
                                      {row.saved ? (
                                        <button
                                          type="button"
                                          className={classes.bulkReviewBtnEdit}
                                          onClick={() => handleBulkRowEdit(row)}
                                        >
                                          <LuPencil size={11} />
                                          Edit
                                        </button>
                                      ) : (
                                        row.status === "done" && (
                                          <button
                                            type="button"
                                            className={
                                              row.isDuplicate || row.needsReview
                                                ? classes.bulkReviewBtnReview
                                                : classes.bulkReviewBtnEdit
                                            }
                                            onClick={() =>
                                              row.isDuplicate || row.needsReview
                                                ? handleBulkRowReview(row)
                                                : handleBulkRowEdit(row)
                                            }
                                          >
                                            {row.isDuplicate || row.needsReview ? (
                                              <>
                                                <LuAlertTriangle size={11} />
                                                Review
                                              </>
                                            ) : (
                                              <>
                                                <LuPencil size={11} />
                                                Edit
                                              </>
                                            )}
                                          </button>
                                        )
                                      )}
                                      <button
                                        type="button"
                                        className={classes.bulkReviewBtnSkip}
                                        title="Delete"
                                        onClick={() =>
                                          handleBulkRowSkip(row.id)
                                        }
                                      >
                                        <LuTrash2 size={12} color="var(--app-ink-red)" />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Count line */}
                    <p className={classes.bulkReviewCountLine}>
                      <strong>{readyCount}</strong> of{" "}
                      <strong>{bulkReviewRows.length}</strong> medications ready
                      to save
                    </p>
                  </>
                );
              })()}

            {/* ── Bulk save preview screen ── */}
            {panelMode === "bulk-save-preview" && (
              <div className={classes.bulkConfirmWrap}>
                <div>
                  <p className={classes.bulkConfirmTitle}>Confirm save</p>
                  <p className={classes.bulkConfirmSub}>
                    {bulkSavePreviewRows.length} medication{bulkSavePreviewRows.length === 1 ? "" : "s"} will be added to {selectedClient?.name || client?.name}'s list.
                  </p>
                </div>
                <div className={classes.bulkConfirmList}>
                  {bulkSavePreviewRows.map((row) => {
                    const ocr = row.ocrResult || {};
                    const overrides = row.overrides || {};
                    const schedRaw = (ocr?.scheduleType?.value || "").toLowerCase();
                    const medType = schedRaw === "prn" ? "PRN" : (ocr?.medicationName?.value ? "Scheduled" : "");
                    const adminCount = overrides.medicationadministered ?? ocr?.administrationCount?.value;
                    const parts = [
                      adminCount != null ? `Take ${adminCount}` : null,
                      ocr?.dosage?.value,
                      ocr?.unit?.value,
                      ocr?.doseForm?.value,
                      ocr?.frequency?.value,
                      ocr?.route?.value,
                      medType || null,
                      ocr?.quantity?.value != null ? `Qty ${ocr.quantity.value}` : null,
                    ].filter(Boolean).join(" · ");
                    return (
                      <div key={row.id} className={classes.bulkConfirmRow}>
                        <span className={classes.bulkConfirmCheckCircle}>
                          <LuCheck size={14} color="var(--app-ink-green)" />
                        </span>
                        <div className={classes.bulkConfirmMedInfo}>
                          <p className={classes.bulkConfirmMedName}>
                            {ocr?.medicationName?.value || "—"}
                          </p>
                          <p className={classes.bulkConfirmMedDetails}>
                            {parts || "—"}
                          </p>
                          {row.sourceFile && (
                            <p className={classes.bulkConfirmSourceText}>
                              Source: {row.sourceFile}
                            </p>
                          )}
                        </div>
                        {ocr?.narcoticStatus?.value && (
                          <span className={classes.bulkConfirmNarcBadge}>NARC</span>
                        )}
                        <button
                          type="button"
                          aria-label={`Edit ${ocr?.medicationName?.value || ""}`}
                          title="Edit"
                          className={classes.bulkConfirmEditBtn}
                          onClick={() => handleBulkRowEdit(row, "bulk-save-preview")}
                        >
                          <LuPencil size={14} color="var(--app-text-muted)" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── Bulk saved screen ── */}
            {panelMode === "bulk-saved" && (() => {
              const savedCount = bulkReviewRows.filter((r) => r.saved).length;
              return (
                <div className={classes.bulkSavedWrap}>
                  <div className={classes.bulkSavedIcon}>
                    <LuCheck size={32} color="var(--app-ink-green)" />
                  </div>
                  <p className={classes.bulkSavedTitle}>
                    {savedCount} medication{savedCount === 1 ? "" : "s"} saved
                  </p>
                  <p className={classes.bulkSavedSub}>
                    Added to {selectedClient?.name || client?.name}'s medication list and linked to the MAR. Each medication keeps a reference to its source artifact for audit.
                  </p>
                  <div style={{ marginTop: 22, display: "flex", justifyContent: "center", gap: 10 }}>
                    <button
                      type="button"
                      onClick={reset}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 8,
                        padding: "10px 16px",
                        height: 40,
                        borderRadius: 8,
                        background: "var(--app-primary)",
                        border: "1px solid var(--app-primary)",
                        color: "var(--app-on-primary)",
                        fontSize: 14,
                        fontWeight: 700,
                        cursor: "pointer",
                        boxShadow: "0 1px 2px 0 rgba(16,24,40,0.05)",
                        fontFamily: "Inter",
                      }}
                    >
                      Go to medication list
                      <LuArrowRight size={14} color="var(--app-on-primary)" />
                    </button>
                  </div>
                </div>
              );
            })()}

            {/* ── Bulk edit: source file card above form ── */}
            {panelMode === null && bulkEditingRowId && (() => {
              const editRow = bulkReviewRows.find((r) => r.id === bulkEditingRowId);
              if (!editRow) return null;
              const editOcr = editRow.ocrResult || {};
              const confVals = Object.values(editOcr)
                .filter((f) => f && typeof f === "object" && typeof f.confidence === "number")
                .map((f) => f.confidence);
              const avgConf = confVals.length > 0
                ? Math.round(confVals.reduce((a, b) => a + b, 0) / confVals.length)
                : null;
              const isEditFailed = editRow.status === "failed";
              const editStatusClass = isEditFailed
                ? classes.bulkRowStatusFailed
                : editRow.isDuplicate
                  ? classes.bulkRowStatusDuplicate
                  : editRow.needsReview
                    ? classes.bulkRowStatusNeedsReview
                    : classes.bulkRowStatusReady;
              const editStatusLabel = isEditFailed
                ? "Failed"
                : editRow.isDuplicate
                  ? "Duplicate"
                  : editRow.needsReview
                    ? "Needs Review"
                    : "Ready";
              return (
                <div className={classes.bulkRowSourceCard}>
                  <div className={classes.bulkRowSourceLeft}>
                    <div className={classes.bulkRowSourceThumb}>
                      <span className={classes.bulkRowSourceThumbLine0} />
                      <span className={classes.bulkRowSourceThumbLine} style={{ width: "100%" }} />
                      <span className={classes.bulkRowSourceThumbLine} style={{ width: "92%" }} />
                      <span className={classes.bulkRowSourceThumbLine} style={{ width: "98%" }} />
                      <span className={classes.bulkRowSourceThumbLine} style={{ width: "76%" }} />
                      <span className={classes.bulkRowSourceThumbLine} style={{ width: "88%" }} />
                      <span className={classes.bulkRowSourceThumbLine} style={{ width: "66%" }} />
                    </div>
                    <div className={classes.bulkRowSourceInfo}>
                      <p className={classes.bulkRowSourceFileName} title={editRow.sourceFile}>
                        {editRow.sourceFile || "—"}
                      </p>
                      <div className={classes.bulkRowSourceMeta}>
                        {editRow.sizeLabel && (
                          <>
                            <span>{editRow.sizeLabel}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                    <div className={classes.bulkRowStatItem}>
                      <p className={classes.bulkRowStatLabel}>Status</p>
                      <span className={editStatusClass}>{editStatusLabel}</span>
                    </div>
                    {avgConf != null && (
                      <div className={classes.bulkRowStatItem}>
                        <p className={classes.bulkRowStatLabel}>Confidence</p>
                        <p className={classes.bulkRowStatValue}>{avgConf}%</p>
                      </div>
                    )}

                </div>
              );
            })()}

            {/* ── Normal form sections (hidden in bulk modes) ── */}
            {panelMode !== "upload" &&
              panelMode !== "bulk-review" &&
              panelMode !== "bulk-save-preview" &&
              panelMode !== "bulk-saved" && (
                <>
                  {/* Already-in-client-list card — single-image scan only
                      (no wizard steps, straight to this form). Locked, no
                      actions, mirrors the bulk-review duplicate-stack card. */}
                  {!bulkEditingRowId &&
                    !data &&
                    singleScanExistingActive &&
                    (() => {
                      const v = form.values || {};
                      const singleScanDetailLine = [
                        v.medicationadministered
                          ? `Take ${v.medicationadministered}`
                          : null,
                        v.dosage
                          ? `${v.dosage}${v.unit ? " " + v.unit : ""}`
                          : null,
                        v.dosageForm || null,
                        v.frequency || null,
                        v.ingestionMode || null,
                        v.medicationType || null,
                        v.totalMedicine ? `Qty ${v.totalMedicine}` : null,
                      ]
                        .filter(Boolean)
                        .join(" · ");
                      return (
                        <div className={classes.singleScanActiveCard}>
                          <div className={classes.singleScanActiveCardHeader}>
                            <span className={classes.singleScanActiveCardIcon}>
                              <LuAlertTriangle
                                size={14}
                                color="var(--app-ink-amber)"
                              />
                            </span>
                            <div className={classes.singleScanActiveCardText}>
                              <p className={classes.singleScanActiveCardLabel}>
                                ALREADY ON ACTIVE LIST
                              </p>
                              <p className={classes.singleScanActiveCardBody}>
                                <strong>
                                  {v.medicationName || "This medication"}
                                </strong>{" "}
                                is already on this client's active medication
                                list. Review before adding it again.
                              </p>
                            </div>
                          </div>
                          <div className={classes.brInnerMedCard}>
                            <div className={classes.brInnerMedCardBody}>
                              <div className={classes.brInnerMedCardHeader}>
                                <p className={classes.brInnerMedCardName}>
                                  {v.medicationName || "—"}
                                </p>
                                <span
                                  className={cx(
                                    classes.brChip,
                                    classes.brChipActiveList,
                                  )}
                                >
                                  Already in client list
                                </span>
                              </div>
                              {singleScanDetailLine && (
                                <p
                                  className={classes.brInnerMedCardDetail}
                                >
                                  {singleScanDetailLine}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                  {/* ── Section: Basics ── */}
                  <div style={scanStyles.sectionCard}>
                    <p style={scanStyles.sectionTitle}>Basics</p>
                    <div className={classes.topFieldsBox}>
                      {/* Is this a prescription medication? */}
                      <div className="isPrescribed">
                        <Radio.Group
                          name="isPrescribed"
                          label={
                            <span>
                              Is this a prescription medication?
                              {scannedBadge("isPrescribed")}
                            </span>
                          }
                          {...form.getInputProps("isPrescribed")}
                          required
                          withAsterisk
                          className={classes.radioGroupWrapper}
                        >
                          <Group className={classes.radioGroup}>
                            <Radio
                              disabled={isPreview}
                              value="Yes"
                              label="Yes"
                            />
                            <Radio disabled={isPreview} value="No" label="No" />
                          </Group>
                        </Radio.Group>
                      </div>
                      <div className="isNarcotic">
                        <Radio.Group
                          name="isNarcotic"
                          label={
                            <span>
                              Is this a narcotic?{scannedBadge("isNarcotic")}
                            </span>
                          }
                          {...form.getInputProps("isNarcotic")}
                          required
                          withAsterisk
                          disabled={isPreview}
                          className={classes.radioGroupWrapper}
                        >
                          <Group className={classes.radioGroup}>
                            <Radio
                              disabled={isPreview}
                              value="Yes"
                              label="Yes"
                            />
                            <Radio disabled={isPreview} value="No" label="No" />
                          </Group>
                        </Radio.Group>
                        {/* <NarcoticBox /> */}
                      </div>
                    </div>

                    {/* medication name — RxTerms autocomplete, full width */}
                    <div ref={rxDropdownRef} style={{ position: "relative" }}>
                      <CustomTextInput
                        id="medication-name"
                        label={
                          <span>
                            Medication Name{scannedBadge("medicationName")}
                          </span>
                        }
                        placeholder={"Enter Medication Name"}
                        value={form.values.medicationName}
                        onChange={(e) => handleRxInputChange(e.target.value)}
                        error={form.errors.medicationName}
                        withAsterisk
                        disabled={isPreview}
                        autoComplete="off"
                        rightSection={
                          rxLoading ? (
                            <Loader color="yellow" size="xs" />
                          ) : form.values.medicationName && !isPreview ? (
                            <IoIosCloseCircle
                              style={{
                                cursor: "pointer",
                                color: "var(--app-text-muted)",
                                fontSize: 18,
                              }}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                form.setFieldValue("medicationName", "");
                                form.setFieldValue("strengthAndForm", "");
                                setRxStrengthOptions([]);
                                setRxShowDropdown(false);
                                setRxSuggestions([]);
                              }}
                            />
                          ) : null
                        }
                      />
                      {rxShowDropdown && !isPreview && (
                        <div
                          style={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            right: 0,
                            background: "var(--app-surface)",
                            border: "1px solid var(--app-border)",
                            borderRadius: 8,
                            boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                            zIndex: 9999,
                            maxHeight: 260,
                            overflowY: "auto",
                          }}
                        >
                          {rxSuggestions.map((drug, idx) => (
                            <div
                              key={idx}
                              onMouseDown={() => handleRxSelect(drug)}
                              style={{
                                padding: "10px 14px",
                                cursor: "pointer",
                                borderBottom:
                                  idx < rxSuggestions.length - 1
                                    ? "1px solid var(--app-border)"
                                    : "none",
                              }}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.background =
                                  "var(--app-surface-2)")
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.background =
                                  "transparent")
                              }
                            >
                              <Text size="sm" weight={500}>
                                {drug.displayName}
                              </Text>
                              {drug.strengthsAndForms.length > 0 && (
                                <Text size="xs" color="dimmed">
                                  {drug.strengthsAndForms
                                    .slice(0, 3)
                                    .join(" · ")}
                                  {drug.strengthsAndForms.length > 3
                                    ? ` +${drug.strengthsAndForms.length - 3} more`
                                    : ""}
                                </Text>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Dosage information box */}
                    <div
                      style={{
                        padding: 12,
                        borderRadius: 10,
                        background: "var(--app-surface-2)",
                        border: "1px solid var(--app-border)",
                        display: "flex",
                        flexDirection: "column",
                        gap: 10,
                      }}
                    >
                      <p
                        style={{
                          margin: 0,
                          fontSize: 13,
                          fontWeight: 600,
                          color: "var(--app-text)",
                          fontFamily: "Inter",
                        }}
                      >
                        Dosage information{" "}
                        <span style={{ color: "var(--app-ink-red)", fontWeight: 600 }}>
                          *
                        </span>
                      </p>

                      {rxStrengthOptions.length === 0 ? (
                        <div style={{ display: "flex", gap: 12 }}>
                          {/* Amount stepper */}
                          <div style={{ flex: 1 }}>
                            <p
                              style={{
                                margin: "0 0 6px",
                                fontSize: 13,
                                fontWeight: 500,
                                color: "var(--app-text)",
                                fontFamily: "Inter",
                              }}
                            >
                              Amount{scannedBadge("dosage")}
                            </p>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                border: `1px solid ${form.errors.dosage ? "var(--app-ink-red)" : "var(--app-border)"}`,
                                borderRadius: 8,
                                background: "var(--app-surface)",
                                paddingInline: 13,
                                boxShadow: "0 1px 2px 0 rgba(16,24,40,0.05)",
                                height: 44,
                                opacity: isPreview ? 0.6 : 1,
                              }}
                            >
                              <button
                                type="button"
                                disabled={isPreview}
                                onClick={() => {
                                  const v = parseFloat(form.values.dosage) || 0;
                                  form.setFieldValue(
                                    "dosage",
                                    String(Math.max(0, +(v - 1).toFixed(2))),
                                  );
                                }}
                                style={{
                                  border: 0,
                                  background: "transparent",
                                  cursor: isPreview ? "not-allowed" : "pointer",
                                  padding: "0 4px",
                                  fontSize: 18,
                                  color: "var(--app-text)",
                                  lineHeight: 1,
                                }}
                                aria-label="Decrease amount"
                              >
                                −
                              </button>
                              <input
                                type="number"
                                min={0}
                                step="any"
                                inputMode="decimal"
                                value={form.values.dosage}
                                onChange={(e) =>
                                  form.setFieldValue("dosage", e.target.value)
                                }
                                disabled={isPreview}
                                placeholder="0"
                                className={classes.noSpinner}
                                style={{
                                  flex: 1,
                                  height: "100%",
                                  border: 0,
                                  outline: 0,
                                  textAlign: "center",
                                  fontSize: 15,
                                  color: "var(--app-text)",
                                  background: "transparent",
                                  fontFamily: "Inter",
                                }}
                              />
                              <button
                                type="button"
                                disabled={isPreview}
                                onClick={() => {
                                  const v = parseFloat(form.values.dosage) || 0;
                                  form.setFieldValue(
                                    "dosage",
                                    String(+(v + 1).toFixed(2)),
                                  );
                                }}
                                style={{
                                  border: 0,
                                  background: "transparent",
                                  cursor: isPreview ? "not-allowed" : "pointer",
                                  padding: "0 4px",
                                  fontSize: 16,
                                  color: "var(--app-text)",
                                  lineHeight: 1,
                                }}
                                aria-label="Increase amount"
                              >
                                +
                              </button>
                            </div>
                            {form.errors.dosage && (
                              <p
                                style={{
                                  margin: "4px 0 0",
                                  fontSize: 12,
                                  color: "var(--app-ink-red)",
                                  fontFamily: "Inter",
                                }}
                              >
                                {form.errors.dosage}
                              </p>
                            )}
                          </div>
                          {/* Unit */}
                          <div style={{ flex: 1 }}>
                            <p
                              style={{
                                margin: "0 0 6px",
                                fontSize: 13,
                                fontWeight: 500,
                                color: "var(--app-text)",
                                fontFamily: "Inter",
                              }}
                            >
                              Unit{scannedBadge("unit")}
                            </p>
                            <Select
                              id="medication-unit"
                              data={
                                form.values.unit &&
                                !UNIT_OPTIONS.includes(form.values.unit)
                                  ? [...UNIT_OPTIONS, form.values.unit]
                                  : UNIT_OPTIONS
                              }
                              searchable
                              styles={{
                                input: {
                                  height: 44,
                                  borderColor: "var(--app-border)",
                                  borderRadius: 8,
                                  fontSize: 14,
                                  fontFamily: "Inter",
                                  boxShadow: "0 1px 2px 0 rgba(16,24,40,0.05)",
                                },
                              }}
                              placeholder="Select unit…"
                              {...form.getInputProps("unit")}
                              rightSection={<IconChevronDown size={14} />}
                              disabled={isPreview}
                            />
                          </div>
                        </div>
                      ) : (
                        <div>
                          <p
                            style={{
                              margin: "0 0 6px",
                              fontSize: 13,
                              fontWeight: 500,
                              color: "var(--app-text)",
                              fontFamily: "Inter",
                            }}
                          >
                            Strength &amp; Form
                          </p>
                          <Select
                            id="strength-and-form"
                            data={rxStrengthOptions}
                            styles={{
                              input: {
                                height: 44,
                                borderColor: "var(--app-border)",
                                borderRadius: 8,
                                fontSize: 14,
                                fontFamily: "Inter",
                                boxShadow: "0 1px 2px 0 rgba(16,24,40,0.05)",
                              },
                            }}
                            placeholder="Select strength & form…"
                            {...form.getInputProps("strengthAndForm")}
                            rightSection={<IconChevronDown size={14} />}
                            disabled={isPreview}
                            clearable
                          />
                        </div>
                      )}

                      {/* Frequency — full width row below */}
                      <div>
                        <p
                          style={{
                            margin: "0 0 6px",
                            fontSize: 13,
                            fontWeight: 500,
                            color: "var(--app-text)",
                            fontFamily: "Inter",
                          }}
                        >
                          Frequency{scannedBadge("frequency")}{" "}
                          <span style={{ color: "var(--app-ink-red)", fontWeight: 600 }}>
                            *
                          </span>
                        </p>
                        <Select
                          id="frequency"
                          styles={{
                            input: {
                              height: 44,
                              borderColor: "var(--app-border)",
                              borderRadius: 8,
                              fontSize: 14,
                              fontFamily: "Inter",
                              boxShadow: "0 1px 2px 0 rgba(16,24,40,0.05)",
                            },
                          }}
                          data={
                            form.values.frequency &&
                            !frequencyData.includes(form.values.frequency)
                              ? [...new Set([...frequencyData, form.values.frequency])]
                              : [...new Set(frequencyData)]
                          }
                          searchable
                          placeholder="Select frequency…"
                          {...form.getInputProps("frequency")}
                          rightSection={<IconChevronDown size={14} />}
                          disabled={isPreview}
                        />
                      </div>
                    </div>
                    {/* medication route */}
                    <div style={{ display: "flex", gap: 12, width: "100%" }}>
                      <CustomSelectBox
                        id="medication-route"
                        label={
                          <span>
                            Medication Route{scannedBadge("ingestionMode")}
                          </span>
                        }
                        placeholder={"Select medication route"}
                        data={
                          form.values.ingestionMode &&
                          !ROUTE_OPTIONS.includes(form.values.ingestionMode)
                            ? [...ROUTE_OPTIONS, form.values.ingestionMode]
                            : ROUTE_OPTIONS
                        }
                        searchable
                        {...form.getInputProps("ingestionMode")}
                        withAsterisk
                        disabled={isPreview}
                      />
                      <CustomSelectBox
                        id="dose-form"
                        label={
                          <span>Dose Form{scannedBadge("dosageForm")}</span>
                        }
                        placeholder={"Select dose form"}
                        data={[
                          "Tablet",
                          "Capsule",
                          "Syrup",
                          "Pill",
                          "Liquid",
                          "Cream",
                          "Oil",
                          "Ointment",
                          "Lotion",
                          "Gel",
                          "Gas",
                          "Solution",
                          "Inhalant",
                          "Injection",
                          "Insert",
                          "Suppository",
                          "Jelly",
                          "Lozenge",
                          "Paste",
                          "Patch",
                          "Rinse",
                          "Shampoo",
                          "Drops",
                          "Spray",
                          "Powder",
                        ]}
                        {...form.getInputProps("dosageForm")}
                        disabled={isPreview}
                      />
                    </div>

                    <div style={{ display: "flex", gap: 12, width: "100%" }}>
                      <CustomTextInput
                        id="rx-number"
                        label={<span>Rx Number{scannedBadge("rxNumber")}</span>}
                        placeholder="Enter Rx number"
                        {...form.getInputProps("rxNumber")}
                        disabled={isPreview}
                      />
                      <DateInput
                        id="expriration-date"
                        minDate={new Date()}
                        placeholder={"Pick a date"}
                        label="Expiration Date"
                        disabled={isPreview}
                        {...form.getInputProps("expirationDate")}
                        onChange={onChangeMedEndDate}
                        clearable
                        className={classes.inputStyle}
                        icon={<FiCalendar />}
                      />
                    </div>
                  </div>

                  {/* ── Section: Schedule ── */}
                  <div style={scanStyles.sectionCard}>
                    <p style={scanStyles.sectionTitle}>Schedule</p>
                    <div style={{ display: "flex", gap: 12, width: "100%" }}>
                      <CustomSelectBox
                        id="medication-type"
                        label={
                          <span>
                            Medication Type{scannedBadge("medicationType")}
                          </span>
                        }
                        {...form.getInputProps("medicationType")}
                        onChange={(value) => {
                          form.setFieldValue("medicationType", value);
                          setType(value);
                        }}
                        placeholder={"Select Medication Type"}
                        withAsterisk
                        data={[
                          { value: "PRN", label: "PRN" },
                          { value: "scheduled", label: "SCHEDULED" },
                        ]}
                        disabled={isPreview}
                      />
                      {type !== "PRN" && (
                        <Select
                          data={[
                            { value: "daily", label: "Daily" },
                            { value: "custom", label: "Custom" },
                          ]}
                          label="Schedule Type"
                          placeholder="Select Schedule Type"
                          rightSection={<IconChevronDown />}
                          disabled={isPreview}
                          {...form.getInputProps("scheduleType")}
                          onChange={(value) => {
                            form.setFieldValue("scheduleType", value);
                            if (value === "daily") {
                              form.setFieldValue("repeatEvery", 1);
                              form.setFieldValue("repeatType", "day");
                            }
                          }}
                          className={classes.inputStyle}
                        />
                      )}
                      {type === "PRN" && (
                        <DateInput
                          id="medication-start-date"
                          placeholder={"Pick a date"}
                          label="Medication Start Date"
                          {...form.getInputProps("medicationDate")}
                          onChange={onChangeMedStartDate}
                          withAsterisk
                          disabled={isPreview}
                          className={classes.inputStyle}
                          icon={<FiCalendar />}
                        />
                      )}
                    </div>
                    {type !== "PRN" && (
                      <ScheduleMenu
                        form={form}
                        isPreview={isPreview}
                        data={data}
                        scannedBadge={scannedBadge}
                        showDetails={true}
                      />
                    )}
                  </div>

                  {/* ── Section: Inventory ── */}
                  <div style={scanStyles.sectionCard}>
                    <p style={scanStyles.sectionTitle}>Inventory</p>
                    <Box mb={10}>
                      <Radio.Group
                        label="Use Medication Count?"
                        onChange={handleRadioChange}
                        value={isMedicationCountEnabled}
                        disabled={isPreview}
                        className={classes.radioGroupWrapper}
                      >
                        <Group className={classes.radioGroup}>
                          <Radio disabled={isPreview} value="yes" label="Yes" />
                          <Radio disabled={isPreview} value="no" label="No" />
                        </Group>
                      </Radio.Group>
                    </Box>

                    {/* Total quantity + Remaining side by side */}
                    <div style={{ display: "flex", gap: 12, width: "100%" }}>
                      <CountField
                        id="medication-quantity"
                        label="Total Quantity"
                        withAsterisk={
                          isMedicationCountEnabled?.toLowerCase() !== "no"
                        }
                        value={form.values.totalMedicine}
                        error={form.errors.totalMedicine}
                        disabled={
                          isPreview ||
                          isMedicationCountEnabled?.toLowerCase() === "no"
                            ? true
                            : false
                        }
                        onChange={(e) => {
                          let value = e.target.value;
                          if (value.includes(".")) {
                            const [integerPart, decimalPart] = value.split(".");
                            if (decimalPart?.length > 1) {
                              value = `${integerPart}.${decimalPart?.slice(0, 1)}`;
                            }
                          }
                          form.setFieldValue("totalMedicine", value);
                        }}
                        onDecrement={() => {
                          const v = parseFloat(form.values.totalMedicine) || 0;
                          form.setFieldValue(
                            "totalMedicine",
                            String(Math.max(0, v - 1)),
                          );
                        }}
                        onIncrement={() => {
                          const v = parseFloat(form.values.totalMedicine) || 0;
                          form.setFieldValue("totalMedicine", String(v + 1));
                        }}
                      />
                      <CountField
                        id="current-medication-count"
                        labelNode={
                          <Tooltip
                            label="Update Current Count from Refill Medication"
                            placement="top"
                            offset={15}
                            withArrow
                            arrowSize={8}
                            disabled={
                              isPreview ||
                              !data ||
                              (data?.medicineRemaining === "unlimited" &&
                                data?.totalMedicine === "unlimited")
                            }
                          >
                            <span>Remaining</span>
                          </Tooltip>
                        }
                        value={form.values.medicineRemaining}
                        error={form.errors.medicineRemaining}
                        disabled={
                          isPreview ||
                          isMedicationCountEnabled?.toLowerCase() === "no" ||
                          (isMedicationCountEnabled?.toLowerCase() === "yes" &&
                          data?.medicineRemaining === undefined
                            ? data?.medicineRemaining == "unlimited"
                            : data?.medicineRemaining !== "unlimited")
                            ? true
                            : false
                        }
                        onChange={(e) => {
                          let value = e.target.value;
                          if (value.includes(".")) {
                            const [integerPart, decimalPart] = value.split(".");
                            if (decimalPart?.length > 1) {
                              value = `${integerPart}.${decimalPart?.slice(0, 1)}`;
                            }
                          }
                          form.setFieldValue("medicineRemaining", value);
                        }}
                        onDecrement={() => {
                          const v =
                            parseFloat(form.values.medicineRemaining) || 0;
                          form.setFieldValue(
                            "medicineRemaining",
                            String(Math.max(0, v - 1)),
                          );
                        }}
                        onIncrement={() => {
                          const v =
                            parseFloat(form.values.medicineRemaining) || 0;
                          form.setFieldValue(
                            "medicineRemaining",
                            String(v + 1),
                          );
                        }}
                      />
                    </div>

                    {/* Administration count on its own row */}
                    <div style={{ display: "flex", gap: 12, width: "100%" }}>
                      <CountField
                        id="administration-count"
                        label={
                          <span>
                            Administration Count
                            {scannedBadge("medicationadministered")}
                          </span>
                        }
                        value={form.values.medicationadministered}
                        error={form.errors.medicationadministered}
                        disabled={isPreview}
                        onChange={(e) => {
                          let value = e.target.value;
                          if (value.includes(".")) {
                            const [integerPart, decimalPart] = value.split(".");
                            if (decimalPart?.length > 2) {
                              value = `${integerPart}.${decimalPart?.slice(0, 2)}`;
                            }
                          }
                          form.setFieldValue("medicationadministered", value);
                        }}
                        onDecrement={() => {
                          const v =
                            parseFloat(form.values.medicationadministered) || 0;
                          form.setFieldValue(
                            "medicationadministered",
                            String(Math.max(0, v - 1)),
                          );
                        }}
                        onIncrement={() => {
                          const v =
                            parseFloat(form.values.medicationadministered) || 0;
                          form.setFieldValue(
                            "medicationadministered",
                            String(v + 1),
                          );
                        }}
                      />
                      <CountField
                        id="refills-left"
                        label={
                          <span>
                            Refills Count{scannedBadge("refillsLeft")}
                          </span>
                        }
                        value={form.values.refillsLeft}
                        error={form.errors.refillsLeft}
                        disabled={isPreview}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, "");
                          form.setFieldValue("refillsLeft", value);
                        }}
                        onDecrement={() => {
                          const v = parseInt(form.values.refillsLeft, 10);
                          if (!isNaN(v) && v > 0)
                            form.setFieldValue("refillsLeft", String(v - 1));
                        }}
                        onIncrement={() => {
                          const v = parseInt(form.values.refillsLeft, 10) || 0;
                          form.setFieldValue("refillsLeft", String(v + 1));
                        }}
                      />
                    </div>
                  </div>

                  {/* ── Section: Clinical Detail ── */}
                  <div style={scanStyles.sectionCard}>
                    <p style={scanStyles.sectionTitle}>Clinical Detail</p>
                    {/* use of medication */}
                    {/* <div style={{ display: "flex", gap: 12, width: "100%" }}>
                <CustomTextArea
                  id="use-of-medication"
                  size="sm"
                  label={"Use of Medication"}
                  placeholder={"Enter medication use"}
                  minRows={3}
                  {...form.getInputProps("medicationUse")}
                  disabled={isPreview}
                />
              </div> */}
                    {/* medication instruction */}
                    <div style={{ display: "flex", gap: 12, width: "100%" }}>
                      <CustomTextArea
                        id="medication-instruction"
                        size="sm"
                        label={
                          <span>
                            Medication Instruction
                            {scannedBadge("medicationInstruction")}
                          </span>
                        }
                        placeholder={"Enter medication Instruction"}
                        minRows={3}
                        {...form.getInputProps("medicationInstruction")}
                        disabled={isPreview}
                      />
                    </div>

                    {/* <div style={{ display: "flex", gap: 12, width: "100%" }}>
                <CustomSelectBox
                  id="responsible-for-purchasing"
                  label={"Responsible for purchasing"}
                  placeholder={"Select purchasing party"}
                  data={[
                    { value: "Family", label: "Family" },
                    { value: "Care Facility", label: "Care Facility" },
                    { value: "Doctor/Nurse", label: "Doctor/Nurse" },
                    { value: "Other", label: "Other" },
                  ]}
                  {...form.getInputProps("responsibleParty")}
                  disabled={isPreview}
                />
                <CustomSelectBox
                  id="responsible-for-administering"
                  label={"Responsible for administering"}
                  placeholder={"Select administering party"}
                  data={[
                    { value: "Family", label: "Family" },
                    { value: "Care Facility", label: "Care Facility" },
                    { value: "Doctor/Nurse", label: "Doctor/Nurse" },
                    { value: "Other", label: "Other" },
                  ]}
                  {...form.getInputProps("administering")}
                  disabled={isPreview}
                />
              </div> */}
                  </div>

                  {/* ── Section: Doctor's Note ── */}
                  <div style={scanStyles.sectionCard}>
                    <p style={scanStyles.sectionTitle}>Doctor's Note</p>

                    {/* dropzone — idle: upload prompt; file picked: file row */}
                    {files?.length === 0 ? (
                      !isPreview && (
                        <Dropzone
                          onDrop={async (newFiles) => {
                            if (!newFiles?.[0]) return;
                            const file = newFiles[0];
                            try {
                              const result = await uploadFileToS3Api({ file });
                              const sizeKB = file.size / 1024;
                              const sizeLabel =
                                sizeKB >= 1024
                                  ? (sizeKB / 1024).toFixed(1) + " MB"
                                  : sizeKB.toFixed(0) + " KB";
                              setFiles([
                                { ...result, name: file.name, sizeLabel },
                              ]);
                            } catch (error) {
                              showError?.(error?.message || "Upload failed");
                            }
                          }}
                          multiple={false}
                          onReject={(f) => console.log("rejected files", f)}
                          disabled={isPreview}
                          styles={{
                            root: {
                              border: "1px dashed var(--app-border)",
                              borderRadius: 10,
                              background: "var(--app-surface-2)",
                              padding: "20px 16px",
                              cursor: "pointer",
                              "&:hover": {
                                background: "var(--app-tint-purple)",
                                borderColor: "var(--app-ink-purple)",
                              },
                            },
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              gap: 6,
                              textAlign: "center",
                              pointerEvents: "none",
                            }}
                          >
                            <span
                              style={{
                                width: 36,
                                height: 36,
                                borderRadius: "50%",
                                background: "var(--app-surface)",
                                border: "1px solid var(--app-border)",
                                display: "grid",
                                placeItems: "center",
                                boxShadow: "0 1px 2px 0 rgba(16,24,40,0.05)",
                              }}
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="var(--app-ink-purple)"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <polyline points="14 2 14 8 20 8" />
                                <line x1="16" y1="13" x2="8" y2="13" />
                                <line x1="16" y1="17" x2="8" y2="17" />
                                <polyline points="10 9 9 9 8 9" />
                              </svg>
                            </span>
                            <p
                              style={{
                                margin: 0,
                                fontSize: 13,
                                fontFamily: "Inter",
                                color: "var(--app-text)",
                              }}
                            >
                              <span
                                style={{ fontWeight: 600, color: "var(--app-ink-purple)" }}
                              >
                                Click to upload
                              </span>{" "}
                              or drag and drop
                            </p>
                            <p
                              style={{
                                margin: 0,
                                fontSize: 11,
                                color: "var(--app-text-muted)",
                                fontFamily: "Inter",
                              }}
                            >
                              PDF or image · max 2 MB
                            </p>
                          </div>
                        </Dropzone>
                      )
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 10,
                          padding: "10px 12px",
                          border: "1px solid var(--app-border)",
                          borderRadius: 8,
                          background: "var(--app-surface-2)",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            minWidth: 0,
                          }}
                        >
                          <IconFile
                            size={22}
                            color="var(--app-text-muted)"
                            strokeWidth={1.5}
                            style={{ flexShrink: 0 }}
                          />
                          <div style={{ minWidth: 0 }}>
                            <p
                              style={{
                                margin: 0,
                                fontSize: 13,
                                fontWeight: 600,
                                color: "var(--app-text)",
                                fontFamily: "Inter",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {files[0]?.name || formatString(files[0]?.path)}
                            </p>
                            {files[0]?.sizeLabel && (
                              <p
                                style={{
                                  margin: 0,
                                  fontSize: 11,
                                  color: "var(--app-text-muted)",
                                  fontFamily: "Inter",
                                }}
                              >
                                Size: {files[0]?.sizeLabel}
                              </p>
                            )}
                          </div>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            flexShrink: 0,
                          }}
                        >
                          {files[0]?.uploadURL && (
                            <button
                              type="button"
                              onClick={() =>
                                window.open(files[0]?.uploadURL, "_blank")
                              }
                              style={{
                                width: 28,
                                height: 28,
                                borderRadius: "50%",
                                border: 0,
                                background: "transparent",
                                color: "var(--app-text-muted)",
                                cursor: "pointer",
                                display: "grid",
                                placeItems: "center",
                              }}
                            >
                              <EyeIconSvg
                                size={16}
                                color="var(--app-text-muted)"
                                strokeWidth={1.5}
                              />
                            </button>
                          )}
                          {!isPreview && (
                            <button
                              type="button"
                              onClick={() => {
                                setFiles([]);
                                form.setFieldValue("files", []);
                              }}
                              style={{
                                width: 28,
                                height: 28,
                                borderRadius: "50%",
                                border: 0,
                                background: "transparent",
                                color: "var(--app-text-muted)",
                                cursor: "pointer",
                                display: "grid",
                                placeItems: "center",
                              }}
                            >
                              <IoIosCloseCircle color="var(--app-text-muted)" size={18} />
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* order date */}
                    <div style={{ marginTop: 12 }}>
                      <DatePickerInput
                        id="order-date"
                        label="Order Date"
                        placeholder="Pick a date"
                        className={classes.inputStyle}
                        icon={<FiCalendar />}
                        {...form.getInputProps("orderDate")}
                        disabled={isPreview}
                      />
                    </div>
                  </div>
                </>
              )}
          </div>
        </div>
        {!isPreview && (
          <div className={classes.saveButton}>
            {/* Cancel / Back button */}
            {panelMode !== "bulk-saved" &&
              panelMode !== "bulk-save-preview" &&
              !(panelMode === "bulk-review" && bulkEditingRowId) && (
                <Button
                  id="cancel-medication"
                  type="button"
                  variant="subtle"
                  onClick={() => {
                    if (bulkEditingRowId) {
                      handleCancelBulkRowEdit();
                    } else if (panelMode === "upload") {
                      setPanelMode(null);
                      setBulkFiles([]);
                    } else if (panelMode === "bulk-review") {
                      reset();
                    } else {
                      onclose();
                      form?.reset();
                      setRxStrengthOptions([]);
                    }
                  }}
                  sx={{
                    background: "transparent",
                    color: "var(--app-text)",
                    border: "none",
                    padding: "10px 18px",
                    fontSize: 14,
                    fontWeight: 600,
                    fontFamily: "Inter",
                    "&:hover": { background: "var(--app-surface-2)" },
                  }}
                >
                  {bulkEditingRowId ? "Back to review" : "Cancel"}
                </Button>
              )}

            {/* Process files button — upload mode; becomes "Check status" when
                a prior submission is still processing on the backend */}
            {panelMode === "upload" && (
              <Button
                id="process-bulk-files"
                type="button"
                loading={bulkProcessing}
                disabled={bulkFiles.length === 0 || bulkProcessing}
                onClick={pendingScanJob ? handleCheckScanStatus : handleBulkProcess}
                leftIcon={!bulkProcessing && <LuSparkles size={14} />}
                sx={{
                  background: "var(--app-primary)",
                  border: "1px solid var(--app-primary)",
                  color: "var(--app-on-primary)",
                  padding: "10px 22px",
                  fontSize: 14,
                  fontWeight: 600,
                  fontFamily: "Inter",
                  borderRadius: 8,
                  boxShadow: "0 1px 2px 0 rgba(16,24,40,0.05)",
                  transition: "filter .12s ease",
                  "&&:hover": {
                    background: "var(--app-primary)",
                    filter: "brightness(0.93)",
                  },
                  "&[data-disabled]": {
                    background: "var(--app-border)",
                    borderColor: "var(--app-border)",
                    color: "var(--app-text-muted)",
                  },
                }}
              >
                {pendingScanJob ? "Check status" : "Process"}
              </Button>
            )}

            {/* Bulk review list footer — selection-aware */}
            {panelMode === "bulk-review" &&
              !bulkEditingRowId &&
              (() => {
                const selectedRows = bulkReviewRows.filter((r) =>
                  bulkSelectedIds.has(r.id),
                );
                // Only unsaved selected rows determine whether "Review" or "Accept & Next" shows
                const unsavedSelected = selectedRows.filter((r) => !r.saved);
                const hasNeedsReview = unsavedSelected.some(
                  (r) =>
                    r.isDuplicate || r.needsReview || r.status === "failed",
                );
                if (selectedRows.length === 0) {
                  return (
                    <Button
                      type="button"
                      disabled
                      sx={{
                        background: "var(--app-border)",
                        border: "1px solid var(--app-border)",
                        color: "var(--app-text-muted)",
                        padding: "10px 22px",
                        fontSize: 14,
                        fontWeight: 600,
                        fontFamily: "Inter",
                        borderRadius: 8,
                        cursor: "not-allowed",
                        "&[data-disabled]": {
                          background: "var(--app-border)",
                          borderColor: "var(--app-border)",
                          color: "var(--app-text-muted)",
                        },
                      }}
                    >
                      Accept & Next
                    </Button>
                  );
                }
                if (hasNeedsReview) {
                  // Find first unsaved selected row that needs review
                  const firstReviewRow = unsavedSelected.find(
                    (r) =>
                      r.isDuplicate || r.needsReview || r.status === "failed",
                  );
                  return (
                    <Button
                      type="button"
                      onClick={() => {
                        if (firstReviewRow) handleBulkRowReview(firstReviewRow);
                      }}
                      sx={{
                        background: "var(--app-primary)",
                        border: "1px solid var(--app-primary)",
                        color: "var(--app-on-primary)",
                        padding: "10px 22px",
                        fontSize: 14,
                        fontWeight: 600,
                        fontFamily: "Inter",
                        borderRadius: 8,
                        boxShadow: "0 1px 2px 0 rgba(16,24,40,0.05)",
                        transition: "filter .12s ease",
                        "&&:hover": {
                          background: "var(--app-primary)",
                          filter: "brightness(0.93)",
                        },
                      }}
                    >
                      Review
                    </Button>
                  );
                }
                // All selected are ready — go to final preview before saving
                return (
                  <Button
                    type="button"
                    onClick={() => {
                      setBulkSavePreviewRows(selectedRows);
                      setPanelMode("bulk-save-preview");
                    }}
                    sx={{
                      background: "var(--app-primary)",
                      border: "1px solid var(--app-primary)",
                      color: "var(--app-on-primary)",
                      padding: "10px 22px",
                      fontSize: 14,
                      fontWeight: 600,
                      fontFamily: "Inter",
                      borderRadius: 8,
                      boxShadow: "0 1px 2px 0 rgba(16,24,40,0.05)",
                      transition: "filter .12s ease",
                      "&&:hover": {
                        background: "var(--app-primary)",
                        filter: "brightness(0.93)",
                      },
                    }}
                  >
                    Accept & Next
                  </Button>
                );
              })()}

            {/* Save / Next button — normal form mode */}
            {panelMode === null && (
              <Button
                id="save-medication"
                loading={loading}
                type={bulkEditingRowId ? "button" : "submit"}
                onClick={
                  bulkEditingRowId
                    ? () => {
                        const result = form.validate();
                        if (result.hasErrors) return;
                        const editingId = bulkEditingRowId;
                        const vals = { ...form.values };
                        const ocrPatch = {
                          medicationName: {
                            value: vals.medicationName,
                            confidence: 100,
                          },
                          dosage: { value: vals.dosage, confidence: 100 },
                          unit: { value: vals.unit, confidence: 100 },
                          frequency: { value: vals.frequency, confidence: 100 },
                          route: { value: vals.ingestionMode, confidence: 100 },
                          doseForm: { value: vals.dosageForm, confidence: 100 },
                          rxNumber: { value: vals.rxNumber, confidence: 100 },
                          quantity: {
                            value: vals.totalMedicine,
                            confidence: 100,
                          },
                          instructions: {
                            value: vals.medicationInstruction,
                            confidence: 100,
                          },
                          scheduleType: {
                            value: vals.medicationType,
                            confidence: 100,
                          },
                          narcoticStatus: {
                            value: vals.isNarcotic === "Yes",
                            confidence: 100,
                          },
                          isPrescribed: {
                            value: vals.isPrescribed !== "No",
                            confidence: 100,
                          },
                          timeSlots: {
                            value: vals.timeSlots || [],
                            confidence: 100,
                          },
                          administrationCount: {
                            value: vals.medicationadministered,
                            confidence: 100,
                          },
                          refillsCount: {
                            value: vals.refillsLeft,
                            confidence: 100,
                          },
                          _formOverrides: {
                            expirationDate: vals.expirationDate || null,
                            medicationDate: vals.medicationDate || null,
                            orderDate: vals.orderDate || null,
                            medicineRemaining: vals.medicineRemaining,
                            totalMedicine: vals.totalMedicine,
                            medicationadministered: vals.medicationadministered,
                            strengthAndForm: vals.strengthAndForm || "",
                            medicationInstruction: vals.medicationInstruction || "",
                            medicationUse: vals.medicationUse || "",
                            isMedicationCountEnabled: isMedicationCountEnabled,
                          },
                        };
                        const afterSaveCb = afterBulkRowSaveRef.current;
                        afterBulkRowSaveRef.current = null;
                        form.reset();
                        setScanState("idle");
                        setScannedFields(new Map());
                        setScanImage(null);
                        const applyPatch = (r) =>
                          r.id === editingId
                            ? { ...r, ocrResult: { ...(r.ocrResult || {}), ...ocrPatch } }
                            : r;
                        const patchedRows = bulkReviewRows.map(applyPatch);
                        setBulkSavePreviewRows((prev) => prev.map(applyPatch));
                        setBulkEditingRowId(null);
                        if (afterSaveCb) {
                          afterSaveCb(patchedRows);
                        } else {
                          setBulkReviewRows(patchedRows);
                          setPanelMode("bulk-review");
                        }
                      }
                    : undefined
                }
                sx={{
                  background: "var(--app-primary)",
                  border: "1px solid var(--app-primary)",
                  color: "var(--app-on-primary)",
                  padding: "10px 22px",
                  fontSize: 14,
                  fontWeight: 600,
                  fontFamily: "Inter",
                  borderRadius: 8,
                  boxShadow: "0 1px 2px 0 rgba(16,24,40,0.05)",
                  transition: "filter .12s ease",
                  "&&:hover": {
                    background: "var(--app-primary)",
                    filter: "brightness(0.93)",
                  },
                  "&[data-disabled]": {
                    background: "var(--app-border)",
                    borderColor: "var(--app-border)",
                    color: "var(--app-text-muted)",
                    boxShadow: "none",
                  },
                }}
              >
                {bulkEditingRowId ? "Next" : "Save medication"}
              </Button>
            )}

            {/* ── Bulk save preview footer ── */}
            {panelMode === "bulk-save-preview" && (
              <>
                <Button
                  type="button"
                  onClick={() => setPanelMode("bulk-review")}
                  sx={{
                    background: "var(--app-surface)",
                    border: "1px solid var(--app-border)",
                    color: "var(--app-text-muted)",
                    padding: "10px 22px",
                    fontSize: 14,
                    fontWeight: 600,
                    fontFamily: "Inter",
                    borderRadius: 8,
                    "&:hover": { background: "var(--app-surface-2)" },
                  }}
                >
                  Back
                </Button>
                <Button
                  type="button"
                  loading={loading}
                  onClick={async () => {
                    // ── Validate each row before saving ──────────────────────
                    const validationErrors = [];
                    bulkSavePreviewRows.forEach((row, idx) => {
                      const ocr = row.ocrResult || {};
                      const overrides = ocr._formOverrides || {};
                      const schedRaw = (ocr?.scheduleType?.value || "daily").toLowerCase();
                      const isPRN = schedRaw === "prn";
                      const medName = ocr?.medicationName?.value || `Medication ${idx + 1}`;
                      // Use the same countEnabled logic as handleBulkRowEdit
                      const savedCountPref = overrides.isMedicationCountEnabled;
                      const countEnabled = savedCountPref != null
                        ? savedCountPref
                        : (ocr?.quantity?.value != null && ocr.quantity.value !== "" ? "yes" : "no");
                      const missing = [];

                      // isPrescribed / isNarcotic — only validate if row was edited through the form
                      const rowWasEdited = Object.keys(overrides).length > 0;
                      if (rowWasEdited) {
                        if (ocr?.isPrescribed?.value == null) missing.push("Is Prescribed");
                        if (ocr?.narcoticStatus?.value == null) missing.push("Is Narcotic");
                      }
                      if (!ocr?.medicationName?.value) missing.push("Medication Name");
                      if (!ocr?.scheduleType?.value) missing.push("Medication Type");
                      if (!ocr?.route?.value) missing.push("Route");
                      // totalMedicine + medicineRemaining only required when count is enabled
                      // (when "no", form sets them to "unlimited" so they pass)
                      if (countEnabled !== "no") {
                        const totalMed = overrides.totalMedicine ?? ocr?.quantity?.value;
                        if (totalMed == null || totalMed === "") missing.push("Total Medicine");
                        const medRemaining = overrides.medicineRemaining ?? ocr?.quantity?.value;
                        if (medRemaining == null || medRemaining === "") missing.push("Medicine Remaining");
                      }
                      // medicationadministered always required regardless of count toggle
                      const administered = overrides.medicationadministered ?? ocr?.administrationCount?.value;
                      if (administered == null || administered === "" || String(administered).trim() === "") missing.push("Times Administered");
                      const instruction = overrides.medicationInstruction || ocr?.instructions?.value;
                      if (!instruction) missing.push("Medication Instruction");
                      // timeSlots — required only for non-PRN (mirrors: delete _validation.timeSlots when PRN)
                      if (!isPRN) {
                        const slots = normalizeTimeSlots(ocr?.timeSlots?.value);
                        if (slots.length === 0) missing.push("Time Slots");
                      }

                      if (missing.length > 0) {
                        validationErrors.push(`${medName}: ${missing.join(", ")}`);
                      }
                    });
                    if (validationErrors.length > 0) {
                      showError(`Please fix missing fields before saving:\n${validationErrors.join("\n")}`);
                      return;
                    }
                    // ─────────────────────────────────────────────────────────
                    const clientId = selectedClient?.id || client?.id;
                    setLoading(true);
                    try {
                      const medicinesPayload = bulkSavePreviewRows.map((row) => {
                        const ocr = row.ocrResult || {};
                        const overrides = ocr._formOverrides || {};
                        const schedRaw = (ocr?.scheduleType?.value || "daily").toLowerCase();
                        const medType = schedRaw === "prn" ? "PRN" : schedRaw;
                        const slots = medType === "PRN" ? [] : normalizeTimeSlots(ocr?.timeSlots?.value);
                        const expirationDate = overrides.expirationDate || null;
                        const ocrStartDate = ocr?.startDate?.value
                          ? moment(
                              String(ocr.startDate.value).trim(),
                              ["MM/DD/YYYY", "M/D/YYYY", "MM-DD-YYYY", "YYYY-MM-DD"],
                              true,
                            )
                          : null;
                        const medicationDate = overrides.medicationDate
                          ? moment(overrides.medicationDate).toDate()
                          : ocrStartDate?.isValid()
                            ? ocrStartDate.toDate()
                            : moment().toDate();
                        const orderDate = overrides.orderDate ? moment(overrides.orderDate).toDate() : moment().toDate();
                        // Use Medication Count? = No (no OCR quantity, and not overridden) → unlimited, not 0
                        const countEnabled = overrides.isMedicationCountEnabled != null
                          ? overrides.isMedicationCountEnabled
                          : (ocr?.quantity?.value != null && ocr.quantity.value !== "" ? "yes" : "no");
                        const totalMedicine = countEnabled === "no"
                          ? "unlimited"
                          : (overrides.totalMedicine ?? ocr?.quantity?.value ?? 0);
                        const medicineRemaining = countEnabled === "no"
                          ? "unlimited"
                          : (overrides.medicineRemaining ?? ocr?.quantity?.value ?? 0);
                        const ocrValues = {
                          medicationDate, orderDate, expirationDate,
                          medicationType: medType,
                          scheduleType: medType === "PRN" ? "prn" : "daily",
                          repeatEvery: 1, repeatType: "", repeatOn: [],
                          timeSlots: slots, medicineRemaining,
                        };
                        const { scheduleDates } = setScheduleDates(ocrValues);
                        const med = {
                          dosage: ocr?.dosage?.value || "",
                          dosageForm: ocr?.doseForm?.value || overrides.strengthAndForm || "",
                          expirationDate: expirationDate ? moment(expirationDate).endOf("D").toISOString() : null,
                          frequency: ocr?.frequency?.value || "",
                          ingestionMode: ocr?.route?.value || "",
                          isNarcotic: ocr?.narcoticStatus?.value ? "Yes" : "No",
                          isPrescribed: ocr?.isPrescribed?.value === false ? "No" : "Yes",
                          rxNumber: ocr?.rxNumber?.value || "",
                          medicationDate: moment(medicationDate).toISOString(),
                          medicationInstruction: ocr?.instructions?.value || "",
                          medicationName: ocr?.medicationName?.value || "",
                          medicationType: JSON.stringify([{ status: medType, date: moment().toISOString() }]),
                          medicationadministered: ocr?.administrationCount?.value || 0,
                          medicineRemaining,
                          orderDate: moment(orderDate).toISOString(),
                          timeSlots: slots,
                          totalMedicine,
                          unit: ocr?.unit?.value || "",
                          strengthAndForm: overrides.strengthAndForm || ocr?.doseForm?.value || "",
                          medicineProvided: Number(medicineRemaining) || 0,
                          id: Date.now(),
                          scheduleType: medType === "PRN" ? "prn" : "daily",
                          repeatEvery: 1, repeatType: "", repeatOn: "",
                          medicationEndDate: expirationDate ? moment(expirationDate).endOf("D").toISOString() : moment().endOf("year").toISOString(),
                          scheduleDates,
                          hasExpiryDate: !!expirationDate,
                        };
                        if (ocr?.refillsCount?.value != null && ocr.refillsCount.value !== "") {
                          med.refillsLeft = Number(ocr.refillsCount.value);
                        }
                        return med;
                      });
                      await AddMedication(medicinesPayload, clientId, false, null, null, false);
                      const savedIds = new Set(bulkSavePreviewRows.map((r) => r.id));
                      setBulkReviewRows((prev) =>
                        prev.map((r) => savedIds.has(r.id) ? { ...r, saved: true } : r)
                      );
                      setLoading(false);
                      setPanelMode("bulk-saved");
                    } catch (e) {
                      showError(e?.message || "Failed to save medications");
                      setLoading(false);
                    }
                  }}
                  sx={{
                    background: "var(--app-primary)",
                    border: "1px solid var(--app-primary)",
                    color: "var(--app-on-primary)",
                    padding: "10px 22px",
                    fontSize: 14,
                    fontWeight: 600,
                    fontFamily: "Inter",
                    borderRadius: 8,
                    boxShadow: "0 1px 2px 0 rgba(16,24,40,0.05)",
                    "&:hover": { filter: "brightness(0.93)" },
                  }}
                >
                  Save
                </Button>
              </>
            )}
          </div>
        )}
      </form>

      {/* ── Webcam capture overlay ── */}
      {cameraOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 2000,
            background: "rgba(0,0,0,0.85)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 16,
          }}
        >
          <video
            ref={(el) => {
              videoRef.current = el;
              // iOS Safari: assign srcObject + call play() the instant the element
              // is in the DOM (synchronous ref callback), before any async gaps.
              if (el && streamRef.current) {
                el.srcObject = streamRef.current;
                el.play().catch(() => {});
              }
            }}
            autoPlay
            playsInline
            muted
            style={{
              width: "100%",
              maxWidth: 540,
              borderRadius: 12,
              background: "#000",
            }}
          />
          <div style={{ display: "flex", gap: 12 }}>
            <button
              type="button"
              onClick={captureFromCamera}
              style={{
                height: 44,
                padding: "0 28px",
                borderRadius: 8,
                background: "#6941C6",
                color: "#fff",
                border: "none",
                fontSize: 14,
                fontWeight: 600,
                fontFamily: "Inter",
                cursor: "pointer",
              }}
            >
              Capture
            </button>
            <button
              type="button"
              onClick={switchCamera}
              style={{
                height: 44,
                padding: "0 16px",
                borderRadius: 8,
                background: "transparent",
                color: "#fff",
                border: "1px solid #fff",
                fontSize: 14,
                fontWeight: 600,
                fontFamily: "Inter",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M1 4v6h6" />
                <path d="M23 20v-6h-6" />
                <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" />
              </svg>
              Flip
            </button>
            <button
              type="button"
              onClick={closeCamera}
              style={{
                height: 44,
                padding: "0 28px",
                borderRadius: 8,
                background: "transparent",
                color: "#fff",
                border: "1px solid #fff",
                fontSize: 14,
                fontWeight: 600,
                fontFamily: "Inter",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <ConfirmationModal
        opened={confirmModal}
        close={confirmClose}
        handleEdit={() => {
          handleEdit(true);
        }}
        // handleNewVersion={() => {
        //   handleNewVersion();
        // }}
      />
      <VersionUpdateModal
        opened={versionUpdateModal}
        close={versionUpdateClose}
        loading={loading}
        existingExpirationDate={form.values?.expirationDate}
        handleEdit={() => {
          handleUpdateThisMedication();
        }}
        handleNewVersion={(startDate, expirationDate) => {
          handleNewVersion(startDate, expirationDate);
        }}
      />
    </Drawer>
  );
};

const CustomTextInput = ({ ...rest }) => {
  const { classes } = useStyles();

  return <TextInput className={classes.inputStyle} {...rest} />;
};

const CustomSelectBox = ({ ...rest }) => {
  const { classes } = useStyles();

  return (
    <Select
      {...rest}
      rightSection={<IconChevronDown />}
      className={classes.inputStyle}
    />
  );
};

const CustomTextArea = ({ label, withAsterisk, ...rest }) => {
  const { classes } = useStyles();
  return (
    <div style={{ width: "100%" }}>
      <Textarea
        label={label}
        withAsterisk={withAsterisk}
        className={classes.inputStyle}
        {...rest}
      />
    </div>
  );
};

const stepBtnStyle = {
  width: "28px",
  height: "28px",
  borderRadius: "50%",
  border: "1.5px solid var(--app-border)",
  backgroundColor: "transparent",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "18px",
  lineHeight: 1,
  flexShrink: 0,
  padding: 0,
  color: "var(--app-text)",
};

const CountField = ({
  value,
  onChange,
  onDecrement,
  onIncrement,
  disabled,
  label,
  labelNode,
  error,
  withAsterisk,
}) => {
  const { classes } = useStyles();
  return (
    <div style={{ width: "100%", minWidth: 0 }}>
      <div
        style={{
          fontSize: "14px",
          fontWeight: 500,
          marginBottom: "6px",
          color: disabled ? "var(--app-text-muted)" : "inherit",
        }}
      >
        {labelNode || label}
        {withAsterisk && (
          <span style={{ color: "var(--app-text)", marginLeft: "2px" }}>*</span>
        )}
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          backgroundColor: "var(--app-surface)",
          borderRadius: "8px",
          padding: "8px 12px",
          gap: "8px",
          border: "1px solid var(--app-border)",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <button
          type="button"
          onClick={onDecrement}
          disabled={disabled}
          style={{
            ...stepBtnStyle,
            opacity: disabled ? 0.4 : 1,
            cursor: disabled ? "not-allowed" : "pointer",
          }}
        >
          −
        </button>
        <input
          value={value ?? ""}
          onChange={onChange}
          disabled={disabled}
          type="number"
          className={classes.noSpinner}
          placeholder={"0"}
          style={{
            flex: 1,
            border: "none",
            backgroundColor: "transparent",
            textAlign: "center",
            outline: "none",
            fontSize: "14px",
            color: disabled ? "var(--app-text-muted)" : "var(--app-text)",
            minWidth: 0,
            borderRadius: "4px",
          }}
        />
        <button
          type="button"
          onClick={onIncrement}
          disabled={disabled}
          style={{
            ...stepBtnStyle,
            width: "30px",
            height: "30px",
            opacity: disabled ? 0.4 : 1,
            cursor: disabled ? "not-allowed" : "pointer",
          }}
        >
          +
        </button>
      </div>
      {error && (
        <div style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>
          {error}
        </div>
      )}
    </div>
  );
};

export default AddNewMedicationModal;
